/* People / Projects / Clients directory views.
   Cards + search + click-to-open. */
import React from 'react';
import { Icon, Avatar, MimeIcon, EntityLink, PersonCard, entityHref, entityHandle, TaxonomyMultiSelect } from './primitives.jsx';
import { useAutoSave, SaveStatus } from '../lib/use-auto-save.jsx';
import { useAnchoredPosition, HIDDEN_ANCHORED_STYLE } from '../lib/use-anchored-position.jsx';
import { StaffingSection, ModalPhotoUploader } from './user-profile-modal.jsx';
import { listPeople, listPeopleActivity, listProjects, listTaxonomies, getClientProfile, getUserProfile, getIndustryProfile, getServiceProfile, getCompanyProfile, getOfficeProfile, patchOffice, uploadOfficePhoto, removeOfficePhoto, adminOffices, getProjectProfile, uploadProjectFile, deleteProjectFile, addProjectMember, removeProjectMember, addProjectLeadPartner, removeProjectLeadPartner, addProjectManager, removeProjectManager, recalcProjectsChf, listItemLeads, addItemLead, removeItemLead, listAdminUsers, convertCurrencyForProject, suggestProjectDescription, getUserStaffing, patchIndustry, patchService, patchCompany, patchClient, patchProjectInline, patchMyProfile, patchAdminUser, deleteAdminUser, listAllFunctions, listAdminInvitations, revokeAdminInvitation, regenerateAdminInvitation, createAdminResetLink, generateAdminUserIntro, deleteAdminUserPhoto, createCompanyNews, deleteCompanyNews, putOrgRepoFile, deleteOrgRepoFile, putClientLogo, clearClientLogo, adminProjects, adminClients, adminServices, adminIndustries, adminCompanies, listCreditors, getRateCard, putProjectRateLines, putProjectMonthly, previewEntityDelete, executeEntityDelete, openDirectMessage } from '../api.js';
import { MarkdownView } from './orgrepo-news-reports-arch.jsx';
import { publicOrigin } from '../lib/public-origin.ts';
import { EntityShell, EntitySection } from './entity-shell.jsx';
import { EditModeProvider, EditSwitch, EditableText, EditableSelect, useEditMode } from './inline-edit.jsx';
import { ProfileExperience, ComplianceSection, ProfileSettings, eOSInsightsCard, TokenInsights } from './profile.jsx';
import { InviteModal, StatusPill, relativeTime, AddEntityButton } from './admin.jsx';
import { useSubCrumbs } from './route-title-context.jsx';
import { MonthlyRevenueStack } from './monthly-revenue-stack.jsx';
import { MonthlyStaffingStack } from './monthly-staffing-stack.jsx';
import { ActivityFeed } from './activity-feed.jsx';
import { StaffingEmbed } from './staffing.jsx';
import { StaffingBookingPanel } from './booking.jsx';

// Strip the most common markdown markers for the short clamped
// preview in list tiles, where wrapping at the right margin matters
// more than rendering headings / bold.
function stripMarkdownForPreview(s) {
  if (!s) return '';
  return String(s)
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s*#{1,6}\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/[*_](.+?)[*_]/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
}

const PROJECT_STATUS_OPTIONS = [
  { value: 'lead',         label: 'Lead'                    },
  { value: 'lost',         label: 'Lost'                    },
  { value: 'won-waiting',  label: 'Won — Waiting for Start' },
  { value: 'running',      label: 'Running'                 },
  { value: 'finished',     label: 'Finished'                },
];
const CURRENCY_OPTIONS = ['CHF', 'EUR', 'USD', 'DKK', 'SEK', 'GBP', 'NOK', 'JPY', 'CNY'];
// Pricing models a project can carry (multi-select). Slugs match the
// backend vocab in functions/_lib/projects.ts (PROJECT_TYPES).
const PROJECT_TYPE_OPTIONS = [
  { value: 'fixed-price',       label: 'Fixed Price'     },
  { value: 'time-and-material', label: 'Time & Material' },
  { value: 'success-based',     label: 'Success-based'   },
];
const projectTypeLabel = v => PROJECT_TYPE_OPTIONS.find(o => o.value === v)?.label || v;

// Display formatting helpers for the integer minor-unit amounts the
// backend stores. We keep amounts as plain integers (no cents-vs-units
// split) — the UI shows them as thousand-separated whole numbers and
// trusts the user to enter the right scale. ISO 4217 codes are
// surfaced as a `currency` suffix.
function fmtAmount(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}
function dateInputValue(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}
// Compact token counts: 0 / 940 / 18.2k / 3.4M.
function fmtTokens(n) {
  const v = Number(n || 0);
  if (!Number.isFinite(v) || v <= 0) return '0';
  if (v < 1000) return String(v);
  if (v < 1_000_000) return `${(v / 1000).toFixed(v < 10_000 ? 1 : 0)}k`;
  return `${(v / 1_000_000).toFixed(v < 10_000_000 ? 1 : 0)}M`;
}

// Copy text to the clipboard with a legacy execCommand fallback for
// non-secure contexts. Returns true on success.
async function copyTextToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* fall through to the textarea fallback */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

/* ─── People ───────────────────────────────────────────────────── */

/* Phase 4: list-with-detail for People.
 *   subPath ''   → list view (table)
 *   subPath '<id>' → <PeopleDetailView/> (full-page, EntityShell with
 *                   activity feed on the right rail).
 *
 * Dispatcher pattern: the EXPORTED component never calls hooks
 * itself, so subPath flipping between '' and a value never changes
 * the hooks count (which would tear down the tree). List + Detail
 * own their own hooks independently. */
export const PeopleView = ({ currentUser, subPath, onNavigate } = {}) => (
  subPath
    ? <PeopleDetailView userId={subPath} currentUser={currentUser} onBack={() => onNavigate?.('')}/>
    : <PeopleList currentUser={currentUser} onNavigate={onNavigate}/>
);

const PEOPLE_VIEWS = [
  { id: 'all',       label: 'All'       },
  { id: 'activated', label: 'Activated' },
  { id: 'invited',   label: 'Invited'   },
];

const PeopleList = ({ currentUser, onNavigate }) => {
  const isAdmin = currentUser?.role === 'admin';
  const [people, setPeople] = React.useState(null);
  const [invites, setInvites] = React.useState(null);
  const [view, setView] = React.useState('activated');   // activated | invited | all
  const [error, setError] = React.useState(null);
  const [q, setQ] = React.useState('');
  const [openInvite, setOpenInvite] = React.useState(false);
  // Per-invite copy feedback: { id, status: 'copied' | 'error' }.
  const [copyState, setCopyState] = React.useState(null);
  const [bioRegen, setBioRegen] = React.useState(null);

  const reload = React.useCallback(() => {
    setError(null);
    listPeople().then(people => {
      setPeople(people);
      // Admin-only activity stats (actions + token totals) are loaded
      // lazily and merged in: they aggregate over unboundedly-growing
      // tables (audit_log / claude_usage), so keeping them off the
      // directory's critical path means the list
      // always renders even if the stats query is slow or fails. On
      // success every row gets explicit numbers (absent from the map =
      // genuinely no recorded activity = 0); while loading or on error
      // the fields stay undefined and the cells render '—' — a "0" in
      // these columns is always real data, never a failed fetch.
      if (isAdmin) {
        listPeopleActivity()
          .then(stats => setPeople(prev => (prev || []).map(p => ({
            ...p,
            actionsTotal: 0, tokensTotal: 0, tokens7d: 0, tokens24h: 0,
            ...(stats[p._id] || {}),
          }))))
          .catch(() => { /* leave stat columns as '—' */ });
      }
    }).catch(err => setError(err.message));
    // Invitations are an admin-only surface.
    if (isAdmin) listAdminInvitations().then(setInvites).catch(() => setInvites([]));
  }, [isAdmin]);
  React.useEffect(() => { reload(); }, [reload]);

  const needle = q.trim().toLowerCase();
  const matched = !people ? [] : !needle ? people : people.filter(p =>
    (p.name || '').toLowerCase().includes(needle) ||
    (p.title || '').toLowerCase().includes(needle) ||
    (p.company || '').toLowerCase().includes(needle) ||
    (p.office || '').toLowerCase().includes(needle),
  );
  // Latest action on top; people with no recorded activity (and any
  // non-admin view, where this data isn't loaded) fall to the bottom,
  // name-sorted.
  const filtered = [...matched].sort((a, b) =>
    (b.lastActionAt || 0) - (a.lastActionAt || 0) || (a.name || '').localeCompare(b.name || ''),
  );
  const filteredInvites = !invites ? null : !needle ? invites : invites.filter(i =>
    (i.email || '').toLowerCase().includes(needle) ||
    (i.role || '').toLowerCase().includes(needle) ||
    (i.invitedByName || '').toLowerCase().includes(needle),
  );
  const pendingCount = (invites || []).filter(i => i.status === 'pending').length;

  const openPerson = p => onNavigate?.(p._id);

  const revoke = async id => {
    if (!confirm('Revoke this invitation? The link will stop working immediately.')) return;
    try { await revokeAdminInvitation(id); reload(); }
    catch (err) { alert(err.message); }
  };
  // Copy the invite link straight to the clipboard — no popup. We must
  // regenerate first (tokens are stored hashed, shown once); that mints a
  // fresh token and revokes the old one. The clipboard write is handed the
  // regenerate as a PROMISE (ClipboardItem) so the user-gesture activation
  // survives the round-trip — otherwise Safari rejects the write after the
  // await and the copy "fails". Falls back to writeText / execCommand.
  // The row is updated in place (not reloaded) so the "Copied ✓" flash
  // sticks and a second click still works.
  const copyLink = async inv => {
    const flash = (id, status) => {
      setCopyState({ id, status });
      setTimeout(() => setCopyState(s => (s && s.id === id ? null : s)), 1800);
    };
    let fresh = null;
    const urlPromise = regenerateAdminInvitation(inv._id)
      .then(f => { fresh = f; return `${publicOrigin()}/?invite=${encodeURIComponent(f.token)}`; });
    let ok = false;
    try {
      if (navigator.clipboard?.write && typeof window !== 'undefined' && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({ 'text/plain': urlPromise.then(u => new Blob([u], { type: 'text/plain' })) }),
        ]);
        ok = true;
      }
    } catch { /* fall through to the writeText / execCommand path */ }
    if (!ok) {
      try { ok = await copyTextToClipboard(await urlPromise); } catch { ok = false; }
    }
    try { await urlPromise; } catch { fresh = null; }   // regenerate failed
    if (fresh) {
      setInvites(list => (list || []).map(x =>
        x._id === inv._id ? { ...x, _id: fresh.id, expiresAt: fresh.expiresAt } : x));
    }
    flash(fresh ? fresh.id : inv._id, fresh && ok ? 'copied' : 'error');
  };

  // Sequentially regenerate every person's bio from the
  // profile-bio-autocreate guidance. Runs client-side against the
  // per-user admin endpoint; progress + per-person failures surface inline.
  const regenerateAllBios = async () => {
    if (!people || people.length === 0) return;
    if (!confirm(
      `Regenerate bios for all ${people.length} people?\n\n` +
      `This overwrites every existing bio, drafting a fresh one with Claude. ` +
      `It runs one person at a time, can take a few minutes, and uses API credits.`,
    )) return;
    const errors = [];
    setBioRegen({ running: true, done: 0, total: people.length, errors });
    for (let i = 0; i < people.length; i++) {
      const u = people[i];
      try {
        const { draft } = await generateAdminUserIntro(u._id);
        if (draft) await patchAdminUser(u._id, { bio: draft });
      } catch (err) {
        errors.push({ name: u.name, message: err.message || String(err) });
      }
      setBioRegen({ running: i + 1 < people.length, done: i + 1, total: people.length, errors: [...errors] });
    }
  };

  // Non-admins only ever see the activated directory.
  const effectiveView = isAdmin ? view : 'activated';
  const showPeople  = effectiveView === 'activated' || effectiveView === 'all';
  const showInvites = isAdmin && (effectiveView === 'invited' || effectiveView === 'all');

  return (
    <div className="page">
      <div className="page__head">
        <h1>People</h1>
        <div className="lede">Everyone in the firm. Click a row to open the full dashboard — projects, weekly staffing, and a live activity feed.</div>
      </div>

      <div style={{display:'flex',gap:10,marginBottom:14,alignItems:'center',flexWrap:'wrap'}}>
        {isAdmin && (
          <div style={{display:'flex',gap:6}}>
            {PEOPLE_VIEWS.map(t => (
              <button
                key={t.id}
                type="button"
                className={effectiveView === t.id ? 'btn-orange' : 'btn-ghost'}
                style={{padding:'4px 12px',fontSize:12}}
                onClick={() => setView(t.id)}
              >
                {t.label}{t.id === 'activated' && people ? ` · ${people.length}` : ''}{t.id === 'invited' && pendingCount > 0 ? ` · ${pendingCount}` : ''}
              </button>
            ))}
          </div>
        )}
        <input
          className="input"
          placeholder="Search people…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{maxWidth:300}}
        />
        <div style={{flex:1}}/>
        {isAdmin && <button className="btn-orange" onClick={() => setOpenInvite(true)}><Icon name="plus" size={14}/> Invite people</button>}
        {isAdmin && (
          <button
            className="btn-ghost"
            onClick={regenerateAllBios}
            disabled={!people || people.length === 0 || bioRegen?.running}
            title="Regenerate every person's bio from the profile-bio-autocreate guidance using Claude. Overwrites existing bios."
          >
            {bioRegen?.running ? `Regenerating bios… ${bioRegen.done}/${bioRegen.total}` : 'Regenerate all bios'}
          </button>
        )}
      </div>

      {bioRegen && !bioRegen.running && (
        <div style={{padding:12,marginBottom:16,background:bioRegen.errors.length ? '#FFF4E5' : '#DCF1E2',color:bioRegen.errors.length ? '#B83C0F' : '#1E6B3A',borderRadius:8,fontSize:13}}>
          Regenerated {bioRegen.done - bioRegen.errors.length} of {bioRegen.total} bios.
          {bioRegen.errors.length > 0 && <> {bioRegen.errors.length} failed: {bioRegen.errors.map(e => e.name).join(', ')}.</>}
        </div>
      )}

      {error && <ErrorBox>{error}</ErrorBox>}

      {showPeople && (
        <>
          {showPeople && (
            <div className="section__head" style={{marginBottom:8}}><h2 style={{fontSize:16}}>Activated · {people ? people.length : '…'}</h2></div>
          )}
          {!people && !error && <Loading>Loading people…</Loading>}
          {people && filtered.length === 0 && (
            <Empty>{needle ? 'No people match.' : 'No people in the directory yet.'}</Empty>
          )}
          {filtered.length > 0 && (
            <div className="tbl-card" style={{marginBottom:showInvites ? 28 : 0}}>
              <table className="tbl tbl--stack tbl--peoplecard" style={{width:'100%'}}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th style={{width:200}}>Title</th>
                    <th style={{width:140}}>Office</th>
                    {isAdmin && <th style={{width:120}}>Last action</th>}
                    {isAdmin && <th style={{width:70,textAlign:'right'}} title="Entries in the person's activity log — chat messages sent, runs, edits, uploads. Open the person to see the log itself.">Actions</th>}
                    {isAdmin && <th style={{width:90,textAlign:'right'}} title="Total Claude tokens used (input + output)">Tokens</th>}
                    {isAdmin && <th style={{width:70,textAlign:'right'}} title="Tokens used in the last 7 days">7d</th>}
                    {isAdmin && <th style={{width:70,textAlign:'right'}} title="Tokens used in the last 24 hours">24h</th>}
                    <th style={{width:38}}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(p => (
                    <tr key={p._id} onClick={() => openPerson(p)} style={{cursor:'pointer'}}>
                      <td data-primary>
                        <div style={{display:'flex',alignItems:'center',gap:10}}>
                          <Avatar src={p.photoUrl} name={p.name} size="md"/>
                          <div style={{minWidth:0}}>
                            <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                              <span style={{fontWeight:600,fontSize:15}}>{p.name}</span>
                              <span style={{fontSize:9.5,padding:'1px 6px',borderRadius:4,textTransform:'uppercase',letterSpacing:'0.06em',fontWeight:600,background:p.role === 'admin' ? '#FFE7DC' : p.role === 'eOS-Manager' ? '#EAE4F6' : 'var(--bg-alt)',color:p.role === 'admin' ? '#B83C0F' : p.role === 'eOS-Manager' ? '#5B43A6' : 'var(--fg-muted)'}}>
                                {p.role === 'admin' ? 'admin' : p.role === 'eOS-Manager' ? 'eOS-Manager' : 'user'}
                              </span>
                            </div>
                            {p.company && <div style={{fontSize:12,color:'var(--fg-muted)',marginTop:2,fontWeight:400}}>{p.company}</div>}
                          </div>
                        </div>
                      </td>
                      <td data-label="Title" style={{color:'var(--fg-muted)',fontSize:12.5}}>{p.title || '—'}</td>
                      <td data-label="Office" style={{color:'var(--fg-muted)',fontSize:12.5}}>{p.office || '—'}</td>
                      {isAdmin && <td data-label="Last action" style={{color:'var(--fg-muted)',fontSize:12.5}} title={p.lastActionAt ? new Date(p.lastActionAt).toLocaleString() : ''}>{p.lastActionAt ? relativeTime(p.lastActionAt, { pastOnly: true }) : '—'}</td>}
                      {isAdmin && <td data-label="Actions" style={{textAlign:'right',fontFamily:'JetBrains Mono',fontSize:12,color:p.actionsTotal ? 'var(--fg)' : 'var(--fg-muted)'}}>{p.actionsTotal == null ? '—' : fmtTokens(p.actionsTotal)}</td>}
                      {isAdmin && <td data-label="Tokens" style={{textAlign:'right',fontFamily:'JetBrains Mono',fontSize:12,color:p.tokensTotal ? 'var(--fg)' : 'var(--fg-muted)'}}>{p.tokensTotal == null ? '—' : fmtTokens(p.tokensTotal)}</td>}
                      {isAdmin && <td data-label="Tokens · 7d" style={{textAlign:'right',fontFamily:'JetBrains Mono',fontSize:12,color:p.tokens7d ? 'var(--fg)' : 'var(--fg-muted)'}}>{p.tokens7d == null ? '—' : fmtTokens(p.tokens7d)}</td>}
                      {isAdmin && <td data-label="Tokens · 24h" style={{textAlign:'right',fontFamily:'JetBrains Mono',fontSize:12,color:p.tokens24h ? 'var(--fg)' : 'var(--fg-muted)'}}>{p.tokens24h == null ? '—' : fmtTokens(p.tokens24h)}</td>}
                      <td style={{textAlign:'right',color:'var(--fg-muted)',fontSize:14}}>›</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showInvites && (
        <>
          <div className="section__head" style={{marginBottom:8}}>
            <h2 style={{fontSize:16}}>Invitations · {filteredInvites ? filteredInvites.length : '…'}</h2>
          </div>
          {filteredInvites === null ? (
            <Loading>Loading invitations…</Loading>
          ) : filteredInvites.length === 0 ? (
            <Empty>{needle ? 'No invitations match.' : <>No invitations yet. Click <strong>Invite people</strong> to issue one.</>}</Empty>
          ) : (
            <table className="tbl tbl--stack" style={{width:'100%'}}>
              <thead><tr><th>Email</th><th>Admin-Role</th><th>Invited by</th><th>Issued</th><th>Expires</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {filteredInvites.map(inv => (
                  <tr key={inv._id}>
                    <td data-primary>{inv.email || <span style={{color:'var(--fg-muted)'}}>open link</span>}</td>
                    <td data-label="Admin-Role">{inv.role}</td>
                    <td data-label="Invited by" style={{color:'var(--fg-muted)'}}>{inv.invitedByName}</td>
                    <td data-label="Issued" style={{color:'var(--fg-muted)'}}>{relativeTime(inv.createdAt, { pastOnly: true })}</td>
                    <td data-label="Expires" style={{color:'var(--fg-muted)'}}>{relativeTime(inv.expiresAt)}</td>
                    <td data-label="Status"><StatusPill status={inv.status}/></td>
                    <td data-label="Actions" style={{whiteSpace:'nowrap'}}>
                      {inv.status === 'pending' && (() => {
                        const st = copyState && copyState.id === inv._id ? copyState.status : null;
                        const label = st === 'copied' ? 'Copied ✓' : st === 'error' ? 'Copy failed' : 'Copy link';
                        const color = st === 'copied' ? 'var(--support-green)' : st === 'error' ? 'var(--support-red)' : 'var(--orange)';
                        return (
                          <>
                            <a style={{color,fontSize:12,cursor:'pointer',marginRight:14,fontWeight:st?600:400}} onClick={() => copyLink(inv)} title="Regenerate the token (the old link stops working) and copy the fresh URL to your clipboard">{label}</a>
                            <a style={{color:'var(--fg-muted)',fontSize:12,cursor:'pointer'}} onClick={() => revoke(inv._id)}>Revoke</a>
                          </>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {openInvite && <InviteModal onClose={() => { setOpenInvite(false); reload(); }}/>}
    </div>
  );
};

/* Header contact line (view mode): title + office + the quick-contact
 * links (email, phone, LinkedIn, Teams). Badges moved out of the header
 * into the Bio tab. */
const ProfileContact = ({ user }) => {
  const link = { color:'var(--orange)', textDecoration:'none', fontWeight:500 };
  const items = [];
  if (user.email) items.push(<a key="m" href={`mailto:${user.email}`} style={link}>{user.email}</a>);
  if (user.phone) items.push(<a key="p" href={`tel:${String(user.phone).replace(/\s+/g,'')}`} style={link}>{user.phone}</a>);
  if (user.linkedinUrl) items.push(<a key="l" href={user.linkedinUrl} target="_blank" rel="noopener noreferrer" style={link}>LinkedIn ↗</a>);
  if (user.teamsUrl) items.push(<a key="t" href={user.teamsUrl} target="_blank" rel="noopener noreferrer" style={link}>Teams chat ↗</a>);
  if (!user.title && !user.office && items.length === 0) return null;
  return (
    <div>
      {(user.title || user.office) && (
        <div style={{fontSize:14}}>
          {user.title}{user.title && user.office ? ' · ' : ''}{user.office && <span style={{color:'var(--fg-muted)'}}>{user.office}</span>}
        </div>
      )}
      {items.length > 0 && (
        <div style={{display:'flex', flexWrap:'wrap', alignItems:'center', gap:'4px 14px', marginTop:(user.title || user.office) ? 8 : 0, fontSize:13}}>
          {items}
        </div>
      )}
    </div>
  );
};

/* "Message" button on a colleague's profile header. Opens (or reuses) the
 * 1:1 direct-message thread and deep-links into the normal chat history, so
 * the conversation lives alongside every other chat. */
const MessageProfileButton = ({ userId }) => {
  const [busy, setBusy] = React.useState(false);
  const open = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { chatId } = await openDirectMessage(userId);
      window.location.hash = `chat/${chatId}`;
    } catch (err) {
      alert(err.message || 'Could not open the conversation.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      className="btn-orange"
      style={{padding:'7px 16px',fontSize:12.5,display:'inline-flex',alignItems:'center',gap:7,whiteSpace:'nowrap'}}
      onClick={open}
      disabled={busy}
      title="Send a direct message"
    >
      <Icon name="chat" size={15}/>
      {busy ? 'Opening…' : 'Message'}
    </button>
  );
};

/* Top-level profile navigation. Scrolls horizontally on narrow screens. */
const ProfileTabs = ({ tabs, active, onChange }) => (
  <div style={{display:'flex', gap:4, borderBottom:'1px solid var(--border)', margin:'0 0 4px', overflowX:'auto', WebkitOverflowScrolling:'touch'}}>
    {tabs.map(t => (
      <button key={t.id} onClick={() => onChange(t.id)} style={{
        padding:'9px 14px', fontSize:13.5, fontWeight:600, whiteSpace:'nowrap',
        background:'transparent', border:'none', cursor:'pointer',
        color: active === t.id ? 'var(--fg)' : 'var(--fg-muted)',
        borderBottom: active === t.id ? '2px solid var(--orange)' : '2px solid transparent',
      }}>{t.label}</button>
    ))}
  </div>
);

/* Lightweight section for view-mode content — a muted label, no card
 * chrome — so the bio stays dominant and the rest reads lighter. */
const LightSection = ({ label, children, style }) => (
  <div style={{marginTop:26, ...style}}>
    {label && <div style={{fontSize:11, letterSpacing:'0.08em', textTransform:'uppercase', color:'var(--fg-muted)', fontWeight:600, marginBottom:10}}>{label}</div>}
    {children}
  </div>
);

/* Admin-only lockout rescue on a person's Settings tab: mints a one-time
 * password-reset link (7-day TTL, shown once — tokens are stored hashed)
 * for the person to set a new password. When email delivery is configured
 * the link is also sent to them directly; either way the admin can copy
 * and share it over any channel. Issuing a new link kills the previous one. */
const ResetLinkSection = ({ userId, userName }) => {
  const [result, setResult] = React.useState(null); // { link, expiresAt, emailSent, emailConfigured }
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [copied, setCopied] = React.useState(false);

  const generate = async () => {
    setBusy(true); setError(null); setCopied(false);
    try { setResult(await createAdminResetLink(userId)); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    if (!result) return;
    const ok = await copyTextToClipboard(result.link);
    setCopied(ok);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div>
      <div style={{fontSize:13,color:'var(--fg-muted)',lineHeight:1.55,marginBottom:10}}>
        Locked out? Generate a one-time link that lets <strong style={{color:'var(--fg)'}}>{userName}</strong> set
        a new password — valid 7 days, usable once. A new link replaces any earlier one.
      </div>
      <button className="btn-ghost" style={{padding:'6px 14px',fontSize:12.5}} onClick={generate} disabled={busy}>
        {busy ? 'Generating…' : result ? 'Generate a new link' : 'Generate password-reset link'}
      </button>
      {error && <div style={{marginTop:10,padding:'8px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:12.5}}>{error}</div>}
      {result && (
        <div style={{marginTop:12,padding:12,background:'var(--bg-alt)',borderRadius:8}}>
          <div style={{display:'flex',gap:10,alignItems:'center'}}>
            <code style={{flex:1,fontSize:11.5,wordBreak:'break-all',color:'var(--fg)'}}>{result.link}</code>
            <button className="btn-orange" style={{padding:'5px 12px',fontSize:11.5,flexShrink:0}} onClick={copy}>
              {copied ? 'Copied ✓' : 'Copy'}
            </button>
          </div>
          <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:8}}>
            Shown once — copy it now. Expires {relativeTime(result.expiresAt)}.
            {result.emailConfigured
              ? (result.emailSent ? ' Also emailed to the person.' : ' Emailing it failed — share the link yourself.')
              : ' Email delivery isn’t configured, so share the link with the person yourself (Teams, etc.).'}
          </div>
        </div>
      )}
    </div>
  );
};

const TagRow = ({ items, type }) => (
  <div style={{display:'flex', flexWrap:'wrap', gap:6}}>
    {items.map(o => (
      <a key={o._id || o.id} className="tag" href={entityHref(type, entityHandle(o))} title={`Open ${o.name}`} style={{textDecoration:'none'}}>{o.name}</a>
    ))}
  </div>
);

const ExpertiseRow = ({ label, items, type }) => (
  <div style={{display:'flex', flexWrap:'wrap', gap:'4px 12px', alignItems:'baseline'}}>
    <span style={{fontSize:'var(--lbl-size)', letterSpacing:'0.04em', textTransform:'uppercase', color:'var(--lbl-color)', fontWeight:'var(--lbl-weight)', minWidth:96}}>{label}</span>
    <TagRow items={items} type={type}/>
  </div>
);

/* Full-page People detail dashboard. Mirrors ClientDetailView in
 * shape but the right rail is a private audit-log activity feed
 * (NOT the public news ticker).
 */
const PeopleDetailView = ({ userId, currentUser, onBack }) => {
  const isAdmin = currentUser?.role === 'admin';
  const [data, setData] = React.useState(null);
  const [tax, setTax] = React.useState(null);
  const [staffing, setStaffing] = React.useState(null);
  const [fnCatalog, setFnCatalog] = React.useState([]);
  const [editing, setEditing] = React.useState(false);
  const [tab, setTab] = React.useState('bio');   // top-level profile nav
  const [error, setError] = React.useState(null);

  // Refetch the profile WITHOUT blanking data (no loading flash, edit
  // panel stays mounted). Used after a firm-role change so the derived
  // functions[]/title/badges refresh.
  const reloadUser = React.useCallback(() => {
    getUserProfile(userId).then(setData).catch(() => {});
  }, [userId]);

  React.useEffect(() => {
    let cancelled = false;
    setError(null); setData(null); setStaffing(null);
    getUserProfile(userId)
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setError(err.message); });
    // Taxonomy catalog resolves the user's membership ids to names +
    // client logos; staffing feeds the auto-filled Projects list.
    listTaxonomies().then(t => { if (!cancelled) setTax(t); }).catch(() => { if (!cancelled) setTax({}); });
    getUserStaffing(userId).then(s => { if (!cancelled) setStaffing(s); }).catch(() => { if (!cancelled) setStaffing({ entries: [] }); });
    // Function catalog powers the editable Function picker (editors only).
    listAllFunctions().then(f => { if (!cancelled) setFnCatalog(f || []); }).catch(() => { if (!cancelled) setFnCatalog([]); });
    return () => { cancelled = true; };
  }, [userId]);

  // Replace the raw subPath id in the breadcrumb with the actual
  // person's name once the profile loads.
  useSubCrumbs(data?.user?.name
    ? [{ label: data.user.name, href: null }]
    : []);

  if (error) {
    return (
      <div className="page">
        <ErrorBox>{error}</ErrorBox>
        <button className="btn-ghost" onClick={onBack}>← Back to people</button>
      </div>
    );
  }
  if (!data) {
    return <div className="page"><Loading>Loading person…</Loading></div>;
  }

  const { user, functions = [], projects = [], activity = [], memberships = {} } = data;
  const viewer = data.viewer || {};
  const canEdit = !!viewer.canEdit;
  const isSelf = !!viewer.isSelf;
  // Self edits go through /api/me/profile; an admin editing someone else
  // goes through the admin route. Both accept the same profile fields.
  const saveProfile = async patch => {
    const updated = isSelf ? await patchMyProfile(patch) : await patchAdminUser(userId, patch);
    setData(d => (d ? { ...d, user: { ...d.user, ...updated } } : d));
  };
  // Resolve the user's taxonomy memberships against the firm catalog.
  const byId = (rows) => new Map((rows || []).map(r => [r._id || r.id, r]));
  const svcById = byId(tax?.services);
  const indById = byId(tax?.industries);
  const cliById = byId(tax?.clients);
  const orgById = byId(tax?.companies);
  const myServices   = (memberships.serviceIds  || []).map(id => svcById.get(id)).filter(Boolean);
  const myIndustries = (memberships.industryIds || []).map(id => indById.get(id)).filter(Boolean);
  const myClients    = (memberships.clientIds   || []).map(id => cliById.get(id)).filter(Boolean);
  const myOrgs       = (memberships.companyIds  || []).map(id => orgById.get(id)).filter(Boolean);

  // Top-level profile tabs. Insights + Settings only for self / admins.
  const showPrivate = isSelf || viewer.isAdmin;
  const tabs = [
    { id: 'bio', label: 'Bio' },
    { id: 'staffing', label: 'Staffing' },
    ...(showPrivate ? [{ id: 'other', label: 'Insights' }, { id: 'settings', label: 'Settings' }] : []),
  ];
  const activeTab = tabs.some(t => t.id === tab) ? tab : 'bio';

  // Projects = union of manual memberships (A) and staffing-derived
  // projects (B). Either source surfaces the engagement.
  const mergedProjects = mergePersonProjects(projects, staffing?.entries || []);
  // Project-history slider order: newest by start date first (undated
  // engagements sort last). Kept separate from mergedProjects, which is
  // active-first / name-sorted for the table below staffing.
  const projectHistory = [...mergedProjects].sort((a, b) => {
    const at = a.startsAt ? new Date(a.startsAt).getTime() : -Infinity;
    const bt = b.startsAt ? new Date(b.startsAt).getTime() : -Infinity;
    return bt - at;
  });
  const ttm = computeTtm(projects);
  const activeCount = mergedProjects.filter(p => !p.endsAt || new Date(p.endsAt).getTime() > Date.now()).length;

  const meta = (
    <>
      {mergedProjects.length} project{mergedProjects.length === 1 ? '' : 's'}{activeCount > 0 ? ` · ${activeCount} active` : ''}
      {ttm > 0 && <><br/>TTM contribution: {fmtAmount(ttm)} CHF</>}
    </>
  );

  return (
    <EditModeProvider canEdit={canEdit} editing={editing} onEditingChange={setEditing}>
    <EntityShell
      title={
        <EditableText
          value={user.name}
          onSave={v => saveProfile({ name: v })}
          placeholder="Full name"
          maxLength={80}
          inputStyle={{fontFamily:'var(--font-display)',fontSize:24,fontWeight:500}}
        />
      }
      subtitle={editing ? null : <ProfileContact user={user}/>}
      avatar={<Avatar src={user.photoUrl} name={user.name} size="xl"/>}
      meta={meta}
      headerAction={
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          {!isSelf && <MessageProfileButton userId={userId} name={user.name}/>}
          <EditSwitch/>
        </div>
      }
      rightRail={activeTab === 'staffing' ? null : <ActivityFeed items={activity}/>}
    >
      <ProfileTabs tabs={tabs} active={activeTab} onChange={setTab}/>
      {editing && activeTab === 'bio' && (
        <EntitySection title="Details">
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:14}}>
            <div className="field" style={{margin:0,gridColumn:'1 / -1'}}>
              <label>Profile photo</label>
              <ModalPhotoUploader
                isSelf={isSelf}
                targetUserId={userId}
                currentUrl={user.photoUrl}
                onUploaded={url => setData(d => (d ? { ...d, user: { ...d.user, photoUrl: url } } : d))}
                onClear={async () => {
                  try {
                    if (isSelf) await patchMyProfile({ photoUrl: null });
                    else await deleteAdminUserPhoto(userId);
                    setData(d => (d ? { ...d, user: { ...d.user, photoUrl: null } } : d));
                  } catch (err) { alert(err.message); }
                }}
              />
            </div>
            <div className="field" style={{margin:0}}>
              <label>Role / function</label>
              {(viewer.role === 'admin' || viewer.role === 'eOS-Manager') ? (
                <EditableSelect
                  value={functions[0]?.id || ''}
                  options={(fnCatalog || []).map(f => ({ value: f.id, label: f.name }))}
                  onSave={async v => { await patchAdminUser(userId, { functionId: v || null }); reloadUser(); }}
                  allowEmpty
                  placeholder="— pick a role —"
                />
              ) : (
                <div style={{fontSize:13,padding:'6px 0',color:'var(--fg-muted)'}}>{functions[0]?.name || '—'}</div>
              )}
            </div>
            <div className="field" style={{margin:0}}>
              <label>Office</label>
              <EditableSelect
                value={user.office || ''}
                options={(tax?.offices || []).map(o => ({ value: o.name, label: o.name }))}
                onSave={v => saveProfile({ office: v })}
                allowEmpty
                placeholder="— pick an office —"
              />
            </div>
            <div className="field" style={{margin:0}}>
              <label>LinkedIn URL</label>
              <EditableText value={user.linkedinUrl} onSave={v => saveProfile({ linkedinUrl: v })} placeholder="https://www.linkedin.com/in/…" maxLength={256}/>
            </div>
            <div className="field" style={{margin:0}}>
              <label>Email</label>
              <div style={{fontSize:13,padding:'6px 0',color:'var(--fg-muted)'}}>{user.email || '—'}</div>
            </div>
            <div className="field" style={{margin:0}}>
              <label>Phone</label>
              <EditableText value={user.phone} onSave={v => saveProfile({ phone: v })} placeholder="+41 79 123 45 67" maxLength={64}/>
            </div>
            <div className="field" style={{margin:0}}>
              <label>Teams chat link</label>
              <EditableText value={user.teamsUrlCustom} onSave={v => saveProfile({ teamsUrl: v })} placeholder="Leave empty to derive from email" maxLength={512}/>
              <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4}}>Empty = a chat deep-link built from the email.</div>
            </div>
            {viewer.canChangeRole && (
              <div className="field" style={{margin:0}}>
                <label>Admin role</label>
                <EditableSelect
                  value={user.role}
                  options={[{ value: 'user', label: 'User' }, { value: 'eOS-Manager', label: 'eOS-Manager' }, { value: 'admin', label: 'Admin' }]}
                  onSave={v => saveProfile({ role: v })}
                />
                <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4}}>Admins manage everyone and see the admin panel. eOS-Managers can edit all projects, clients, staffing, profiles, services, offices and industries — but not the admin panel, manual, logs or OrgRepo.</div>
              </div>
            )}
          </div>
        </EntitySection>
      )}

      {/* ── Bio tab — bio (dominant in view mode) + expertise + projects ── */}
      {activeTab === 'bio' && editing && canEdit && (
        <EntitySection title="Bio">
          <EditableText
            multiline
            rows={6}
            value={user.bio}
            onSave={v => saveProfile({ bio: v })}
            maxLength={2000}
            placeholder="Short intro — what you do, what you're known for, what you care about."
            viewRender={v => (v ? <MarkdownView source={v}/> : <span style={{color:'var(--fg-muted)'}}>No bio yet.</span>)}
          />
        </EntitySection>
      )}

      {activeTab === 'bio' && !editing && user.bio && (
        <div style={{fontSize:15.5, lineHeight:1.7, marginTop:6}}>
          <MarkdownView source={user.bio}/>
        </div>
      )}

      {activeTab === 'bio' && editing && canEdit && (
        <EntitySection title="Expertise">
          <ProfileExperience
            userId={isSelf ? undefined : userId}
            self={isSelf}
            initialPicks={isSelf ? null : memberships}
            showStaffing={false}
          />
        </EntitySection>
      )}

      {activeTab === 'bio' && !editing && (myServices.length > 0 || myIndustries.length > 0 || myClients.length > 0 || myOrgs.length > 0) && (
        <LightSection label="Expertise">
          <div style={{display:'flex', flexDirection:'column', gap:12}}>
            {myServices.length    > 0 && <ExpertiseRow label="Services"     items={myServices}    type="service"/>}
            {myIndustries.length  > 0 && <ExpertiseRow label="Industries"   items={myIndustries}  type="industry"/>}
            {myClients.length     > 0 && <ExpertiseRow label="Clients"      items={myClients}     type="client"/>}
            {myOrgs.length        > 0 && <ExpertiseRow label="Organizations" items={myOrgs}        type="organization"/>}
          </div>
        </LightSection>
      )}

      {activeTab === 'bio' && projectHistory.length > 0 && (
        <LightSection label="Project history">
          <ProjectHistorySlider projects={projectHistory} clientsById={cliById}/>
        </LightSection>
      )}

      {/* ── Staffing tab — weekly plan + daily booking in one container ── */}
      {activeTab === 'staffing' && (
        <LightSection style={{marginTop:16}}>
          <div className="staffing-bleed">
            {(isSelf || canEdit)
              ? <StaffingBookingPanel userId={userId} canEdit={canEdit}/>
              : <StaffingEmbed userIds={[userId]} weeks={16}/>}
            {editing && canEdit && (
              <div style={{marginTop:18}}>
                <StaffingSection userId={userId} viewerIsAdmin={!!viewer.canManage} self={isSelf} title="Edit staffing plan"/>
              </div>
            )}
          </div>
        </LightSection>
      )}

      {activeTab === 'bio' && (
        <LightSection label={`Projects · ${mergedProjects.length}`}>
        {mergedProjects.length === 0 ? (
          <div style={{padding:'14px 12px',color:'var(--fg-muted)',fontSize:13}}>
            Not staffed on any project yet. Projects appear here automatically from staffing, or when an admin adds this person to a project.
          </div>
        ) : (
          <table className="tbl tbl--stack" style={{width:'100%'}}>
            <thead>
              <tr>
                <th>Project</th>
                <th style={{width:180}}>Client</th>
                <th style={{width:100}}>Status</th>
                <th style={{width:90}}>Role</th>
                <th style={{width:140,textAlign:'right'}}>Volume</th>
                <th style={{width:160,textAlign:'right'}}>Dates</th>
              </tr>
            </thead>
            <tbody>
              {mergedProjects.map(p => (
                <tr key={p.projectId} style={{borderLeft:`3px solid ${STATUS_COLOR[p.status] || 'var(--fg-muted)'}`}}>
                  <td data-primary>
                    <div style={{fontWeight:500}}>
                      <EntityLink type="project" handle={p.projectSlug || p.projectId}>{p.projectName}</EntityLink>
                    </div>
                    <SourceTag sources={p.sources}/>
                  </td>
                  <td data-label="Client" style={{color:'var(--fg-muted)',fontSize:12.5}}>
                    <EntityLink type="client" handle={p.clientId} muted>{p.clientName || '—'}</EntityLink>
                  </td>
                  <td data-label="Status">
                    {p.status ? (
                      <span style={{fontSize:10.5,padding:'2px 8px',borderRadius:4,fontWeight:600,letterSpacing:'0.05em',textTransform:'uppercase',background:STATUS_BG[p.status] || 'var(--bg-card)',color:STATUS_COLOR[p.status] || 'var(--fg-muted)'}}>
                        {p.status}
                      </span>
                    ) : <span style={{color:'var(--fg-muted)',fontSize:11.5}}>—</span>}
                  </td>
                  <td data-label="Role" style={{color:'var(--fg-muted)',fontSize:11.5}}>{p.role || '—'}</td>
                  <td data-label="Volume" style={{textAlign:'right',fontFamily:'JetBrains Mono',fontSize:11.5,color:p.volumeChf ? 'var(--fg)' : 'var(--fg-muted)'}}>
                    {p.volumeChf ? `${fmtAmount(p.volumeChf)} CHF` : '—'}
                  </td>
                  <td data-label="Dates" style={{textAlign:'right',color:'var(--fg-muted)',fontSize:11.5}}>
                    {p.startsAt ? dateInputValue(p.startsAt) : '—'}
                    {p.endsAt ? <> → {dateInputValue(p.endsAt)}</> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </LightSection>
      )}

      {/* ── Insights tab — token usage + role compliance + eOS insights ── */}
      {activeTab === 'other' && showPrivate && (
        <>
          <LightSection label="Token usage">
            <TokenInsights userId={isSelf ? undefined : userId} self={isSelf}/>
          </LightSection>
          <LightSection label="Role compliance">
            <ComplianceSection userId={isSelf ? undefined : userId} self={isSelf}/>
          </LightSection>
          <LightSection label={<><span style={{textTransform:'none'}}>eOS</span> insights</>}>
            <eOSInsightsCard userId={isSelf ? undefined : userId} self={isSelf}/>
          </LightSection>
        </>
      )}

      {/* ── Settings tab — settings, integrations, data & memory ── */}
      {activeTab === 'settings' && showPrivate && (
        <LightSection style={{marginTop:16}}>
          <ProfileSettings user={user} userId={isSelf ? undefined : userId} self={isSelf}/>
        </LightSection>
      )}

      {activeTab === 'settings' && isAdmin && !isSelf && (
        <LightSection label="Access">
          <ResetLinkSection userId={userId} userName={user.name}/>
        </LightSection>
      )}

      {activeTab === 'settings' && isAdmin && !isSelf && (
        <DangerZoneSection
          noun="person"
          name={user.name}
          onDelete={() => deleteAdminUser(userId)}
          onDeleted={onBack}
        />
      )}
    </EntityShell>
    </EditModeProvider>
  );
};

/* Horizontal project-history slider — one card per engagement, newest
 * by start date first. Each card links to the project detail page and
 * shows the client/company logo, client name, and project name. Client
 * logos resolve from the taxonomy catalog by clientId; staffing-only
 * rows (no clientId) fall back to initials from the client name. */
const ProjectHistorySlider = ({ projects, clientsById }) => (
  <div style={{display:'flex',gap:12,overflowX:'auto',paddingBottom:6,WebkitOverflowScrolling:'touch'}}>
    {projects.map(p => {
      const client = p.clientId ? clientsById.get(p.clientId) : null;
      const href = (p.projectSlug || p.projectId) ? `#projects/${p.projectSlug || p.projectId}` : null;
      const card = (
        <>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
            <ClientLogo client={client || { name: p.clientName }} size={32}/>
            <div style={{fontSize:11.5,color:'var(--fg-muted)',minWidth:0,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{p.clientName || '—'}</div>
          </div>
          <div style={{fontWeight:600,fontSize:13.5,lineHeight:1.3}}>{p.projectName}</div>
          {p.startsAt && <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4}}>{dateInputValue(p.startsAt)}</div>}
        </>
      );
      const style = {
        flex:'0 0 auto',width:208,padding:'10px 0 10px 12px',background:'transparent',
        borderLeft:`3px solid ${STATUS_COLOR[p.status] || 'var(--fg-muted)'}`,
        color:'inherit',textDecoration:'none',display:'block',
      };
      return href ? (
        <a key={p.projectId} href={href} title={`Open ${p.projectName}`}
           style={{...style,cursor:'pointer'}}>
          {card}
        </a>
      ) : (
        <div key={p.projectId} style={style}>{card}</div>
      );
    })}
  </div>
);

/* Small muted marker showing whether a project came from staffing,
 * a manual project membership, or both. */
const SourceTag = ({ sources }) => {
  const has = (k) => sources && sources.has && sources.has(k);
  const label = has('staffing') && has('member') ? 'staffed · member'
    : has('staffing') ? 'from staffing'
    : 'project member';
  return (
    <div style={{fontSize:10,color:'var(--fg-muted)',marginTop:3,textTransform:'uppercase',letterSpacing:'0.05em'}}>{label}</div>
  );
};

/* Merge a person's manual project memberships with the projects they
 * are staffed on (client-project staffing entries carry a projectId).
 * Dedupe by projectId; track which source(s) contributed so the UI can
 * show it. Membership rows keep their richer metadata (status, dates,
 * volume, role); staffing-only rows fill name + client. */
function mergePersonProjects(memberProjects, staffingEntries) {
  const byId = new Map();
  for (const p of memberProjects) {
    if (!p.projectId) continue;
    byId.set(p.projectId, { ...p, sources: new Set(['member']) });
  }
  for (const e of staffingEntries) {
    if (!e.projectId) continue;  // only client-project entries reference a project
    const existing = byId.get(e.projectId);
    if (existing) { existing.sources.add('staffing'); continue; }
    byId.set(e.projectId, {
      projectId: e.projectId,
      projectName: e.projectName || '—',
      clientName: e.clientName || null,
      clientId: null,
      status: null, role: null, volumeChf: null, startsAt: null, endsAt: null,
      sources: new Set(['staffing']),
    });
  }
  return [...byId.values()].sort((a, b) => {
    const aActive = !a.endsAt || new Date(a.endsAt).getTime() > Date.now();
    const bActive = !b.endsAt || new Date(b.endsAt).getTime() > Date.now();
    if (aActive !== bActive) return aActive ? -1 : 1;
    return (a.projectName || '').localeCompare(b.projectName || '');
  });
}


/* ─── Projects ─────────────────────────────────────────────────── */

/* Phase 8: same list-with-detail pattern as the other directory
 * views. Dispatcher only — no hooks in the exported component so
 * subPath transitions don't tear down the tree. */
export const ProjectsView = ({ currentUser, subPath, onNavigate } = {}) => (
  subPath
    ? <ProjectDetailView projectId={subPath} currentUser={currentUser} onBack={() => onNavigate?.('')}/>
    : <ProjectsList currentUser={currentUser} onNavigate={onNavigate}/>
);

const projectStatusLabel = v => PROJECT_STATUS_OPTIONS.find(o => o.value === v)?.label || v;

const ProjectsList = ({ currentUser, onNavigate }) => {
  const [projects, setProjects] = React.useState(null);
  const [clientsById, setClientsById] = React.useState({});
  const [servicesById, setServicesById] = React.useState({});
  const [industriesById, setIndustriesById] = React.useState({});
  const [error, setError] = React.useState(null);
  const [q, setQ] = React.useState('');
  // Structured filters (single-select each, ANDed together).
  const [filterClient,      setFilterClient]      = React.useState('');
  const [filterService,     setFilterService]     = React.useState('');
  const [filterIndustry,    setFilterIndustry]    = React.useState('');
  const [filterLeadPartner, setFilterLeadPartner] = React.useState('');
  const [filterMember,      setFilterMember]      = React.useState('');
  const [filtersOpen,       setFiltersOpen]       = React.useState(false);
  const filterAnchorRef = React.useRef(null);
  const isAdmin = currentUser?.role === 'admin';
  const [recalcBusy, setRecalcBusy] = React.useState(false);
  const [recalcMsg, setRecalcMsg] = React.useState(null);

  const reload = React.useCallback(() => {
    Promise.all([
      listProjects().catch(err => { setError(err.message); return []; }),
      listTaxonomies().catch(() => ({ clients: [], services: [], industries: [] })),
    ]).then(([projs, tax]) => {
      setProjects(projs);
      setClientsById(Object.fromEntries((tax.clients || []).map(c => [c._id, c])));
      setServicesById(Object.fromEntries((tax.services || []).map(s => [s._id, s])));
      setIndustriesById(Object.fromEntries((tax.industries || []).map(i => [i._id, i])));
    });
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  const handleRecalcChf = async () => {
    if (!window.confirm('Recalculate the CHF volume of every project from its local volume, using the current governance FX rates (governance/exchange-rate.md)?')) return;
    setRecalcBusy(true); setRecalcMsg(null);
    try {
      const r = await recalcProjectsChf();
      setRecalcMsg(`Recalculated ${r.updated} project${r.updated === 1 ? '' : 's'} · rates as of ${r.asOf}.`);
      reload();
    } catch (e) {
      setRecalcMsg(`Failed: ${e.message}`);
    } finally {
      setRecalcBusy(false);
    }
  };

  // Build a single searchable string covering every field a project
  // carries, so the search box matches names, client, status, tags,
  // people, pricing, finance refs, etc. — not just name/client/desc.
  const haystack = React.useCallback(p => {
    const clientName    = clientsById[p.clientId]?.name || '';
    const serviceNames  = (p.serviceIds  || []).map(id => servicesById[id]?.name).filter(Boolean);
    const industryNames = (p.industryIds || []).map(id => industriesById[id]?.name).filter(Boolean);
    const typeLabels    = (p.types || []).map(projectTypeLabel);
    const people        = [...(p.leadPartners || []), ...(p.managers || []), ...(p.members || [])].map(x => x.name);
    return [
      p.name, p.description, clientName, p.status, projectStatusLabel(p.status),
      ...serviceNames, ...industryNames, ...typeLabels, ...people,
      p.creditor, p.debitor, p.currency, p.slug,
      p.volumeChf != null ? String(p.volumeChf) : '',
      p.volumeLocal != null ? String(p.volumeLocal) : '',
    ].filter(Boolean).join(' ').toLowerCase();
  }, [clientsById, servicesById, industriesById]);

  const filtered = React.useMemo(() => {
    if (!projects) return [];
    const tokens = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return projects.filter(p => {
      if (filterClient      && p.clientId !== filterClient)                      return false;
      if (filterService     && !(p.serviceIds     || []).includes(filterService))     return false;
      if (filterIndustry    && !(p.industryIds    || []).includes(filterIndustry))    return false;
      if (filterLeadPartner && !(p.leadPartnerIds || []).includes(filterLeadPartner)) return false;
      if (filterMember      && !(p.memberIds      || []).includes(filterMember))      return false;
      if (!tokens.length) return true;
      const hay = haystack(p);
      return tokens.every(t => hay.includes(t));
    });
  }, [projects, q, filterClient, filterService, filterIndustry, filterLeadPartner, filterMember, haystack]);

  // Distinct filter options, drawn from the projects actually loaded so
  // every option resolves to at least one row.
  const options = React.useMemo(() => {
    const clientIds = new Set(), serviceIds = new Set(), industryIds = new Set();
    const leads = new Map(), members = new Map();
    for (const p of (projects || [])) {
      if (p.clientId) clientIds.add(p.clientId);
      for (const id of (p.serviceIds  || [])) serviceIds.add(id);
      for (const id of (p.industryIds || [])) industryIds.add(id);
      for (const x of (p.leadPartners || [])) leads.set(x.userId, x.name);
      for (const x of (p.members      || [])) members.set(x.userId, x.name);
    }
    const byName = arr => arr.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return {
      clients:      byName([...clientIds].map(id => ({ id, name: clientsById[id]?.name || id }))),
      services:     byName([...serviceIds].map(id => ({ id, name: servicesById[id]?.name || id }))),
      industries:   byName([...industryIds].map(id => ({ id, name: industriesById[id]?.name || id }))),
      leadPartners: byName([...leads].map(([id, name]) => ({ id, name }))),
      members:      byName([...members].map(([id, name]) => ({ id, name }))),
    };
  }, [projects, clientsById, servicesById, industriesById]);

  const activeFilters = [
    filterClient      && { key: 'client',   label: `Client: ${options.clients.find(c => c.id === filterClient)?.name || filterClient}`,            clear: () => setFilterClient('') },
    filterService     && { key: 'service',  label: `Service: ${options.services.find(s => s.id === filterService)?.name || filterService}`,         clear: () => setFilterService('') },
    filterIndustry    && { key: 'industry', label: `Industry: ${options.industries.find(i => i.id === filterIndustry)?.name || filterIndustry}`,    clear: () => setFilterIndustry('') },
    filterLeadPartner && { key: 'lead',     label: `Lead: ${options.leadPartners.find(p => p.id === filterLeadPartner)?.name || filterLeadPartner}`, clear: () => setFilterLeadPartner('') },
    filterMember      && { key: 'member',   label: `Member: ${options.members.find(m => m.id === filterMember)?.name || filterMember}`,             clear: () => setFilterMember('') },
  ].filter(Boolean);
  const clearAll = () => {
    setQ(''); setFilterClient(''); setFilterService('');
    setFilterIndustry(''); setFilterLeadPartner(''); setFilterMember('');
  };
  const hasQueryOrFilter = !!q.trim() || activeFilters.length > 0;

  return (
    <div className="page">
      <div className="page__head">
        <h1>Projects</h1>
        <div className="lede">Every engagement the firm is running. Each project is anchored to one client.</div>
      </div>

      <div style={{display:'flex',gap:10,marginBottom:activeFilters.length?10:18,flexWrap:'wrap',alignItems:'center'}}>
        <input
          className="input"
          placeholder="Search projects — name, client, status, tags, people…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{flex:'1 1 320px',maxWidth:420,minWidth:0}}
        />
        <div ref={filterAnchorRef} style={{position:'relative'}}>
          <button
            type="button"
            onClick={() => setFiltersOpen(o => !o)}
            style={{
              padding:'8px 12px',border:'1px solid',borderRadius:6,fontSize:13,cursor:'pointer',
              background:'var(--bg-card)',color:'var(--fg)',
              borderColor: filtersOpen || activeFilters.length ? 'var(--orange)' : 'var(--border)',
              display:'inline-flex',alignItems:'center',gap:6,
            }}
          >
            Filters{activeFilters.length > 0 && (
              <span style={{background:'var(--orange)',color:'#fff',borderRadius:9,fontSize:10,fontWeight:700,padding:'1px 6px',lineHeight:1.4}}>{activeFilters.length}</span>
            )}
          </button>
          {filtersOpen && (
            <ProjectFilterPanel
              anchorRef={filterAnchorRef}
              options={options}
              values={{ filterClient, filterService, filterIndustry, filterLeadPartner, filterMember }}
              setters={{ setFilterClient, setFilterService, setFilterIndustry, setFilterLeadPartner, setFilterMember }}
              onClose={() => setFiltersOpen(false)}
            />
          )}
        </div>
        <div style={{flex:1}}/>
        {projects && <span style={{fontSize:12,color:'var(--fg-muted)',alignSelf:'center'}}>{filtered.length} of {projects.length}</span>}
        {isAdmin && (
          <button
            className="btn-ghost"
            disabled={recalcBusy}
            onClick={handleRecalcChf}
            title="Re-price every project's CHF volume from its local volume using the fixed governance FX table."
          >
            {recalcBusy ? 'Recalculating…' : 'Recalculate CHF'}
          </button>
        )}
        {isAdmin && <AddEntityButton kind="projects" onCreated={reload}/>}
      </div>
      {recalcMsg && (
        <div style={{fontSize:12,color:recalcMsg.startsWith('Failed') ? '#A20235' : 'var(--fg-muted)',marginTop:-8,marginBottom:14}}>
          {recalcMsg}
        </div>
      )}

      {activeFilters.length > 0 && (
        <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:18,alignItems:'center'}}>
          {activeFilters.map(f => (
            <span key={f.key} style={{display:'inline-flex',alignItems:'center',gap:6,background:'rgba(255,100,40,0.10)',border:'1px solid var(--orange)',borderRadius:14,padding:'3px 6px 3px 10px',fontSize:11.5}}>
              {f.label}
              <button onClick={f.clear} title="Remove" style={{all:'unset',cursor:'pointer',color:'var(--orange)',fontWeight:700,lineHeight:1,padding:'0 2px'}}>×</button>
            </span>
          ))}
          <button onClick={clearAll} style={{padding:'4px 8px',fontSize:11.5,border:'1px solid var(--border)',borderRadius:6,background:'var(--bg-card)',color:'var(--fg)',cursor:'pointer'}}>Clear all</button>
        </div>
      )}

      {error && <ErrorBox>{error}</ErrorBox>}
      {!projects && !error && <Loading>Loading projects…</Loading>}

      {projects && filtered.length === 0 && (
        <Empty>
          {hasQueryOrFilter
            ? 'No projects match.'
            : isAdmin ? 'No projects yet. Use the Add project button above.' : 'No projects yet. Ask an admin to add one.'}
        </Empty>
      )}

      {filtered.length > 0 && (
        <div className="tile-grid">
          {filtered.map(p => (
            <div
              key={p._id}
              className="kpi"
              style={{borderLeft:`3px solid ${STATUS_COLOR[p.status] || 'var(--fg-muted)'}`,cursor:'pointer'}}
              onClick={() => onNavigate?.(p.slug || p._id)}
            >
              <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:10,flexWrap:'wrap'}}>
                <span style={{fontSize:10.5,padding:'2px 8px',borderRadius:4,fontWeight:600,letterSpacing:'0.06em',textTransform:'uppercase',background:STATUS_BG[p.status] || 'var(--bg-card)',color:STATUS_COLOR[p.status] || 'var(--fg-muted)'}}>{p.status}</span>
                {(p.serviceIds || []).map(id => servicesById[id]).filter(Boolean).map(s => (
                  <span key={s._id} className="tag" style={{fontSize:10}}>{s.name}</span>
                ))}
                {(p.industryIds || []).map(id => industriesById[id]).filter(Boolean).map(i => (
                  <span key={i._id} className="tag" style={{fontSize:10}}>{i.name}</span>
                ))}
              </div>
              <div style={{fontFamily:'var(--font-display)',fontSize:17,lineHeight:1.2,marginBottom:6}}>{p.name}</div>
              <div style={{display:'flex',alignItems:'center',gap:8,fontSize:11.5,color:'var(--fg-muted)'}}>
                {clientsById[p.clientId] && <ClientLogo client={clientsById[p.clientId]} size={20}/>}
                <span>client: <strong style={{color:'var(--fg)'}}>{clientsById[p.clientId]?.name || p.clientLabel || '—'}</strong></span>
              </div>
              {p.description && (
                <div style={{fontSize:12.5,color:'var(--fg-muted)',marginTop:6,lineHeight:1.4,overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical'}}>
                  {stripMarkdownForPreview(p.description)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

    </div>
  );
};

/* Filter popover for the Projects list. One select per category
 * (client, service, industry, lead partner, member); selections AND
 * together and surface as removable chips in the parent. Closes on
 * outside-click or Escape. Mirrors the Staffing filter panel. */
const ProjectFilterPanel = ({ anchorRef, options, values, setters, onClose }) => {
  const ref = React.useRef(null);
  const pos = useAnchoredPosition(anchorRef, true, { align: 'start', width: 260, maxHeight: 420 });
  React.useEffect(() => {
    const onDoc = e => { if (!ref.current?.contains(e.target)) onClose(); };
    const onEsc = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [onClose]);

  const Row = ({ label, value, onChange, items, allLabel }) => (
    <div style={{marginBottom:12}}>
      <div style={{fontSize:10.5,fontWeight:600,color:'var(--fg-muted)',textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:4}}>{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)} style={{width:'100%',padding:'8px 10px',border:'1px solid var(--border)',borderRadius:6,fontSize:13,background:'var(--bg-card)',cursor:'pointer'}}>
        <option value="">{allLabel}</option>
        {items.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    </div>
  );

  return (
    <div ref={ref} style={{zIndex:200,overflowY:'auto',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,boxShadow:'0 10px 30px rgba(0,0,0,0.18)',padding:14,...(pos || HIDDEN_ANCHORED_STYLE)}}>
      <Row label="Client"        value={values.filterClient}      onChange={setters.setFilterClient}      items={options.clients}      allLabel="All clients"/>
      <Row label="Lead partner"  value={values.filterLeadPartner} onChange={setters.setFilterLeadPartner} items={options.leadPartners} allLabel="All lead partners"/>
      <Row label="Member"        value={values.filterMember}      onChange={setters.setFilterMember}      items={options.members}      allLabel="All members"/>
      <Row label="Industry"      value={values.filterIndustry}    onChange={setters.setFilterIndustry}    items={options.industries}   allLabel="All industries"/>
      <Row label="Service"       value={values.filterService}     onChange={setters.setFilterService}     items={options.services}     allLabel="All services"/>
      <div style={{display:'flex',justifyContent:'flex-end',marginTop:4}}>
        <button onClick={onClose} style={{padding:'5px 12px',border:'1px solid var(--border)',borderRadius:6,background:'var(--bg-card)',color:'var(--fg)',fontSize:13,cursor:'pointer'}}>Done</button>
      </div>
    </div>
  );
};

/* Project status → chip colours. Distinct from the active/paused/done
 * STATUS_COLOR map (a different status vocabulary used elsewhere). */
const PROJECT_STATUS_CHIP = {
  lead:          { fg: 'var(--fg-muted)',      bg: 'var(--bg-card)' },
  lost:          { fg: 'var(--support-red)',   bg: '#FCE6E0' },
  'won-waiting': { fg: '#1F6FEB',              bg: '#DCE7FF' },
  running:       { fg: 'var(--support-green)', bg: '#DCF1E2' },
  finished:      { fg: 'var(--fg-muted)',      bg: 'var(--bg-card)' },
};
const projectStatusChip = s => PROJECT_STATUS_CHIP[s] || { fg: 'var(--fg-muted)', bg: 'var(--bg-card)' };

/* The three statuses a person can hold on a project, surfaced as a
 * picker when adding and as a badge on each PersonCard. */
const TEAM_STATUS_OPTS = [
  { value: 'lead-partner', label: 'Lead Partner' },
  { value: 'manager',      label: 'Lead Manager' },
  { value: 'member',       label: 'Member' },
];
const TEAM_BADGE = {
  'lead-partner': { label: 'Lead Partner', bg: '#FFE7DC', fg: '#B83C0F' },
  'manager':      { label: 'Lead Manager', bg: '#EBE5F8', fg: '#471D65' },
  'member':       { label: 'Member',       bg: 'var(--bg-card)', fg: 'var(--fg-muted)' },
  'staffed':      { label: 'Staffed',      bg: 'var(--bg-card)', fg: 'var(--fg-muted)' },
};
const TeamBadge = ({ status }) => {
  const s = TEAM_BADGE[status] || TEAM_BADGE.member;
  return (
    <span style={{display:'inline-block',marginTop:4,fontSize:9.5,padding:'1px 6px',borderRadius:4,background:s.bg,color:s.fg,textTransform:'uppercase',letterSpacing:'0.06em',fontWeight:600}}>
      {s.label}
    </span>
  );
};

/* Add-to-team picker: choose a person AND their status in one go. The
 * parent routes the status to the right endpoint (lead-partners /
 * managers / members). */
const ProjectTeamAdd = ({ users, existingIds, onAdd, busy }) => {
  const [userId, setUserId] = React.useState('');
  const [status, setStatus] = React.useState('member');
  if (!users) return <span style={{fontSize:11,color:'var(--fg-muted)',fontWeight:400,letterSpacing:0,textTransform:'none'}}>loading directory…</span>;
  const candidates = users.filter(u => !existingIds.has(u._id));
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:6,fontWeight:400,letterSpacing:0,textTransform:'none',flexWrap:'wrap'}}>
      <select
        value={userId}
        onChange={e => setUserId(e.target.value)}
        style={{padding:'3px 6px',fontSize:11.5,border:'1px solid var(--border)',borderRadius:6,background:'var(--bg)'}}
        disabled={busy}
      >
        <option value="">+ add person…</option>
        {candidates.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
      </select>
      <select
        value={status}
        onChange={e => setStatus(e.target.value)}
        style={{padding:'3px 6px',fontSize:11.5,border:'1px solid var(--border)',borderRadius:6,background:'var(--bg)'}}
        disabled={busy}
      >
        {TEAM_STATUS_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <button
        type="button"
        className="btn-orange"
        style={{padding:'3px 10px',fontSize:11.5}}
        disabled={!userId || busy}
        onClick={() => { onAdd(userId, status); setUserId(''); setStatus('member'); }}
      >
        Add
      </button>
    </span>
  );
};

/* Project description — editable on the Overview tab, with a Claude
 * "suggest" helper. View mode renders markdown; edit mode is a textarea
 * that autosaves through the parent's onSave. */
const ProjectDescriptionSection = ({ project, files = [], editing, canEdit, onSave }) => {
  const [draft, setDraft] = React.useState(project.description || '');
  const [suggesting, setSuggesting] = React.useState(false);
  const [error, setError] = React.useState(null);
  React.useEffect(() => { setDraft(project.description || ''); }, [project.id]);

  const dirty = draft !== (project.description || '');
  const save = async () => {
    setError(null);
    try { await onSave({ description: draft }); }
    catch (err) { setError(err.message); throw err; }
  };
  const { status: saveStatus } = useAutoSave({ dirty, save, delay: 1200, signature: draft });

  const suggest = async () => {
    setSuggesting(true); setError(null);
    try {
      const result = await suggestProjectDescription(project.id);
      if (result?.description) setDraft(result.description);
    } catch (err) { setError(err.message); }
    finally { setSuggesting(false); }
  };

  if (!editing || !canEdit) {
    if (!project.description) return null;
    return (
      <EntitySection title="Description">
        <MarkdownView source={project.description}/>
      </EntitySection>
    );
  }
  return (
    <EntitySection
      title="Description"
      action={
        <button
          type="button"
          className="btn-ghost"
          onClick={suggest}
          disabled={suggesting}
          style={{padding:'3px 10px',fontSize:11,fontWeight:600,color:'var(--orange)'}}
          title={files.length > 0
            ? `Ask Claude to draft a description from the project metadata + ${files.length} attached file${files.length === 1 ? '' : 's'}.`
            : 'Ask Claude to draft a description from this project\'s metadata.'}
        >
          {suggesting ? '…drafting' : '✨ Suggest'}
        </button>
      }
    >
      <textarea
        className="textarea"
        rows={6}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        placeholder="One paragraph describing the engagement. Click ✨ Suggest to have Claude draft one."
        style={{width:'100%'}}
      />
      {error && <div style={{padding:8,marginTop:8,background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12}}>{error}</div>}
      <div style={{display:'flex',justifyContent:'flex-end',marginTop:10}}>
        <SaveStatus status={saveStatus} error={error} dirty={dirty}/>
      </div>
    </EntitySection>
  );
};

/* Full-page Project detail view. Previously a popup modal; now
 * embedded in the main column inside EntityShell so it sits in
 * the same shell as Client/People/Industry/Service detail views.
 * Same data + same actions (upload, member edit, commercials),
 * just rendered as a real page. */
const ProjectDetailView = ({ projectId, currentUser, onBack }) => {
  const isAdmin = currentUser?.role === 'admin';
  // Firm editors (admin + eOS-Manager) get the roster/file management
  // surface; entity deletion (Settings tab) stays admin-only.
  const canManage = isAdmin || currentUser?.role === 'eOS-Manager';
  const [tab, setTab] = React.useState('overview');
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [users, setUsers] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [addingMember, setAddingMember] = React.useState(false);

  const reload = React.useCallback(() => {
    getProjectProfile(projectId)
      .then(setData)
      .catch(err => setError(err.message));
  }, [projectId]);

  useSubCrumbs(data?.project?.name
    ? [{ label: data.project.name, href: null }]
    : []);

  React.useEffect(() => { reload(); }, [reload]);
  // Firm editors can add members from the firm directory — pull the list
  // once. Uses the open /api/people directory (same source the client +
  // leads pickers use) so eOS-Managers aren't blocked by the admin-only
  // /api/admin/users analytics endpoint.
  React.useEffect(() => {
    if (!canManage) return;
    listPeople().then(setUsers).catch(() => setUsers([]));
  }, [canManage]);

  // Staffing chart input: pivot the monthly per-person breakdown
  // (month-major in data.monthly) into one stacked series per person.
  // Kept above the early returns so the hook order stays stable.
  const projectStaffSeries = React.useMemo(() => {
    const byPerson = new Map();
    for (const m of (data?.monthly || [])) {
      for (const pp of (m.people || [])) {
        let s = byPerson.get(pp.userId);
        if (!s) { s = { key: pp.userId, label: pp.name, byMonth: {} }; byPerson.set(pp.userId, s); }
        s.byMonth[m.ym] = (s.byMonth[m.ym] || 0) + pp.days;
      }
    }
    return [...byPerson.values()];
  }, [data?.monthly]);

  const handleUpload = async file => {
    setUploading(true);
    try {
      await uploadProjectFile(projectId, file);
      reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async fileId => {
    if (!confirm('Delete this file?')) return;
    try {
      await deleteProjectFile(projectId, fileId);
      reload();
    } catch (err) {
      setError(err.message);
    }
  };

  // Unified team add/remove — route a chosen status to the right
  // endpoint so one picker covers lead partners, managers and members.
  const handleAddTeam = async (userId, status) => {
    setAddingMember(true);
    try {
      if (status === 'lead-partner') await addProjectLeadPartner(projectId, userId);
      else if (status === 'manager') await addProjectManager(projectId, userId);
      else await addProjectMember(projectId, userId, 'Member');
      reload();
    } catch (err) { setError(err.message); }
    finally { setAddingMember(false); }
  };
  const handleRemoveTeam = async (userId, status) => {
    if (!confirm('Remove this person from the project team?')) return;
    try {
      if (status === 'lead-partner') await removeProjectLeadPartner(projectId, userId);
      else if (status === 'manager') await removeProjectManager(projectId, userId);
      else await removeProjectMember(projectId, userId);
      reload();
    } catch (err) { setError(err.message); }
  };

  if (error) {
    return (
      <div className="page">
        <ErrorBox>{error}</ErrorBox>
        <button className="btn-ghost" onClick={onBack}>← Back to projects</button>
      </div>
    );
  }
  if (!data) {
    return <div className="page"><Loading>Loading project…</Loading></div>;
  }

  const { project, client, services = [], industries = [], members, files, leadPartners = [], managers = [] } = data;
  const canEdit = !!data.viewer?.canEdit;
  const serviceLookup = Object.fromEntries(services.map(s => [s.id, s.name]));
  // Revenue chart input: this one project as a single-element list, so we
  // can reuse MonthlyRevenueStack (stacked by the project's services).
  const revenueProjects = [{ ...project, services: project.serviceIds || services.map(s => s.id) }];
  // Roster + file management hit firm-editor endpoints (admin +
  // eOS-Manager); project fields (commercials/description/title) are
  // owner-aware. Both surfaces only appear while the Edit switch is on.
  const adminEditing = canManage && editing;
  const fieldEditing = canEdit && editing;
  const saveProject = async patch => {
    const updated = await patchProjectInline(project.slug || project.id, patch);
    setData(d => (d ? { ...d, project: { ...d.project, ...updated } } : d));
  };

  // Unified, deduped team list. Priority: lead partner > manager >
  // member, so a person who holds several roles shows once at the
  // highest. Staffed-but-not-explicit members carry the `fixed` flag and
  // can't be removed here (remove their staffing instead).
  const teamMap = new Map();
  for (const p of leadPartners) teamMap.set(p.userId, { ...p, status: 'lead-partner' });
  for (const p of managers)     if (!teamMap.has(p.userId)) teamMap.set(p.userId, { ...p, status: 'manager' });
  for (const m of members)      if (!teamMap.has(m.userId)) teamMap.set(m.userId, { ...m, status: 'member' });
  const team = [...teamMap.values()];

  const tabs = [
    { id: 'overview',    label: 'Overview' },
    { id: 'commercials', label: 'Commercials' },
    { id: 'staffing',    label: 'Staffing' },
    ...(isAdmin ? [{ id: 'settings', label: 'Settings' }] : []),
  ];
  const activeTab = tabs.some(t => t.id === tab) ? tab : 'overview';

  const statusChip = projectStatusChip(project.status);
  const dateRange = (project.startsAt || project.endsAt)
    ? `${project.startsAt ? dateInputValue(project.startsAt) : '…'}${project.endsAt ? ` → ${dateInputValue(project.endsAt)}` : ''}`
    : null;

  // Header subtitle: client name followed by status + service + industry
  // chips, all on one line that wraps when it runs long.
  const subtitle = (
    <span style={{display:'flex',flexWrap:'wrap',alignItems:'center',gap:'6px 8px'}}>
      {client
        ? <a href={`#clients/${client.slug || client.id}`} style={{color:'var(--orange)',fontWeight:600,fontSize:14}}>{client.name}</a>
        : project.clientLabel ? <span style={{fontWeight:600,fontSize:14}}>{project.clientLabel}</span> : null}
      <span style={{fontSize:10.5,padding:'2px 9px',borderRadius:10,fontWeight:600,letterSpacing:'0.04em',textTransform:'uppercase',background:statusChip.bg,color:statusChip.fg}}>
        {projectStatusLabel(project.status)}
      </span>
      {services.map(s => (
        <a key={s.id} className="tag" href={entityHref('service', entityHandle(s))} title={`Open ${s.name}`} style={{textDecoration:'none',fontSize:11.5,padding:'2px 9px'}}>{s.name}</a>
      ))}
      {industries.map(i => (
        <a key={i.id} href={entityHref('industry', entityHandle(i))} title={`Open ${i.name}`} style={{fontSize:11.5,padding:'2px 9px',borderRadius:10,background:'#EBE5F8',color:'#471D65',textDecoration:'none'}}>{i.name}</a>
      ))}
    </span>
  );

  // Right of the header: just the budget, large.
  const meta = project.volumeChf != null ? (
    <div style={{textAlign:'right'}}>
      <div style={{fontFamily:'var(--font-display)',fontSize:28,fontWeight:500,color:'var(--fg)',lineHeight:1.1}}>
        {fmtAmount(project.volumeChf)} <span style={{fontSize:16,color:'var(--fg-muted)'}}>CHF</span>
      </div>
      {project.volumeLocal != null && project.currency && project.currency !== 'CHF' && (
        <div style={{fontSize:12,color:'var(--fg-muted)',marginTop:2}}>{fmtAmount(project.volumeLocal)} {project.currency}</div>
      )}
    </div>
  ) : null;

  return (
    <EditModeProvider canEdit={canEdit} editing={editing} onEditingChange={setEditing}>
    <EntityShell
      avatar={client ? (
        client.logoUrl
          ? <a href={`#clients/${client.slug || client.id}`} title={client.name}><img src={client.logoUrl} alt={client.name} style={{width:56,height:56,objectFit:'contain',borderRadius:10,background:'var(--bg)',border:'1px solid var(--border)'}}/></a>
          : <a href={`#clients/${client.slug || client.id}`} title={client.name} style={{width:56,height:56,borderRadius:10,background:'var(--bg)',border:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'var(--font-display)',fontSize:22,color:'var(--fg-muted)',textDecoration:'none'}}>{(client.name || '?').charAt(0)}</a>
      ) : null}
      title={
        <EditableText
          value={project.name}
          onSave={v => saveProject({ name: v })}
          placeholder="Project name"
          maxLength={120}
          inputStyle={{fontFamily:'var(--font-display)',fontSize:24,fontWeight:500}}
        />
      }
      subtitle={subtitle}
      meta={meta}
      headerAction={<EditSwitch/>}
    >
      <ProfileTabs tabs={tabs} active={activeTab} onChange={setTab}/>

      {/* ── Overview — status, team, description, files ── */}
      {activeTab === 'overview' && (
        <>
          <EntitySection title="Status">
            <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
              <span style={{fontSize:12.5,padding:'4px 12px',borderRadius:20,fontWeight:600,letterSpacing:'0.04em',textTransform:'uppercase',background:statusChip.bg,color:statusChip.fg}}>
                {projectStatusLabel(project.status)}
              </span>
              {(project.types || []).length > 0 && (
                <span style={{fontSize:12.5,color:'var(--fg-muted)'}}>{project.types.map(projectTypeLabel).join(' · ')}</span>
              )}
              {dateRange && <span style={{fontSize:12.5,color:'var(--fg-muted)'}}>{dateRange}</span>}
            </div>
          </EntitySection>

          <EntitySection
            title={`Team · ${team.length}`}
            action={adminEditing
              ? <ProjectTeamAdd
                  users={users}
                  existingIds={new Set(team.map(t => t.userId))}
                  onAdd={handleAddTeam}
                  busy={addingMember}
                />
              : null}
          >
            {team.length === 0 ? (
              <div style={{padding:'12px',color:'var(--fg-muted)',fontSize:13}}>
                No team yet. {canManage ? 'Turn on Edit to add people and choose their status.' : ''}
              </div>
            ) : (
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:10}}>
                {team.map(t => {
                  const isStaffedMember = t.status === 'member' && t.fixed;
                  return (
                    <PersonCard
                      key={t.userId}
                      userId={t.userId}
                      name={t.name}
                      photoUrl={t.photoUrl}
                      title={t.title}
                      lines={[[t.company, t.office].filter(Boolean).join(' · ')]}
                      badge={<TeamBadge status={isStaffedMember ? 'staffed' : t.status}/>}
                      onRemove={adminEditing && !isStaffedMember ? () => handleRemoveTeam(t.userId, t.status) : undefined}
                    />
                  );
                })}
              </div>
            )}
          </EntitySection>

          <ProjectDescriptionSection
            project={project}
            files={files}
            editing={fieldEditing}
            canEdit={canEdit}
            onSave={saveProject}
          />

          <EntitySection
            title={`Files · ${files.length}`}
            action={adminEditing
              ? (
                <>
                  <button
                    type="button"
                    className="btn-ghost"
                    style={{padding:'4px 12px',fontSize:12}}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading…' : '+ Upload file'}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{display:'none'}}
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) handleUpload(f);
                      e.target.value = '';
                    }}
                  />
                </>
              )
              : null}
          >
            {files.length === 0 ? (
              <div style={{padding:'12px',color:'var(--fg-muted)',fontSize:13}}>
                No files yet. {canManage ? 'Use the button above to upload the first one.' : ''}
              </div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                {files.map(f => (
                  <div key={f.id} style={{padding:'8px 0',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:10}}>
                    <MimeIcon mime={f.mime} size={18} tint="var(--fg-muted)"/>
                    <a href={f.url} target="_blank" rel="noopener noreferrer" style={{flex:1,minWidth:0,color:'inherit',textDecoration:'none'}}>
                      <div style={{fontSize:13,fontWeight:500,wordBreak:'break-word'}}>{f.name}</div>
                      <div style={{fontSize:11,color:'var(--fg-muted)'}}>
                        {fmtBytes(f.size)}{f.uploadedBy ? ` · by ${f.uploadedBy}` : ''}
                      </div>
                    </a>
                    {adminEditing && (
                      <button
                        type="button"
                        onClick={() => handleDeleteFile(f.id)}
                        title="Delete file"
                        style={{background:'transparent',border:0,color:'var(--support-red)',cursor:'pointer',padding:4,fontSize:14}}
                      >🗑</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </EntitySection>
        </>
      )}

      {/* ── Commercials — revenue, monthly table, terms + day rates ── */}
      {activeTab === 'commercials' && (
        <>
          <EntitySection title="Revenue — monthly, stacked by service">
            <MonthlyRevenueStack
              projects={revenueProjects}
              stackKey="services"
              tagLookup={serviceLookup}
              months={18}
              footnote="Last 18 months. Stored monthly revenue (or the top-line volume spread across the project's working days), split across the project's tagged services."
            />
          </EntitySection>

          <EntitySection title="Monthly revenue">
            <ProjectMonthlyTable
              project={project}
              initialMonths={data.monthly || []}
              rateLines={data.rateLines || []}
              fxToChf={data.fxToChf}
              editing={fieldEditing}
              onSaved={months => setData(d => (d ? { ...d, monthly: months } : d))}
            />
          </EntitySection>

          <EntitySection title="Commercial terms">
            {fieldEditing ? (
              <ProjectCommercialsEditor
                project={project}
                files={files}
                onSave={saveProject}
                hideDescription
              />
            ) : (
              <ProjectCommercialsReadOnly project={project}/>
            )}
            {(project.types || []).includes('time-and-material') && (
              <div style={{marginTop:18}}>
                <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600,marginBottom:10}}>
                  Day rates (Time &amp; Material)
                </div>
                <ProjectRateTable
                  project={project}
                  initialLines={data.rateLines || []}
                  editing={fieldEditing}
                  onSaved={saved => setData(d => (d ? { ...d, rateLines: saved } : d))}
                />
              </div>
            )}
          </EntitySection>
        </>
      )}

      {/* ── Staffing — monthly stack + weekly booking, one container ── */}
      {activeTab === 'staffing' && (
        <EntitySection title="Staffing &amp; booking">
          <div className="staffing-bleed">
            <MonthlyStaffingStack
              series={projectStaffSeries}
              footnote="Staffed person-days per month for this project (1 day = 8 h, derived from staffing %), one segment per person. A week counts toward the month of its Monday."
            />
            <div style={{marginTop:18}}>
              <StaffingEmbed projectId={project.id} weeks={16} bare/>
            </div>
          </div>
        </EntitySection>
      )}

      {/* ── Settings — admin-only danger zone ── */}
      {activeTab === 'settings' && isAdmin && (
        <DangerZoneSection
          noun="project"
          name={project.name}
          kind="project"
          entityId={project.id}
          onDeleted={onBack}
        />
      )}
    </EntityShell>
    </EditModeProvider>
  );
};

/* ─── project commercials (read-only + editable) ──────────────── */

const ProjectCommercialsReadOnly = ({ project }) => {
  const rows = [
    { label: 'Status',           value: PROJECT_STATUS_OPTIONS.find(o => o.value === project.status)?.label || project.status || '—' },
    { label: 'Type',             value: (project.types || []).length ? project.types.map(projectTypeLabel).join(', ') : '—' },
    { label: 'Volume (CHF)',     value: project.volumeChf != null ? `${fmtAmount(project.volumeChf)} CHF` : '—' },
    { label: 'Volume (local)',   value: project.volumeLocal != null ? `${fmtAmount(project.volumeLocal)} ${project.currency || ''}`.trim() : '—' },
    { label: 'Start',            value: project.startsAt ? new Date(project.startsAt).toLocaleDateString() : '—' },
    { label: 'End',              value: project.endsAt ? new Date(project.endsAt).toLocaleDateString() : '—' },
    { label: 'Creditor',         value: project.creditor || '—' },
    { label: 'Debitor',          value: project.debitor || '—' },
  ];
  return (
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:8,marginBottom:18}}>
      {rows.map(r => (
        <div key={r.label} style={{padding:'2px 0'}}>
          <div style={{fontSize:'var(--lbl-size)',letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--lbl-color)',fontWeight:'var(--lbl-weight)',marginBottom:2}}>{r.label}</div>
          <div style={{fontSize:'var(--val-size)',color:'var(--val-color)',fontWeight:'var(--val-weight)'}}>{r.value}</div>
        </div>
      ))}
    </div>
  );
};

const ProjectCommercialsEditor = ({ project, files = [], onSave, hideDescription = false }) => {
  const [busy, setBusy] = React.useState(false);
  const [suggesting, setSuggesting] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Creditor is no longer free text — it's picked from the firm's master
  // list (/governance/creditors.md). Fetch the options once; if the
  // project's stored value isn't in the list (legacy data), keep it as a
  // selectable option so saving doesn't silently drop it.
  const [creditorOptions, setCreditorOptions] = React.useState([]);
  React.useEffect(() => {
    let cancelled = false;
    listCreditors().then(list => { if (!cancelled) setCreditorOptions(list); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // This editor only mounts while the page-level Edit switch is on, so
  // there's no separate "edit" click — the fields are live immediately.
  // Saves go through the parent's merge (onSave) rather than a refetch,
  // so an auto-save mid-edit never unmounts the form.
  const formFromProject = (p) => ({
    status:      p.status || 'lead',
    types:       Array.isArray(p.types) ? p.types : [],
    volumeChf:   p.volumeChf ?? '',
    volumeLocal: p.volumeLocal ?? '',
    currency:    p.currency || '',
    startsAt:    dateInputValue(p.startsAt),
    endsAt:      dateInputValue(p.endsAt),
    creditor:    p.creditor || '',
    debitor:     p.debitor || '',
    description: p.description || '',
  });
  const [form, setForm] = React.useState(() => formFromProject(project));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      const patch = {
        status:      form.status,
        types:       form.types,
        volumeChf:   form.volumeChf === '' ? null : Number(form.volumeChf),
        volumeLocal: form.volumeLocal === '' ? null : Number(form.volumeLocal),
        currency:    form.currency || null,
        startsAt:    form.startsAt || null,
        endsAt:      form.endsAt || null,
        creditor:    form.creditor.trim() || null,
        debitor:     form.debitor.trim() || null,
        description: form.description,
      };
      await onSave(patch);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const suggestDescription = async () => {
    setSuggesting(true); setError(null);
    try {
      const result = await suggestProjectDescription(project.id);
      if (result?.description) {
        setForm(f => ({ ...f, description: result.description }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSuggesting(false);
    }
  };

  // FX conversion — fires on local-volume blur OR when currency /
  // start-date changes. Stores the latest rate + as-of + confidence
  // so the UI can show the source under the CHF field.
  const [converting, setConverting] = React.useState(false);
  const [conversion, setConversion] = React.useState(null);
  const [conversionError, setConversionError] = React.useState(null);
  const formRef = React.useRef(form);
  React.useEffect(() => { formRef.current = form; }, [form]);

  const triggerConversion = React.useCallback(async () => {
    const f = formRef.current;
    if (!f.volumeLocal || !f.currency) {
      // No conversion possible without both fields; clear stale CHF.
      setConversion(null); setConversionError(null);
      return;
    }
    setConverting(true); setConversionError(null);
    try {
      const result = await convertCurrencyForProject(project.id, {
        amount: Number(f.volumeLocal),
        currency: f.currency,
        date: f.startsAt || undefined,
      });
      setConversion(result);
      setForm(prev => ({ ...prev, volumeChf: String(result.amount_chf) }));
    } catch (err) {
      setConversionError(err.message);
    } finally {
      setConverting(false);
    }
  }, [project.id]);

  const dirty = JSON.stringify(form) !== JSON.stringify(formFromProject(project));
  const { status: saveStatus } = useAutoSave({
    dirty,
    save,
    enabled: !converting,
    delay: 1200,
    signature: JSON.stringify(form),
  });

  return (
    <div style={{marginBottom:18}}>
      <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:10,fontWeight:600}}>
        Edit project
      </div>
      <div className="respo-2col" style={{gap:10,marginBottom:10}}>
        <Field label="Status">
          <select className="select" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
            {PROJECT_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Field>
        <Field label="Type">
          <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
            {PROJECT_TYPE_OPTIONS.map(o => {
              const on = form.types.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, types: on ? f.types.filter(t => t !== o.value) : [...f.types, o.value] }))}
                  style={{padding:'5px 10px',fontSize:12,borderRadius:6,cursor:'pointer',border:`1px solid ${on ? 'var(--orange)' : 'var(--border)'}`,background:on ? 'var(--orange)' : 'transparent',color:on ? '#fff' : 'var(--fg)',fontWeight:on ? 600 : 400}}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </Field>
        <Field label="Start date">
          <input
            className="input" type="date"
            value={form.startsAt}
            onChange={e => { setForm(f => ({ ...f, startsAt: e.target.value })); setTimeout(triggerConversion, 0); }}
          />
        </Field>
        <Field label="End date">
          <input className="input" type="date" value={form.endsAt} onChange={e => setForm(f => ({ ...f, endsAt: e.target.value }))}/>
        </Field>
      </div>

      {/* Local volume + currency on the LEFT (user-edited), CHF on
          the RIGHT (Claude-converted, read-only). The conversion
          fires on blur of the volume field OR whenever currency /
          start-date changes; the rate + as-of + confidence appear
          as a hint under the CHF field. */}
      <div className="respo-2col" style={{gap:10,marginBottom:10,alignItems:'start'}}>
        <Field label="Volume (local currency)">
          <div style={{display:'flex',gap:6}}>
            <input
              className="input" inputMode="numeric"
              value={form.volumeLocal}
              onChange={e => setForm(f => ({ ...f, volumeLocal: e.target.value.replace(/[^\d]/g, '') }))}
              onBlur={triggerConversion}
              placeholder="e.g. 250000"
              style={{flex:1}}
            />
            <select
              className="select"
              value={form.currency}
              onChange={e => { setForm(f => ({ ...f, currency: e.target.value })); setTimeout(triggerConversion, 0); }}
              style={{flex:'0 0 88px'}}
            >
              <option value="">—</option>
              {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </Field>
        <Field label="Volume (CHF)">
          <input
            className="input" inputMode="numeric" readOnly
            value={form.volumeChf}
            placeholder={form.volumeLocal && form.currency ? 'converting…' : '—'}
            style={{background:'var(--bg-alt)',cursor:'default'}}
            title="Auto-computed from the local volume × the FX rate Claude estimates for the start date (or today)."
          />
          {converting && (
            <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:3}}>Asking Claude for the rate…</div>
          )}
          {!converting && conversion && (
            <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:3,lineHeight:1.4}}>
              Rate <strong style={{color:'var(--fg)'}}>{conversion.rate.toFixed(4)}</strong> CHF / {form.currency} ·
              as of {conversion.as_of} · {conversion.confidence} confidence
            </div>
          )}
          {!converting && conversionError && (
            <div style={{fontSize:11,color:'#A20235',marginTop:3}}>Conversion failed: {conversionError}</div>
          )}
        </Field>
      </div>
      {/* Finance references. Creditor (who the firm bills against) is now
          picked from the master list in /governance/creditors.md — an org's
          revenue rolls up from the projects sharing its creditors. Debitor
          (who's billed) stays free text. */}
      <div className="respo-2col" style={{gap:10,marginBottom:10}}>
        <Field label="Creditor">
          <select
            className="select"
            value={form.creditor}
            onChange={e => setForm(f => ({ ...f, creditor: e.target.value }))}
          >
            <option value="">—</option>
            {/* Legacy / off-list value: keep it selectable so a save can't drop it. */}
            {form.creditor && !creditorOptions.some(c => c === form.creditor) && (
              <option value={form.creditor}>{form.creditor} (not in list)</option>
            )}
            {creditorOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Debitor">
          <input
            className="input"
            value={form.debitor}
            onChange={e => setForm(f => ({ ...f, debitor: e.target.value }))}
            placeholder="e.g. billed party / SAP id"
            maxLength={200}
          />
        </Field>
      </div>

      {!hideDescription && (
        <>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:6,marginBottom:3}}>
            <div style={{fontSize:10.5,letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600}}>Description</div>
            <button
              type="button"
              className="btn-ghost"
              onClick={suggestDescription}
              disabled={suggesting || busy}
              style={{padding:'3px 10px',fontSize:11,fontWeight:600,color:'var(--orange)'}}
              title={files.length > 0
                ? `Ask Claude to draft a description based on the project metadata + ${files.length} attached file${files.length === 1 ? '' : 's'}.`
                : 'Ask Claude to draft a description based on the project metadata. Attach files first for a richer summary.'}
            >
              {suggesting ? '…drafting' : '✨ Suggest description'}
            </button>
          </div>
          <textarea
            className="textarea"
            rows={5}
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="One paragraph. Click ✨ Suggest description to have Claude draft one from this project's metadata and attached files."
          />
        </>
      )}
      {error && <div style={{padding:8,marginTop:8,background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12}}>{error}</div>}
      <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:12,alignItems:'center'}}>
        <SaveStatus status={saveStatus} error={error} dirty={dirty}/>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label style={{display:'block'}}>
    <div style={{fontSize:'var(--lbl-size)',letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--lbl-color)',fontWeight:'var(--lbl-weight)',marginBottom:3}}>{label}</div>
    {children}
  </label>
);

/* ─── Project T&M daily-rate budget table ──────────────────────────
 * Optional budget builder shown on Time & Material projects. Level rows
 * (partner … analyst + freelancer) are priced rate × quantity; the rate
 * pre-fills from the rate card (/rules/guidelines/rate-card.md) and stays
 * editable. Flat rows cover Nebenkosten, Other, and Discount. The
 * resulting budget is summed live and flagged ("!") when it doesn't match
 * the project's top-line volume. Amounts are in the project's local
 * currency; the resulting total is also shown in CHF. */
const RATE_UNIT_OPTS = [{ v: 'day', l: 'days' }, { v: 'hour', l: 'hours' }, { v: 'week', l: 'weeks' }];
const UNIT_TO_DAYS = { day: 1, hour: 1 / 8, week: 5 };
const FALLBACK_LEVELS = [
  { key: 'partner', display: 'Partner' }, { key: 'director', display: 'Director' },
  { key: 'senior-manager', display: 'Senior Manager' }, { key: 'manager', display: 'Manager' },
  { key: 'consultant', display: 'Consultant' }, { key: 'analyst', display: 'Analyst' },
  { key: 'freelancer', display: 'Freelancer' },
];
const FLAT_KIND_LABEL = { nebenkosten: 'Nebenkosten', other: 'Other', discount: 'Discount' };

const numOf = v => { const n = Number(String(v ?? '').replace(/[^\d.-]/g, '')); return Number.isFinite(n) ? n : 0; };
const rowAmount = r => r.kind === 'level' ? numOf(r.rate) * numOf(r.quantity) : numOf(r.amount);

/** Pick the rate-card table whose currency matches the project, else the
 *  default org. Returns a level→dailyRate map. */
function rateMapFor(rateCard, currency) {
  if (!rateCard?.orgs?.length) return {};
  const byCcy = currency ? rateCard.orgs.find(o => o.currency === currency) : null;
  const org = byCcy || rateCard.orgs.find(o => o.org === rateCard.defaultOrg) || rateCard.orgs[0];
  const map = {};
  for (const row of org?.rows || []) map[row.level] = row.dailyRate;
  return map;
}

const ProjectRateTable = ({ project, initialLines = [], editing, onSaved }) => {
  const currency = project.currency || '';
  const [rateCard, setRateCard] = React.useState(null);
  const [rows, setRows] = React.useState(() => (initialLines || []).map(l => ({
    id: l.id || crypto.randomUUID(),
    kind: l.kind,
    level: l.level || 'consultant',
    label: l.label || '',
    unit: l.unit || 'day',
    rate: l.rate ?? '',
    quantity: l.quantity ?? '',
    amount: l.amount ?? '',
  })));
  const [error, setError] = React.useState(null);
  const savedSig = React.useRef('');

  React.useEffect(() => {
    let cancelled = false;
    getRateCard().then(rc => { if (!cancelled) setRateCard(rc); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const payload = React.useMemo(() => rows.map(r => ({
    id: r.id, kind: r.kind, level: r.kind === 'level' ? r.level : null,
    label: r.label || null, unit: r.kind === 'level' ? r.unit : null,
    rate: r.kind === 'level' ? numOf(r.rate) : null,
    quantity: r.kind === 'level' ? numOf(r.quantity) : null,
    amount: rowAmount(r),
  })), [rows]);
  const sig = JSON.stringify(payload);
  React.useEffect(() => {
    // Baseline the saved signature to the initial server state so a fresh
    // load isn't considered dirty.
    savedSig.current = JSON.stringify((initialLines || []).map(l => ({
      id: l.id, kind: l.kind, level: l.kind === 'level' ? (l.level || 'consultant') : null,
      label: l.label || null, unit: l.kind === 'level' ? (l.unit || 'day') : null,
      rate: l.kind === 'level' ? numOf(l.rate) : null,
      quantity: l.kind === 'level' ? numOf(l.quantity) : null,
      amount: l.kind === 'level' ? numOf(l.rate) * numOf(l.quantity) : numOf(l.amount),
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const dirty = sig !== savedSig.current;

  const save = async () => {
    setError(null);
    try {
      const saved = await putProjectRateLines(project.slug || project.id, payload);
      savedSig.current = sig;
      onSaved?.(saved);
    } catch (err) { setError(err.message); throw err; }
  };
  const { status: saveStatus } = useAutoSave({ dirty, save, enabled: editing, delay: 1200, signature: sig });

  const rateMap = rateMapFor(rateCard, currency);
  const setRow = (id, patch) => setRows(rs => rs.map(r => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = id => setRows(rs => rs.filter(r => r.id !== id));
  const addLevel = () => {
    const level = 'consultant';
    setRows(rs => [...rs, { id: crypto.randomUUID(), kind: 'level', level, label: '', unit: 'day', rate: rateMap[level] ?? '', quantity: '', amount: '' }]);
  };
  const addFlat = kind => setRows(rs => [...rs, { id: crypto.randomUUID(), kind, level: 'consultant', label: '', unit: 'day', rate: '', quantity: '', amount: '' }]);
  const onLevelChange = (id, level) => setRow(id, { level, rate: rateMap[level] ?? '' });

  // Totals.
  const levelRows = rows.filter(r => r.kind === 'level');
  const levelAmount = levelRows.reduce((s, r) => s + rowAmount(r), 0);
  const totalDays = levelRows.reduce((s, r) => s + numOf(r.quantity) * (UNIT_TO_DAYS[r.unit] || 1), 0);
  const neben = rows.filter(r => r.kind === 'nebenkosten').reduce((s, r) => s + numOf(r.amount), 0);
  const other = rows.filter(r => r.kind === 'other').reduce((s, r) => s + numOf(r.amount), 0);
  const discount = rows.filter(r => r.kind === 'discount').reduce((s, r) => s + Math.abs(numOf(r.amount)), 0);
  const budget = levelAmount + neben + other - discount;
  const blendedAdr = totalDays > 0 ? Math.round(levelAmount / totalDays) : null;

  const ratio = (project.volumeChf != null && project.volumeLocal) ? project.volumeChf / project.volumeLocal : (currency === 'CHF' ? 1 : null);
  const budgetChf = ratio != null ? Math.round(budget * ratio) : null;
  const topBudget = project.volumeLocal ?? null;
  // "!" when the resulting budget diverges from the top-line volume by
  // more than ~0.5% (and a top budget exists).
  const mismatch = topBudget != null && Math.abs(budget - topBudget) > Math.max(1, topBudget * 0.005);

  // Per-level subtotals ("sum per level").
  const perLevel = {};
  for (const r of levelRows) {
    const k = r.level || 'unknown';
    if (!perLevel[k]) perLevel[k] = { days: 0, amount: 0 };
    perLevel[k].days += numOf(r.quantity) * (UNIT_TO_DAYS[r.unit] || 1);
    perLevel[k].amount += rowAmount(r);
  }
  const levelDisplay = key => (rateCard?.levels || FALLBACK_LEVELS).find(l => l.key === key)?.display || key;

  if (!editing && rows.length === 0) {
    return <div style={{padding:'12px 14px',color:'var(--fg-muted)',fontSize:13}}>No daily-rate budget yet. Turn on Edit to build one.</div>;
  }

  const cellNum = { textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontSize: 12 };
  const inputStyle = { width: '100%', textAlign: 'right', padding: '3px 6px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 5, background: 'var(--bg)' };

  return (
    <div>
      <table className="tbl" style={{width:'100%'}}>
        <thead>
          <tr>
            <th>Role / line</th>
            <th style={{width:90,textAlign:'right'}}>Rate</th>
            <th style={{width:120}}>Unit</th>
            <th style={{width:90,textAlign:'right'}}>Qty</th>
            <th style={{width:130,textAlign:'right'}}>Amount ({currency || '—'})</th>
            {editing && <th style={{width:36}}/>}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id}>
              <td>
                {r.kind === 'level' ? (
                  editing ? (
                    <select className="select" value={r.level} onChange={e => onLevelChange(r.id, e.target.value)} style={{fontSize:12,padding:'3px 6px'}}>
                      {(rateCard?.levels || FALLBACK_LEVELS).map(l => <option key={l.key} value={l.key}>{l.display}</option>)}
                    </select>
                  ) : levelDisplay(r.level)
                ) : (
                  <span style={{fontWeight:600}}>{FLAT_KIND_LABEL[r.kind]}{r.label ? ` · ${r.label}` : ''}</span>
                )}
                {r.kind !== 'level' && editing && (
                  <input className="input" value={r.label} onChange={e => setRow(r.id, { label: e.target.value })} placeholder="note (optional)" maxLength={120} style={{marginTop:4,fontSize:11,padding:'3px 6px'}}/>
                )}
              </td>
              <td style={cellNum}>
                {r.kind === 'level'
                  ? (editing ? <input value={r.rate} onChange={e => setRow(r.id, { rate: e.target.value })} inputMode="numeric" style={inputStyle}/> : fmtAmount(numOf(r.rate)))
                  : '—'}
              </td>
              <td>
                {r.kind === 'level'
                  ? (editing ? (
                      <select className="select" value={r.unit} onChange={e => setRow(r.id, { unit: e.target.value })} style={{fontSize:12,padding:'3px 6px'}}>
                        {RATE_UNIT_OPTS.map(u => <option key={u.v} value={u.v}>{u.l}</option>)}
                      </select>
                    ) : (RATE_UNIT_OPTS.find(u => u.v === r.unit)?.l || r.unit))
                  : (r.kind === 'discount' ? <span style={{fontSize:11,color:'var(--fg-muted)'}}>subtracted</span> : '—')}
              </td>
              <td style={cellNum}>
                {r.kind === 'level'
                  ? (editing ? <input value={r.quantity} onChange={e => setRow(r.id, { quantity: e.target.value })} inputMode="numeric" style={inputStyle}/> : fmtAmount(numOf(r.quantity)))
                  : '—'}
              </td>
              <td style={cellNum}>
                {r.kind === 'level'
                  ? <strong>{fmtAmount(rowAmount(r))}</strong>
                  : (editing
                      ? <input value={r.amount} onChange={e => setRow(r.id, { amount: e.target.value })} inputMode="numeric" style={inputStyle}/>
                      : <strong>{r.kind === 'discount' ? '−' : ''}{fmtAmount(Math.abs(numOf(r.amount)))}</strong>)}
              </td>
              {editing && (
                <td style={{textAlign:'center'}}>
                  <button className="btn-ghost" onClick={() => removeRow(r.id)} title="Remove line" style={{padding:'2px 7px',fontSize:13,color:'#A20235'}}>×</button>
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={editing ? 6 : 5} style={{color:'var(--fg-muted)',fontSize:12,padding:'10px 12px'}}>No lines yet — add roles and cost lines below.</td></tr>
          )}
        </tbody>
      </table>

      {editing && (
        <div style={{display:'flex',flexWrap:'wrap',gap:8,marginTop:10}}>
          <button className="btn-ghost" onClick={addLevel} style={{padding:'4px 12px',fontSize:12,fontWeight:600}}>+ Add role</button>
          <button className="btn-ghost" onClick={() => addFlat('nebenkosten')} style={{padding:'4px 12px',fontSize:12}}>+ Nebenkosten</button>
          <button className="btn-ghost" onClick={() => addFlat('other')} style={{padding:'4px 12px',fontSize:12}}>+ Other</button>
          <button className="btn-ghost" onClick={() => addFlat('discount')} style={{padding:'4px 12px',fontSize:12}}>+ Discount</button>
          <div style={{marginLeft:'auto'}}><SaveStatus status={saveStatus} error={error} dirty={dirty}/></div>
        </div>
      )}

      {/* Per-level subtotals */}
      {Object.keys(perLevel).length > 0 && (
        <div style={{marginTop:14,display:'flex',flexWrap:'wrap',gap:8}}>
          {Object.entries(perLevel).map(([k, v]) => (
            <div key={k} style={{padding:'6px 10px',background:'var(--bg-alt)',borderRadius:7,fontSize:11.5}}>
              <span style={{fontWeight:600}}>{levelDisplay(k)}</span>
              <span style={{color:'var(--fg-muted)'}}> · {(+v.days.toFixed(2))} d · </span>
              <span style={{fontFamily:'JetBrains Mono, monospace'}}>{fmtAmount(Math.round(v.amount))} {currency}</span>
            </div>
          ))}
        </div>
      )}

      {/* Resulting budget + reconciliation against the top-line volume */}
      <div style={{marginTop:14,padding:'12px 14px',borderRadius:10,border:`1px solid ${mismatch ? '#E0A100' : 'var(--border)'}`,background:'var(--bg-alt)'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',gap:12,flexWrap:'wrap'}}>
          <div style={{fontSize:11,letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600}}>Resulting project budget</div>
          <div style={{fontFamily:'JetBrains Mono, monospace',fontSize:15,fontWeight:600}}>
            {fmtAmount(Math.round(budget))} {currency}
            {budgetChf != null && <span style={{color:'var(--fg-muted)',fontWeight:400,fontSize:12.5}}> · {fmtAmount(budgetChf)} CHF</span>}
          </div>
        </div>
        <div style={{marginTop:6,fontSize:11.5,color:'var(--fg-muted)'}}>
          {totalDays > 0 && <>Σ {(+totalDays.toFixed(2))} person-days{blendedAdr != null && <> · blended ADR {fmtAmount(blendedAdr)} {currency}/day</>} · </>}
          {topBudget != null
            ? <>top-line volume {fmtAmount(topBudget)} {currency}</>
            : <>no top-line volume set</>}
        </div>
        {mismatch && (
          <div style={{marginTop:8,display:'flex',gap:8,alignItems:'flex-start',color:'#8a6500',fontSize:12.5}}>
            <span style={{fontWeight:800,fontSize:15,lineHeight:1}}>!</span>
            <span>Resulting budget ({fmtAmount(Math.round(budget))} {currency}) doesn't match the project's top-line volume ({fmtAmount(topBudget)} {currency}) — off by {fmtAmount(Math.round(Math.abs(budget - topBudget)))} {currency}.</span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Project monthly commercial table ─────────────────────────────
 * One row per month the project spans. Revenue is prefilled (over time
 * by default, or by staffed capacity × the blended day rate) and stays
 * editable — a hand-edited cell is starred and survives re-prefills;
 * actual months are never overwritten by a prefill. Staffed days come
 * 1:1 from staffing (expand a row to see who). Planned ADR blends from
 * the project's rate table. Booked capacity + Realized ADR are reserved
 * for the upcoming hour-booking page and shown blank. Values are in the
 * project's local currency with a CHF column; the footer reconciles the
 * total against the project's top-line budget. */
const monthLabelOf = ym => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};
const ymStartMs = ym => { const [y, m] = ym.split('-').map(Number); return Date.UTC(y, m - 1, 1); };
const ymEndMs   = ym => { const [y, m] = ym.split('-').map(Number); return Date.UTC(y, m, 0); };
function workingDaysForYm(ym, startMs, endMs) {
  const [y, m] = ym.split('-').map(Number);
  const first = Date.UTC(y, m - 1, 1), next = Date.UTC(y, m, 1), day = 86400000;
  const floor = ms => { const d = new Date(ms); return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()); };
  const lo = startMs != null ? floor(startMs) : -Infinity;
  const hi = endMs != null ? floor(endMs) : Infinity;
  let c = 0;
  for (let t = first; t < next; t += day) {
    if (t < lo || t > hi) continue;
    const dow = new Date(t).getUTCDay();
    if (dow >= 1 && dow <= 5) c++;
  }
  return c;
}
function blendedAdrFromLines(lines) {
  let amt = 0, days = 0;
  for (const l of (lines || [])) {
    if (l.kind !== 'level') continue;
    const a = (l.rate != null && l.quantity != null) ? numOf(l.rate) * numOf(l.quantity) : numOf(l.amount);
    amt += a;
    days += numOf(l.quantity) * (UNIT_TO_DAYS[l.unit] || 1);
  }
  return days > 0 ? Math.round(amt / days) : null;
}

const ProjectMonthlyTable = ({ project, initialMonths = [], rateLines = [], fxToChf, editing, onSaved }) => {
  const currency = project.currency || '';
  const chfRate = (typeof fxToChf === 'number' && fxToChf > 0)
    ? fxToChf
    : ((project.volumeChf != null && project.volumeLocal) ? project.volumeChf / project.volumeLocal : (currency === 'CHF' ? 1 : null));
  const volumeLocal = project.volumeLocal != null ? Number(project.volumeLocal) : null;
  const startMs = project.startsAt ? new Date(project.startsAt).getTime() : null;
  const endMsRaw = project.endsAt ? new Date(project.endsAt).getTime() : null;

  const [rows, setRows] = React.useState(() => (initialMonths || []).map(m => ({
    ym: m.ym, status: m.status === 'actual' ? 'actual' : 'plan',
    revenue: m.revenueLocal != null ? String(m.revenueLocal) : '',
    manual: !!m.manual, staffedDays: Number(m.staffedDays || 0), people: m.people || [],
  })));
  const [expanded, setExpanded] = React.useState({});
  const [error, setError] = React.useState(null);
  const savedSig = React.useRef('');

  const blendedAdr = React.useMemo(() => blendedAdrFromLines(rateLines), [rateLines]);

  const ymList = rows.map(r => r.ym);
  const spanStart = startMs ?? (ymList.length ? ymStartMs(ymList[0]) : null);
  const spanEnd = endMsRaw ?? (ymList.length ? ymEndMs(ymList[ymList.length - 1]) : Date.now());

  // Derived "over time" hint (full volume spread by working days) — shown
  // faintly in empty cells and used for totals before anything is entered.
  const weightsAll = {}; let totAll = 0;
  for (const r of rows) { const w = workingDaysForYm(r.ym, spanStart, spanEnd); weightsAll[r.ym] = w; totAll += w; }
  const hint = {};
  for (const r of rows) hint[r.ym] = (volumeLocal != null && totAll > 0) ? volumeLocal * (weightsAll[r.ym] / totAll) : 0;
  const effective = r => (r.revenue !== '' ? numOf(r.revenue) : (hint[r.ym] || 0));

  const payload = rows.map(r => ({ ym: r.ym, revenueLocal: r.revenue === '' ? null : numOf(r.revenue), status: r.status, manual: r.manual }));
  const sig = JSON.stringify(payload);
  React.useEffect(() => {
    savedSig.current = JSON.stringify((initialMonths || []).map(m => ({
      ym: m.ym, revenueLocal: m.revenueLocal ?? null, status: m.status === 'actual' ? 'actual' : 'plan', manual: !!m.manual,
    })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const dirty = sig !== savedSig.current;

  const save = async () => {
    setError(null);
    try {
      const resp = await putProjectMonthly(project.slug || project.id, payload);
      savedSig.current = sig;
      onSaved?.(resp.months || []);
    } catch (err) { setError(err.message); throw err; }
  };
  const { status: saveStatus } = useAutoSave({ dirty, save, enabled: editing, delay: 1200, signature: sig });

  const setRow = (ym, patch) => setRows(rs => rs.map(r => (r.ym === ym ? { ...r, ...patch } : r)));
  const prefillOverTime = () => {
    const actualSum = rows.filter(r => r.status === 'actual').reduce((s, r) => s + effective(r), 0);
    const remaining = (volumeLocal || 0) - actualSum;
    const planRows = rows.filter(r => r.status !== 'actual');
    const w = {}; let tot = 0;
    for (const r of planRows) { const x = workingDaysForYm(r.ym, spanStart, spanEnd); w[r.ym] = x; tot += x; }
    setRows(rs => rs.map(r => {
      if (r.status === 'actual') return r;
      const val = tot > 0 ? remaining * (w[r.ym] / tot) : (planRows.length ? remaining / planRows.length : 0);
      return { ...r, revenue: String(Math.max(0, Math.round(val))), manual: false };
    }));
  };
  const prefillByStaffed = () => {
    setRows(rs => rs.map(r => {
      if (r.status === 'actual') return r;
      const val = blendedAdr != null ? r.staffedDays * blendedAdr : 0;
      return { ...r, revenue: String(Math.round(val)), manual: false };
    }));
  };
  const clearPlan = () => setRows(rs => rs.map(r => (r.status === 'actual' ? r : { ...r, revenue: '', manual: false })));

  const totalRevenue = rows.reduce((s, r) => s + effective(r), 0);
  const totalChf = chfRate != null ? Math.round(totalRevenue * chfRate) : null;
  const totalStaffed = rows.reduce((s, r) => s + r.staffedDays, 0);
  const delta = volumeLocal != null ? totalRevenue - volumeLocal : null;
  const mismatch = delta != null && Math.abs(delta) > Math.max(1, (volumeLocal || 0) * 0.005);

  if (rows.length === 0) {
    return <div style={{padding:'12px 14px',color:'var(--fg-muted)',fontSize:13}}>Set the project's start and end dates to build the monthly table.</div>;
  }

  const numCell = { textAlign: 'right', fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5 };
  const revInput = { width: '100%', textAlign: 'right', padding: '3px 6px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 5, background: 'var(--bg)' };

  return (
    <div>
      <div style={{overflowX:'auto'}}>
        <table className="tbl tbl--financial" style={{width:'100%'}}>
          <thead>
            <tr>
              <th>Month</th>
              <th style={{width:96}}>Status</th>
              <th style={{width:150,textAlign:'right'}}>Revenue ({currency || '—'})</th>
              <th style={{width:110,textAlign:'right'}} title="Staffed capacity in days, 1:1 from staffing. Click to see who.">Staffed days</th>
              <th style={{width:120,textAlign:'right'}} title="Planned ADR — the blended day rate from the project's rate table.">Planned ADR</th>
              <th style={{width:96,textAlign:'right'}} title="Booked capacity — pending the hour-booking page.">Booked</th>
              <th style={{width:110,textAlign:'right'}} title="Realized ADR — pending the hour-booking page.">Realized ADR</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const eff = effective(r);
              const chf = chfRate != null ? Math.round(eff * chfRate) : null;
              const isHint = r.revenue === '';
              return (
                <React.Fragment key={r.ym}>
                  <tr style={{borderLeft:`3px solid ${r.status === 'actual' ? 'var(--orange)' : 'transparent'}`}}>
                    <td style={{fontWeight:500}}>{monthLabelOf(r.ym)}</td>
                    <td>
                      {editing ? (
                        <select className="select" value={r.status} onChange={e => setRow(r.ym, { status: e.target.value })} style={{fontSize:11.5,padding:'2px 4px'}}>
                          <option value="plan">plan</option>
                          <option value="actual">actual</option>
                        </select>
                      ) : <span style={{fontSize:11.5,color:r.status === 'actual' ? 'var(--orange)' : 'var(--fg-muted)',fontWeight:600}}>{r.status}</span>}
                    </td>
                    <td style={numCell}>
                      {editing ? (
                        <input value={r.revenue} placeholder={fmtAmount(Math.round(hint[r.ym] || 0))} onChange={e => setRow(r.ym, { revenue: e.target.value.replace(/[^\d]/g, ''), manual: true })} inputMode="numeric" style={revInput} title={isHint ? 'Derived (prefilled over time). Type to override.' : (r.manual ? 'Manually edited' : 'Prefilled')}/>
                      ) : (
                        <span style={{color:isHint ? 'var(--fg-muted)' : 'var(--fg)'}}>{r.manual && !isHint ? '★ ' : ''}{fmtAmount(Math.round(eff))}</span>
                      )}
                      {chf != null && <div style={{fontSize:10,color:'var(--fg-muted)'}}>{fmtAmount(chf)} CHF</div>}
                    </td>
                    <td style={{...numCell, cursor: r.people.length ? 'pointer' : 'default'}} onClick={() => r.people.length && setExpanded(x => ({ ...x, [r.ym]: !x[r.ym] }))}>
                      {r.staffedDays ? (+r.staffedDays.toFixed(2)) : '—'}
                      {r.people.length > 0 && <span style={{color:'var(--fg-muted)',marginLeft:4}}>{expanded[r.ym] ? '▾' : '▸'}</span>}
                    </td>
                    <td style={numCell}>{blendedAdr != null ? fmtAmount(blendedAdr) : '—'}</td>
                    <td style={{...numCell, color:'var(--fg-muted)'}}>—</td>
                    <td style={{...numCell, color:'var(--fg-muted)'}}>—</td>
                  </tr>
                  {expanded[r.ym] && r.people.length > 0 && (
                    <tr>
                      <td colSpan={7} style={{background:'var(--bg-alt)',padding:'8px 14px'}}>
                        <div style={{display:'flex',flexWrap:'wrap',gap:10,fontSize:11.5}}>
                          {r.people.map(pp => (
                            <span key={pp.userId}><EntityLink type="person" handle={pp.userId}>{pp.name}</EntityLink> <span style={{color:'var(--fg-muted)',fontFamily:'JetBrains Mono, monospace'}}>{(+pp.days.toFixed(2))} d</span></span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{borderTop:'2px solid var(--border)',fontWeight:600}}>
              <td>Total</td>
              <td/>
              <td style={numCell}>
                {fmtAmount(Math.round(totalRevenue))} {currency}
                {totalChf != null && <div style={{fontSize:10,color:'var(--fg-muted)',fontWeight:400}}>{fmtAmount(totalChf)} CHF</div>}
              </td>
              <td style={numCell}>{totalStaffed ? (+totalStaffed.toFixed(2)) : '—'}</td>
              <td colSpan={3} style={{...numCell, fontWeight:400, color:'var(--fg-muted)'}}>
                {delta != null ? <>Δ to budget {delta >= 0 ? '+' : '−'}{fmtAmount(Math.round(Math.abs(delta)))} {currency}</> : 'no top-line budget'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {editing && (
        <div style={{display:'flex',flexWrap:'wrap',gap:8,marginTop:10,alignItems:'center'}}>
          <button className="btn-ghost" onClick={prefillOverTime} style={{padding:'4px 12px',fontSize:12,fontWeight:600}} title="Spread the remaining budget across plan months by working days. Actual months are left untouched.">Prefill over time</button>
          <button className="btn-ghost" onClick={prefillByStaffed} disabled={blendedAdr == null} style={{padding:'4px 12px',fontSize:12}} title={blendedAdr == null ? 'Add rate-table lines first to get a blended day rate.' : 'Plan months = staffed days × blended day rate. Actual months are left untouched.'}>Prefill by staffed capacity</button>
          <button className="btn-ghost" onClick={clearPlan} style={{padding:'4px 12px',fontSize:12}}>Clear plan</button>
          <div style={{marginLeft:'auto'}}><SaveStatus status={saveStatus} error={error} dirty={dirty}/></div>
        </div>
      )}

      {mismatch && (
        <div style={{marginTop:10,display:'flex',gap:8,alignItems:'flex-start',color:'#8a6500',fontSize:12.5}}>
          <span style={{fontWeight:800,fontSize:15,lineHeight:1}}>!</span>
          <span>Monthly revenue totals {fmtAmount(Math.round(totalRevenue))} {currency}, {delta >= 0 ? 'over' : 'under'} the project's top-line volume ({fmtAmount(volumeLocal)} {currency}) by {fmtAmount(Math.round(Math.abs(delta)))} {currency}. Saving is still allowed.</span>
        </div>
      )}
    </div>
  );
};

/* Admin-only "Danger zone" delete. Shared by every entity detail page
 * (project / client / organization / service / industry / person).
 *
 * Two modes:
 *   • Cascade mode (pass `kind` + `entityId`): on click we fetch a delete
 *     preview and show the 3-way prompt — for each first-class dependent
 *     (a client's projects, a project's staffing, …) the admin chooses
 *     Delete / Keep-unlinked / Reassign. Owned scaffolding (memberships,
 *     tags, news) is always cleaned up and shown as a count.
 *   • Legacy mode (pass `onDelete`): a plain confirm + the supplied API
 *     call. Used by the person page, which isn't part of the entity graph.
 * `onDeleted` runs on success (typically onBack, to leave the gone view). */
const DangerZoneSection = ({ noun, name, kind, entityId, onDelete, onDeleted }) => {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [phase, setPhase] = React.useState('idle');   // idle | loading | review
  const [preview, setPreview] = React.useState(null);
  const [choices, setChoices] = React.useState({});    // key → { mode, targetId }

  const runLegacy = async () => {
    if (!window.confirm(`Delete ${noun}${name ? ` "${name}"` : ''}? This permanently removes it and can't be undone.`)) return;
    setBusy(true); setErr(null);
    try { await onDelete(); onDeleted?.(); }
    catch (e) { setErr(e.message || String(e)); setBusy(false); }
  };

  const openReview = async () => {
    setErr(null); setPhase('loading');
    try {
      const p = await previewEntityDelete(kind, entityId);
      const init = {};
      for (const d of p.decisions) init[d.key] = { mode: d.defaultMode, targetId: '' };
      setChoices(init); setPreview(p); setPhase('review');
    } catch (e) { setErr(e.message || String(e)); setPhase('idle'); }
  };

  const setChoice = (key, patch) =>
    setChoices(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const confirmDelete = async () => {
    for (const d of preview.decisions) {
      if (d.count > 0 && choices[d.key]?.mode === 'reassign' && !choices[d.key]?.targetId) {
        setErr(`Choose which ${noun} to move "${d.label}" to.`); return;
      }
    }
    setBusy(true); setErr(null);
    try {
      const resolutions = {};
      for (const d of preview.decisions) {
        const c = choices[d.key] || { mode: d.defaultMode };
        resolutions[d.key] = c.mode === 'reassign'
          ? { mode: 'reassign', targetId: c.targetId }
          : { mode: c.mode };
      }
      await executeEntityDelete(kind, entityId, resolutions);
      onDeleted?.();
    } catch (e) { setErr(e.message || String(e)); setBusy(false); }
  };

  const errBox = err && (
    <div style={{marginTop:8,padding:8,background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12}}>{err}</div>
  );

  // Review panel (cascade mode, after preview loaded).
  if (kind && phase === 'review' && preview) {
    const live = preview.decisions.filter(d => d.count > 0);
    const radioName = key => `cascade-${entityId}-${key}`;
    const opt = (d, mode, label) => (
      <label key={mode} style={{display:'flex',alignItems:'center',gap:6,fontSize:12.5,fontWeight:400,letterSpacing:0,textTransform:'none'}}>
        <input
          type="radio"
          name={radioName(d.key)}
          checked={(choices[d.key]?.mode || d.defaultMode) === mode}
          onChange={() => setChoice(d.key, { mode })}
          disabled={busy}
        />
        {label}
      </label>
    );
    return (
      <EntitySection title="Danger zone">
        <div style={{padding:'4px 0',display:'flex',flexDirection:'column',gap:14}}>
          <div style={{fontSize:13}}>
            Deleting {noun} <strong>{preview.name}</strong>. Choose what happens to everything linked to it.
          </div>
          {live.map(d => (
            <div key={d.key} style={{border:'1px solid var(--border)',borderRadius:8,padding:'10px 12px'}}>
              <div style={{fontSize:12.5,fontWeight:600,marginBottom:6}}>
                {d.label} <span style={{color:'var(--fg-muted)',fontWeight:400}}>({d.count})</span>
              </div>
              <div style={{display:'flex',flexDirection:'column',gap:4}}>
                {opt(d, 'delete', d.cascades ? 'Delete them — and everything under them' : 'Delete them')}
                {opt(d, 'keep', `Keep them, but unlink (remember "${preview.name}" as a static label)`)}
                {opt(d, 'reassign', `Move them to another ${noun}`)}
              </div>
              {(choices[d.key]?.mode || d.defaultMode) === 'reassign' && (
                <select
                  value={choices[d.key]?.targetId || ''}
                  onChange={e => setChoice(d.key, { targetId: e.target.value })}
                  disabled={busy}
                  style={{marginTop:6,padding:'4px 6px',fontSize:12,border:'1px solid var(--border)',borderRadius:6,background:'var(--bg)',maxWidth:320}}
                >
                  <option value="">choose a {noun}…</option>
                  {preview.reassignCandidates.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
            </div>
          ))}
          {preview.ownedCount > 0 && (
            <div style={{fontSize:12,color:'var(--fg-muted)'}}>
              Also removes {preview.ownedCount} linked record{preview.ownedCount === 1 ? '' : 's'} (memberships, tags, news, subscriptions).
            </div>
          )}
          <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
            <button
              type="button" className="btn-ghost" disabled={busy}
              onClick={confirmDelete}
              style={{color:'var(--support-red)',borderColor:'var(--support-red)'}}
            >
              {busy ? 'Deleting…' : `Delete ${noun} permanently`}
            </button>
            <button type="button" className="btn-ghost" disabled={busy} onClick={() => { setPhase('idle'); setErr(null); }}>
              Cancel
            </button>
          </div>
          {errBox}
        </div>
      </EntitySection>
    );
  }

  // Idle trigger (both modes).
  return (
    <EntitySection title="Danger zone">
      <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',padding:'4px 0'}}>
        <button
          type="button"
          className="btn-ghost"
          disabled={busy || phase === 'loading'}
          onClick={kind ? openReview : runLegacy}
          style={{color:'var(--support-red)',borderColor:'var(--support-red)'}}
        >
          {phase === 'loading' ? 'Checking…' : busy ? 'Deleting…' : `Delete ${noun}`}
        </button>
        <span style={{fontSize:12,color:'var(--fg-muted)'}}>
          {kind
            ? `Removes this ${noun}; you'll choose what happens to anything linked to it.`
            : `Permanently removes this ${noun}. This can't be undone.`}
        </span>
      </div>
      {errBox}
    </EntitySection>
  );
};

/* Per-item leads — who leads this client / service / industry /
 * organization. Self-contained: fetches its own lead list (and, for
 * admins, the people directory for the picker). Mirrors the project
 * lead-partners section. Mutations are admin-only (server-enforced). */
const ItemLeadsSection = ({ itemType, itemId, isAdmin, title = 'Leads', noun = 'lead', emptyLabel }) => {
  const [leads, setLeads] = React.useState(null);
  const [users, setUsers] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  // Multi-add panel: an item can have many leads, so the picker lets you
  // tick several people and add them in one go.
  const [adding, setAdding] = React.useState(false);
  const [picked, setPicked] = React.useState(() => new Set());
  const [query, setQuery] = React.useState('');

  React.useEffect(() => {
    if (!itemId) return;
    let live = true;
    listItemLeads(itemType, itemId).then(l => { if (live) setLeads(l); }).catch(e => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [itemType, itemId]);

  React.useEffect(() => {
    if (!isAdmin) return;
    listPeople().then(setUsers).catch(() => setUsers([]));
  }, [isAdmin]);

  const list = leads || [];
  const existingIds = new Set(list.map(p => p.userId));

  const addMany = async ids => {
    if (!ids.length) return;
    setBusy(true); setError(null);
    try {
      let latest = list;
      for (const uid of ids) latest = await addItemLead(itemType, itemId, uid);
      setLeads(latest);
      setPicked(new Set());
      setQuery('');
      setAdding(false);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const remove = async userId => {
    setBusy(true); setError(null);
    try { setLeads(await removeItemLead(itemType, itemId, userId)); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const candidates = (users || []).filter(u => !existingIds.has(u._id));
  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? candidates.filter(u => (u.name || '').toLowerCase().includes(needle) || (u.title || '').toLowerCase().includes(needle))
    : candidates;
  const toggle = id => setPicked(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (
    <EntitySection
      title={`${title} · ${list.length}`}
      action={isAdmin
        ? <button
            type="button"
            className="btn-ghost"
            style={{padding:'3px 10px',fontSize:11.5,fontWeight:400,letterSpacing:0,textTransform:'none'}}
            onClick={() => { setAdding(a => !a); setPicked(new Set()); setQuery(''); }}
          >
            {adding ? 'Close' : `+ Add ${noun}s`}
          </button>
        : null}
    >
      {error && <div style={{padding:'8px 12px',color:'#A20235',fontSize:12.5}}>{error}</div>}
      {isAdmin && adding && (
        <div style={{padding:12,marginBottom:12,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8}}>
          {users === null ? (
            <div style={{fontSize:12.5,color:'var(--fg-muted)'}}>loading directory…</div>
          ) : (
            <>
              <input
                className="input"
                placeholder="Search people…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                style={{marginBottom:8}}
              />
              <div style={{maxHeight:220,overflowY:'auto',display:'grid',gap:2}}>
                {filtered.length === 0 ? (
                  <div style={{fontSize:12.5,color:'var(--fg-muted)',padding:'6px 0'}}>
                    {candidates.length === 0 ? 'Everyone is already a lead.' : 'No matches.'}
                  </div>
                ) : filtered.map(u => (
                  <label key={u._id} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 4px',cursor:'pointer',borderRadius:6}}>
                    <input type="checkbox" checked={picked.has(u._id)} onChange={() => toggle(u._id)} disabled={busy}/>
                    <span style={{fontSize:13}}>{u.name}</span>
                    {u.title && <span style={{fontSize:11.5,color:'var(--fg-muted)'}}>· {u.title}</span>}
                  </label>
                ))}
              </div>
              <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:10}}>
                <button type="button" className="btn-ghost" style={{padding:'4px 12px',fontSize:12}} disabled={busy} onClick={() => { setAdding(false); setPicked(new Set()); setQuery(''); }}>Cancel</button>
                <button type="button" className="btn-orange" style={{padding:'4px 12px',fontSize:12}} disabled={busy || picked.size === 0} onClick={() => addMany([...picked])}>
                  {busy ? 'Adding…' : `Add ${picked.size || ''} ${noun}${picked.size === 1 ? '' : 's'}`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {leads === null ? (
        <div style={{padding:12,color:'var(--fg-muted)',fontSize:13}}>Loading…</div>
      ) : list.length === 0 ? (
        <div style={{padding:12,color:'var(--fg-muted)',fontSize:13}}>
          {emptyLabel || `No ${noun}s yet.${isAdmin ? ' Use the picker to name who leads this.' : ''}`}
        </div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:10}}>
          {list.map(p => (
            <PersonCard
              key={p.userId}
              userId={p.userId}
              name={p.name}
              photoUrl={p.photoUrl}
              title={p.title}
              onRemove={isAdmin ? () => remove(p.userId) : undefined}
            />
          ))}
        </div>
      )}
    </EntitySection>
  );
};

function fmtBytes(n) {
  if (!Number.isFinite(n)) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

const STATUS_COLOR = {
  active: 'var(--support-green)',
  paused: 'var(--fg-muted)',
  done:   '#1F6FEB',
};
const STATUS_BG = {
  active: '#DCF1E2',
  paused: 'var(--bg-card)',
  done:   '#DCE7FF',
};

/* ─── Clients ──────────────────────────────────────────────────── */

/* Phase 3: list-with-detail.
 *   subPath ''      → list view (table)
 *   subPath '<slug>'→ <ClientDetailView/> with EntityShell + News
 * The subPath is the client slug (clean URL). Click on a row sets
 * subPath via onNavigate so the URL deep-links.
 *
 * Dispatcher pattern (no hooks in the exported component) so
 * navigating list↔detail can't change hook counts. */
export const ClientsView = ({ currentUser, subPath, onNavigate } = {}) => (
  subPath
    ? <ClientDetailView slug={subPath} currentUser={currentUser} onBack={() => onNavigate?.('')}/>
    : <ClientsList currentUser={currentUser} onNavigate={onNavigate}/>
);

const ClientsList = ({ currentUser, onNavigate }) => {
  const [tax, setTax] = React.useState(null);
  const [projects, setProjects] = React.useState([]);
  const [error, setError] = React.useState(null);
  const [q, setQ] = React.useState('');
  const isAdmin = currentUser?.role === 'admin';

  const reload = React.useCallback(() => {
    listTaxonomies()
      .then(t => setTax(t))
      .catch(err => setError(err.message));
    listProjects().then(setProjects).catch(() => setProjects([]));
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  const projectsByClient = React.useMemo(() => {
    const m = {};
    for (const p of projects) (m[p.clientId] = m[p.clientId] || []).push(p);
    return m;
  }, [projects]);

  const industriesById = React.useMemo(
    () => Object.fromEntries(((tax?.industries) || []).map(i => [i._id, i])),
    [tax],
  );

  // Compute trailing-12-months revenue per client from local project
  // data. Lightweight — same proportional-split logic as the chart.
  const ttmByClient = React.useMemo(() => computeTtmByClient(projects), [projects]);

  const needle = q.trim().toLowerCase();
  const clients = tax?.clients || [];
  const matched = !needle ? clients : clients.filter(c =>
    (c.name || '').toLowerCase().includes(needle) ||
    (industriesById[c.industryId]?.name || '').toLowerCase().includes(needle),
  );
  // Highest TTM revenue on top; ties and zero-revenue clients fall back
  // to name order.
  const filtered = [...matched].sort((a, b) =>
    (ttmByClient[b._id] || 0) - (ttmByClient[a._id] || 0) || (a.name || '').localeCompare(b.name || ''),
  );

  const openClient = c => onNavigate?.(c.slug || c._id);

  return (
    <div className="page">
      <div className="page__head">
        <h1>Clients</h1>
        <div className="lede">Companies the firm works with. Click a row to open the full dashboard — projects, revenue, people, plus a daily news ticker from the Client News Watcher.</div>
      </div>

      <div style={{display:'flex',gap:10,marginBottom:14}}>
        <input
          className="input"
          placeholder="Search clients…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{maxWidth:380}}
        />
        <div style={{flex:1}}/>
        {tax && <span style={{fontSize:12,color:'var(--fg-muted)',alignSelf:'center'}}>{filtered.length} of {clients.length}</span>}
        {isAdmin && <AddEntityButton kind="clients" onCreated={reload}/>}
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}
      {!tax && !error && <Loading>Loading clients…</Loading>}
      {tax && filtered.length === 0 && (
        <Empty>{needle ? 'No clients match.' : isAdmin ? 'No clients yet. Use the Add client button above.' : 'No clients yet. Ask an admin to add one.'}</Empty>
      )}

      {filtered.length > 0 && (
        <div className="tbl-card">
          <table className="tbl" style={{width:'100%'}}>
            <thead>
              <tr>
                <th style={{width:48}}></th>
                <th>Name</th>
                <th style={{width:160}}>Industry</th>
                <th style={{width:90,textAlign:'right'}}># projects</th>
                <th style={{width:140,textAlign:'right'}}>TTM revenue</th>
                <th style={{width:38}}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const cps = projectsByClient[c._id] || [];
                const ttm = ttmByClient[c._id] || 0;
                return (
                  <tr key={c._id} onClick={() => openClient(c)} style={{cursor:'pointer'}}>
                    <td><ClientLogo client={c} size={28}/></td>
                    <td>
                      <div style={{fontWeight:500}}>{c.name}</div>
                      {c.description && (
                        <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:480}}>
                          {stripMarkdownForPreview(c.description)}
                        </div>
                      )}
                    </td>
                    <td style={{color:'var(--fg-muted)',fontSize:12.5}}>{industriesById[c.industryId]?.name || '—'}</td>
                    <td style={{textAlign:'right',color:'var(--fg-muted)',fontSize:12.5}}>{cps.length}</td>
                    <td style={{textAlign:'right',fontFamily:'JetBrains Mono',fontSize:12,color: ttm > 0 ? 'var(--fg)' : 'var(--fg-muted)'}}>
                      {ttm > 0 ? `${fmtAmount(ttm)} CHF` : '—'}
                    </td>
                    <td style={{textAlign:'right',color:'var(--fg-muted)',fontSize:14}}>›</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* Trailing-12-months revenue per client. Uses the same proportional-
 * split policy as MonthlyRevenueStack — a project running Jan→Dec at
 * €120k contributes €10k to every month, of which the last 12 land
 * here. Cheap to compute on the client; no extra API call needed. */
/* The set of the last 12 "YYYY-MM" month keys, current month included. */
function ttmMonthsSet() {
  const now = new Date();
  const keys = new Set();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    keys.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return keys;
}

/* Trailing-twelve-month CHF for a project from its authoritative monthly
 * series (stored revenue, or the working-day spread the API derives).
 * Returns null when the series isn't present so callers fall back to the
 * legacy date-overlap estimate. */
function monthlyTtm(p, monthsSet) {
  if (!p || !p.monthlyRevenueChf || typeof p.monthlyRevenueChf !== 'object') return null;
  let t = 0;
  for (const [ym, v] of Object.entries(p.monthlyRevenueChf)) if (monthsSet.has(ym)) t += Number(v || 0);
  return t;
}

function computeTtmByClient(projects) {
  const now = Date.now();
  const windowStart = new Date(now);
  windowStart.setMonth(windowStart.getMonth() - 12);
  const monthsSet = ttmMonthsSet();
  const out = {};
  for (const p of (projects || [])) {
    const m = monthlyTtm(p, monthsSet);
    if (m != null) { if (m) out[p.clientId] = (out[p.clientId] || 0) + m; continue; }
    const vol = Number(p.volumeChf || 0);
    if (!Number.isFinite(vol) || vol <= 0) continue;
    const s = p.startsAt ? new Date(p.startsAt).getTime() : null;
    const e = p.endsAt   ? new Date(p.endsAt).getTime()   : null;
    const projStart = s ?? e ?? now;
    const projEnd   = Math.min(e ?? now, now);
    const effStart  = Math.min(projStart, projEnd);
    if (projEnd <= effStart) continue;
    const totalMs   = projEnd - effStart || 1;
    const overlap   = Math.max(0, Math.min(projEnd, now) - Math.max(effStart, windowStart.getTime()));
    if (overlap <= 0) continue;
    const share     = overlap / totalMs;
    out[p.clientId] = (out[p.clientId] || 0) + vol * share;
  }
  return out;
}

export const ClientLogo = ({ client, size = 40 }) => {
  const url = client?.logoUrl;
  const initials = (client?.name || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
  if (url) {
    return (
      <img
        src={url}
        alt={client?.name || 'client'}
        style={{width:size,height:size,borderRadius:6,objectFit:'contain',background:'var(--bg-card)',border:'1px solid var(--border)'}}
      />
    );
  }
  return (
    <div style={{
      width:size,height:size,borderRadius:6,
      display:'flex',alignItems:'center',justifyContent:'center',
      background:'var(--bg-card)',color:'var(--fg-muted)',
      fontWeight:700,fontSize: Math.max(10, size * 0.4),letterSpacing:'0.04em',
      border:'1px solid var(--border)',
    }}>{initials}</div>
  );
};

/* The big client logo at the top of the detail page. In edit mode it
 * grows an "Add / Change logo" button (and "Clear" when one is set)
 * that hits the client-scoped /api/clients/:id/logo endpoint — open to
 * the engagement lead as well as admins. `onChanged` receives the
 * updated public client so the page can refresh without a refetch. */
const ClientLogoEditable = ({ client, onChanged, size = 76 }) => {
  const { canEdit, editing } = useEditMode();
  const fileRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handle = client.slug || client._id || client.id;
  const showControls = canEdit && editing;

  const upload = async file => {
    setBusy(true); setError(null);
    try {
      const updated = await putClientLogo(handle, file);
      onChanged?.(updated);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!confirm(`Remove the logo for "${client.name}"?`)) return;
    setBusy(true); setError(null);
    try {
      const updated = await clearClientLogo(handle);
      onChanged?.(updated);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6}}>
      <ClientLogo client={client} size={size}/>
      {showControls && (
        <>
          <div style={{display:'flex',gap:6,flexWrap:'wrap',justifyContent:'center'}}>
            <button
              type="button"
              className="btn-ghost"
              style={{padding:'2px 9px',fontSize:11}}
              disabled={busy}
              onClick={() => fileRef.current?.click()}
              title={client.logoUrl ? 'Replace the company logo' : 'Upload a company logo'}
            >
              {busy ? '…' : (client.logoUrl ? 'Change logo' : 'Add logo')}
            </button>
            {client.logoUrl && !busy && (
              <button
                type="button"
                className="btn-ghost"
                style={{padding:'2px 9px',fontSize:11,color:'var(--support-red)'}}
                onClick={remove}
              >
                Clear
              </button>
            )}
          </div>
          {error && (
            <div style={{fontSize:10.5,color:'var(--support-red)',maxWidth:140,textAlign:'center'}}>{error}</div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
            style={{display:'none'}}
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) upload(f);
              e.target.value = '';
            }}
          />
        </>
      )}
    </div>
  );
};

/* Full-page Client detail (Phase 3). Renders via EntityShell so it
 * shares the right-rail news ticker pattern with Services /
 * Industries detail views. Fetches /api/clients/:slug for everything;
 * computes the monthly revenue chart locally from project data.
 *
 * `slug` is the subPath from App routing — `#clients/<slug>`. We
 * also accept a raw client id as a fallback so admins clicking
 * through from search history still work.
 */
/* Small labelled badge for the client Team groups. */
const ClientTeamBadge = ({ label, bg, fg }) => (
  <span style={{display:'inline-block',marginTop:4,fontSize:9.5,padding:'1px 6px',borderRadius:4,background:bg,color:fg,textTransform:'uppercase',letterSpacing:'0.06em',fontWeight:600}}>
    {label}
  </span>
);

/* The client Team container: the account's lead partner(s), everyone
 * currently staffed on the account's projects, and everyone who worked
 * on them before but isn't staffed now — all in one place. Lead partners
 * are editable (admin); current / past are derived from staffing and
 * read-only. */
const ClientTeamSection = ({ clientId, isAdmin, adminEditing, currentPeople = [], pastPeople = [] }) => {
  const [leads, setLeads] = React.useState(null);
  const [users, setUsers] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [picked, setPicked] = React.useState(() => new Set());
  const [query, setQuery] = React.useState('');
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!clientId) return;
    let live = true;
    listItemLeads('client', clientId).then(l => { if (live) setLeads(l); }).catch(e => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [clientId]);

  React.useEffect(() => {
    if (!adminEditing) return;
    listPeople().then(setUsers).catch(() => setUsers([]));
  }, [adminEditing]);

  const leadList = leads || [];
  const leadIds = new Set(leadList.map(p => p.userId));
  // A lead partner who is also staffed shouldn't appear twice — show
  // them only under "Lead partner".
  const current = currentPeople.filter(p => !leadIds.has(p.userId));
  const past = pastPeople.filter(p => !leadIds.has(p.userId));
  const total = leadList.length + current.length + past.length;

  const existingIds = leadIds;
  const candidates = (users || []).filter(u => !existingIds.has(u._id));
  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? candidates.filter(u => (u.name || '').toLowerCase().includes(needle) || (u.title || '').toLowerCase().includes(needle))
    : candidates;
  const toggle = id => setPicked(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const addMany = async ids => {
    if (!ids.length) return;
    setBusy(true); setError(null);
    try {
      let latest = leadList;
      for (const uid of ids) latest = await addItemLead('client', clientId, uid);
      setLeads(latest); setPicked(new Set()); setQuery(''); setAdding(false);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const remove = async userId => {
    setBusy(true); setError(null);
    try { setLeads(await removeItemLead('client', clientId, userId)); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const group = (label, people, badge, removable) => (
    people.length === 0 ? null : (
      <div style={{marginBottom:16}}>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600,marginBottom:8}}>
          {label} · {people.length}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:10}}>
          {people.map(p => (
            <PersonCard
              key={p.userId}
              userId={p.userId}
              name={p.name}
              photoUrl={p.photoUrl}
              title={p.title}
              badge={badge}
              onRemove={removable && adminEditing ? () => remove(p.userId) : undefined}
            />
          ))}
        </div>
      </div>
    )
  );

  return (
    <EntitySection
      title={`Team · ${total}`}
      action={adminEditing
        ? <button
            type="button"
            className="btn-ghost"
            style={{padding:'3px 10px',fontSize:11.5,fontWeight:400,letterSpacing:0,textTransform:'none'}}
            onClick={() => { setAdding(a => !a); setPicked(new Set()); setQuery(''); }}
          >
            {adding ? 'Close' : '+ Add lead partner'}
          </button>
        : null}
    >
      {error && <div style={{padding:'8px 12px',color:'#A20235',fontSize:12.5}}>{error}</div>}
      {adminEditing && adding && (
        <div style={{padding:'4px 0 12px',marginBottom:12,borderBottom:'1px solid var(--border)'}}>
          {users === null ? (
            <div style={{fontSize:12.5,color:'var(--fg-muted)'}}>loading directory…</div>
          ) : (
            <>
              <input className="input" placeholder="Search people…" value={query} onChange={e => setQuery(e.target.value)} style={{marginBottom:8}}/>
              <div style={{maxHeight:220,overflowY:'auto',display:'grid',gap:2}}>
                {filtered.length === 0 ? (
                  <div style={{fontSize:12.5,color:'var(--fg-muted)',padding:'6px 0'}}>
                    {candidates.length === 0 ? 'Everyone is already a lead partner.' : 'No matches.'}
                  </div>
                ) : filtered.map(u => (
                  <label key={u._id} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 4px',cursor:'pointer',borderRadius:6}}>
                    <input type="checkbox" checked={picked.has(u._id)} onChange={() => toggle(u._id)} disabled={busy}/>
                    <span style={{fontSize:13}}>{u.name}</span>
                    {u.title && <span style={{fontSize:11.5,color:'var(--fg-muted)'}}>· {u.title}</span>}
                  </label>
                ))}
              </div>
              <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:10}}>
                <button type="button" className="btn-ghost" style={{padding:'4px 12px',fontSize:12}} disabled={busy} onClick={() => { setAdding(false); setPicked(new Set()); setQuery(''); }}>Cancel</button>
                <button type="button" className="btn-orange" style={{padding:'4px 12px',fontSize:12}} disabled={busy || picked.size === 0} onClick={() => addMany([...picked])}>
                  {busy ? 'Adding…' : `Add ${picked.size || ''} lead partner${picked.size === 1 ? '' : 's'}`}
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {total === 0 ? (
        <div style={{padding:'12px',color:'var(--fg-muted)',fontSize:13}}>
          No one on this account yet. {isAdmin ? 'Name a lead partner above; people staffed on the account\'s projects appear automatically.' : ''}
        </div>
      ) : (
        <>
          {group('Lead partner', leadList, <ClientTeamBadge label="Lead Partner" bg="#FFE7DC" fg="#B83C0F"/>, true)}
          {group('Currently working', current, <ClientTeamBadge label="Current" bg="#DCF1E2" fg="var(--support-green)"/>, false)}
          {group('Previously working', past, <ClientTeamBadge label="Past" bg="var(--bg-card)" fg="var(--fg-muted)"/>, false)}
        </>
      )}
    </EntitySection>
  );
};

/* Small overlapping avatar stack + headcount for a project's team. */
const TeamFaces = ({ team }) => {
  const t = team || { count: 0, people: [] };
  if (!t.count) return <span style={{color:'var(--fg-muted)',fontSize:11.5}}>—</span>;
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:6}}>
      <span style={{display:'inline-flex'}}>
        {(t.people || []).slice(0, 4).map((m, i) => {
          const initials = (m.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('');
          return (
            <span key={i} title={m.name} style={{marginLeft: i ? -6 : 0, zIndex: 10 - i, width:22, height:22, borderRadius:'50%', border:'2px solid var(--bg)', background:'var(--bg-card)', display:'inline-flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0}}>
              {m.photoUrl
                ? <img src={m.photoUrl} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                : <span style={{fontSize:8,fontWeight:700,color:'var(--fg-muted)'}}>{initials}</span>}
            </span>
          );
        })}
      </span>
      <span style={{fontSize:11.5,color:'var(--fg-muted)'}}>{t.count} {t.count === 1 ? 'person' : 'people'}</span>
    </span>
  );
};

const ClientDetailView = ({ slug, currentUser, onBack }) => {
  const isAdmin = currentUser?.role === 'admin';
  // Firm editors (admin + eOS-Manager) manage the team/leads; entity
  // deletion (Settings tab) stays admin-only.
  const canManage = isAdmin || currentUser?.role === 'eOS-Manager';
  const [data, setData] = React.useState(null);
  const [tax, setTax] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [tab, setTab] = React.useState('overview');
  const [editing, setEditing] = React.useState(false);

  useSubCrumbs(data?.client?.name
    ? [{ label: data.client.name, href: null }]
    : []);

  React.useEffect(() => {
    let cancelled = false;
    setError(null); setData(null);
    getClientProfile(slug)
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setError(err.message || String(err)); });
    listTaxonomies()
      .then(t => { if (!cancelled) setTax(t); })
      .catch(() => { /* graceful */ });
    return () => { cancelled = true; };
  }, [slug, reloadKey]);

  if (error) {
    return (
      <div className="page">
        <div className="page__head">
          <h1>Client · {slug}</h1>
          <div className="lede">Failed to load this client.</div>
        </div>
        <ErrorBox>{error}</ErrorBox>
        <div style={{fontSize:12,color:'var(--fg-muted)',margin:'6px 0 14px'}}>
          Tried: <code>/api/clients/{slug}</code>
        </div>
        <div style={{display:'flex',gap:10}}>
          <button className="btn-orange" onClick={() => setReloadKey(k => k + 1)}>Retry</button>
          <button className="btn-ghost" onClick={onBack}>← Back to clients</button>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="page">
        <Loading>Loading client <code style={{color:'var(--fg-muted)'}}>{slug}</code>…</Loading>
      </div>
    );
  }

  const { client, industry, projects } = data;
  const currentPeople = data.currentPeople || [];
  const pastPeople = data.pastPeople || [];
  const canEdit = !!data.viewer?.canEdit;
  const adminEditing = canManage && editing;
  const saveClient = async patch => {
    const updated = await patchClient(client.slug || client._id || client.id, patch);
    setData(d => (d ? { ...d, client: { ...d.client, ...updated } } : d));
  };
  const applyLogo = updated => setData(d => (d ? { ...d, client: { ...d.client, ...updated } } : d));
  const serviceLookup   = Object.fromEntries(((tax?.services)   || []).map(s => [s._id, s.name]));
  const industryLookup  = Object.fromEntries(((tax?.industries) || []).map(i => [i._id, i.name]));
  // Staffing chart input: one stacked series per project, carrying that
  // project's staffed person-days by month.
  const clientStaffSeries = (projects || [])
    .filter(p => p.staffedDaysByMonth && Object.keys(p.staffedDaysByMonth).length)
    .map(p => ({ key: p.id, label: p.name, byMonth: p.staffedDaysByMonth }));
  // People to scope the staffing/booking grid to — everyone who has ever
  // worked on the account.
  const clientUserIds = [...new Set([...currentPeople, ...pastPeople].map(p => p.userId))];

  const ttm = computeTtm(projects);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'history',  label: 'Project History' },
    { id: 'staffing', label: 'Staffing' },
    ...(isAdmin ? [{ id: 'settings', label: 'Settings' }] : []),
  ];
  const activeTab = tabs.some(t => t.id === tab) ? tab : 'overview';

  const meta = (
    <>
      {projects.length} project{projects.length === 1 ? '' : 's'} · {currentPeople.length} active
      {ttm > 0 && <><br/>TTM: {fmtAmount(ttm)} CHF</>}
    </>
  );

  return (
    <EditModeProvider canEdit={canEdit} editing={editing} onEditingChange={setEditing}>
    <EntityShell
      title={
        <EditableText
          value={client.name}
          onSave={v => saveClient({ name: v })}
          placeholder="Client name"
          maxLength={80}
          inputStyle={{fontFamily:'var(--font-display)',fontSize:24,fontWeight:500}}
        />
      }
      subtitle={industry ? (
        <span style={{display:'inline-flex',flexWrap:'wrap',alignItems:'center',gap:'2px 8px'}}>
          <EntityLink type="industry" handle={entityHandle(industry)}>{industry.name}</EntityLink>
        </span>
      ) : null}
      badges={[{ label: 'Client', color: '#FF6428' }]}
      avatar={<ClientLogoEditable client={client} onChanged={applyLogo}/>}
      meta={meta}
      headerAction={<EditSwitch/>}
      entityType="client"
      entityId={client._id || client.id}
      rightRail={activeTab === 'staffing' ? null : undefined}
    >
      <ProfileTabs tabs={tabs} active={activeTab} onChange={setTab}/>

      {/* ── Overview — team, revenue, auto-generated summary ── */}
      {activeTab === 'overview' && (
        <>
          <ClientTeamSection
            clientId={client._id || client.id}
            isAdmin={canManage}
            adminEditing={adminEditing}
            currentPeople={currentPeople}
            pastPeople={pastPeople}
          />

          <EntitySection title="Revenue — monthly, stacked by service">
            <MonthlyRevenueStack
              projects={projects}
              stackKey="services"
              tagLookup={serviceLookup}
              months={18}
              footnote="Last 18 months. Each project's volume is split proportionally across its active months, then by tagged services."
            />
          </EntitySection>

          <EntitySection title="Summary">
            <EditableText
              multiline
              rows={6}
              value={client.description}
              onSave={v => saveClient({ description: v })}
              placeholder="Auto-generated nightly from the account's projects. Edit to override."
              viewRender={v => (v
                ? <MarkdownView source={v}/>
                : <span style={{color:'var(--fg-muted)'}}>No summary yet — generated automatically each night from this account's projects.</span>)}
            />
            <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:10,fontStyle:'italic'}}>
              Auto-generated each night (02:00) by the Client Summary Writer agent from this account's projects and their descriptions. Edit to override; your text is kept until the next refresh.
            </div>
          </EntitySection>
        </>
      )}

      {/* ── Project History — the account's projects ── */}
      {activeTab === 'history' && (
        <EntitySection title={`Projects · ${projects.length}`}>
          {projects.length === 0 ? (
            <div style={{padding:'14px 12px',color:'var(--fg-muted)',fontSize:13}}>
              No projects yet for this client.
            </div>
          ) : (
            <table className="tbl tbl--stack tbl--projcard" style={{width:'100%'}}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th style={{width:100}}>Status</th>
                  <th>Services</th>
                  <th style={{width:130,textAlign:'right'}}>Volume</th>
                  <th style={{width:160,textAlign:'right'}}>Dates</th>
                  <th style={{width:150}}>Team</th>
                </tr>
              </thead>
              <tbody>
                {projects.map(p => (
                  <tr
                    key={p._id}
                    onClick={() => { window.location.hash = `#projects/${p.slug || p._id}`; }}
                    style={{borderLeft:`3px solid ${STATUS_COLOR[p.status] || 'var(--fg-muted)'}`,cursor:'pointer'}}
                    title={`Open ${p.name}`}
                  >
                    <td data-primary>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <ClientLogo client={client} size={22}/>
                        <span style={{fontWeight:500}}>{p.name}</span>
                      </div>
                      {p.description && (
                        <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:480}}>
                          {stripMarkdownForPreview(p.description)}
                        </div>
                      )}
                    </td>
                    <td data-label="Status">
                      <span style={{fontSize:10.5,padding:'2px 8px',borderRadius:4,fontWeight:600,letterSpacing:'0.05em',textTransform:'uppercase',background:STATUS_BG[p.status] || 'var(--bg-card)',color:STATUS_COLOR[p.status] || 'var(--fg-muted)'}}>
                        {p.status}
                      </span>
                    </td>
                    <td data-label="Services" style={{fontSize:11.5}}>
                      {(p.services || []).slice(0, 3).map(sid => (
                        <a key={sid} className="tag" href={entityHref('service', sid)} onClick={e => e.stopPropagation()} title={`Open ${serviceLookup[sid] || 'service'}`} style={{marginRight:4,fontSize:10.5,padding:'1px 7px',textDecoration:'none'}}>
                          {serviceLookup[sid] || '?'}
                        </a>
                      ))}
                      {(p.industries || []).slice(0, 2).map(iid => (
                        <a key={iid} href={entityHref('industry', iid)} onClick={e => e.stopPropagation()} title={`Open ${industryLookup[iid] || 'industry'}`} style={{marginRight:4,fontSize:10.5,padding:'1px 7px',borderRadius:10,background:'#EBE5F8',color:'#471D65',textDecoration:'none',display:'inline-block'}}>
                          {industryLookup[iid] || '?'}
                        </a>
                      ))}
                    </td>
                    <td data-label="Volume" style={{textAlign:'right',fontFamily:'JetBrains Mono'}}>
                      <div style={{fontSize:12.5,color:p.volumeChf ? 'var(--fg)' : 'var(--fg-muted)'}}>{p.volumeChf ? `${fmtAmount(p.volumeChf)} CHF` : '—'}</div>
                      {p.volumeLocal != null && p.currency && p.currency !== 'CHF' && (
                        <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:1}}>{fmtAmount(p.volumeLocal)} {p.currency}</div>
                      )}
                    </td>
                    <td data-label="Dates" style={{textAlign:'right',color:'var(--fg-muted)',fontSize:11.5}}>
                      {p.startsAt ? dateInputValue(p.startsAt) : '—'}
                      {p.endsAt ? <> → {dateInputValue(p.endsAt)}</> : null}
                    </td>
                    <td data-label="Team">
                      <TeamFaces team={p.team}/>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </EntitySection>
      )}

      {/* ── Staffing — across all of the account's projects ── */}
      {activeTab === 'staffing' && (
        <EntitySection title="Staffing &amp; booking">
          <div className="staffing-bleed">
            <MonthlyStaffingStack
              series={clientStaffSeries}
              footnote="Staffed person-days per month across this client's projects (1 day = 8 h, derived from staffing %), one segment per project. A week counts toward the month of its Monday."
            />
            <div style={{marginTop:18}}>
              {clientUserIds.length === 0 ? (
                <div style={{padding:'12px',color:'var(--fg-muted)',fontSize:13}}>
                  No one's staffed on this account's projects yet. Allocate people on the Staffing page and they'll show up here.
                </div>
              ) : (
                <StaffingEmbed userIds={clientUserIds} weeks={16} bare/>
              )}
            </div>
          </div>
        </EntitySection>
      )}

      {/* ── Settings — admin-only danger zone ── */}
      {activeTab === 'settings' && isAdmin && (
        <DangerZoneSection
          noun="client"
          name={client.name}
          kind="client"
          entityId={client._id || client.id}
          onDeleted={onBack}
        />
      )}
    </EntityShell>
    </EditModeProvider>
  );
};

function computeTtm(projects) {
  const now = Date.now();
  const windowStart = new Date(now);
  windowStart.setMonth(windowStart.getMonth() - 12);
  const monthsSet = ttmMonthsSet();
  let total = 0;
  for (const p of (projects || [])) {
    const m = monthlyTtm(p, monthsSet);
    if (m != null) { total += m; continue; }
    const vol = Number(p.volumeChf || 0);
    if (!Number.isFinite(vol) || vol <= 0) continue;
    const s = p.startsAt ? new Date(p.startsAt).getTime() : null;
    const e = p.endsAt   ? new Date(p.endsAt).getTime()   : null;
    const projStart = s ?? e ?? now;
    const projEnd   = Math.min(e ?? now, now);
    if (projEnd <= projStart) continue;
    const totalMs   = projEnd - projStart || 1;
    const overlap   = Math.max(0, Math.min(projEnd, now) - Math.max(projStart, windowStart.getTime()));
    total += vol * (overlap / totalMs);
  }
  return total;
}

/* shared atoms */
const Loading = ({ children }) => (
  <div style={{padding:24,color:'var(--fg-muted)',fontSize:13,textAlign:'center'}}>{children}</div>
);
const Empty = ({ children }) => (
  <div style={{padding:24,color:'var(--fg-muted)',fontSize:13.5,textAlign:'center'}}>{children}</div>
);
const ErrorBox = ({ children }) => (
  <div style={{padding:12,marginBottom:16,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{children}</div>
);

/* ─── Phase 2 placeholder: Services + Industries views ─────────────
 * Real list+detail dashboards land in Phases 5–6. For now we just
 * surface the count + lede so the nav entries don't dead-end.
 */

/* Phase 6: Services list-with-detail. Dispatcher only — no hooks
 * at the export boundary so list↔detail transitions are clean. */
export const ServicesView = ({ currentUser, subPath, onNavigate } = {}) => (
  subPath
    ? <ServiceDetailView slug={subPath} currentUser={currentUser} onBack={() => onNavigate?.('')}/>
    : <ServicesList currentUser={currentUser} onNavigate={onNavigate}/>
);

const ServicesList = ({ currentUser, onNavigate }) => {
  const [tax, setTax] = React.useState(null);
  const [projects, setProjects] = React.useState([]);
  const [error, setError] = React.useState(null);
  const [q, setQ] = React.useState('');
  const isAdmin = currentUser?.role === 'admin';

  const reload = React.useCallback(() => {
    listTaxonomies()
      .then(t => setTax(t))
      .catch(err => setError(err.message));
    listProjects().then(setProjects).catch(() => setProjects([]));
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  const servicesList   = tax?.services || [];
  const servicesById   = React.useMemo(
    () => Object.fromEntries(servicesList.map(s => [s._id, s])),
    [servicesList],
  );

  // Counts. Falls back to the legacy singular `serviceId` when the
  // multi-field isn't there.
  const projectCountByServiceId = React.useMemo(() => {
    const m = {};
    for (const p of projects) {
      if (p.serviceId) m[p.serviceId] = (m[p.serviceId] || 0) + 1;
      for (const id of (p.services || [])) m[id] = (m[id] || 0) + 1;
    }
    return m;
  }, [projects]);

  // Sub-service count per parent service.
  const subCountByParentId = React.useMemo(() => {
    const m = {};
    for (const s of servicesList) {
      if (s.parentServiceId) m[s.parentServiceId] = (m[s.parentServiceId] || 0) + 1;
    }
    return m;
  }, [servicesList]);

  const needle = q.trim().toLowerCase();
  const filtered = !needle ? servicesList : servicesList.filter(s =>
    (s.name || '').toLowerCase().includes(needle) ||
    (s.description || '').toLowerCase().includes(needle),
  );

  const open = s => onNavigate?.(s.slug || s._id);

  return (
    <div className="page">
      <div className="page__head">
        <h1>Services</h1>
        <div className="lede">
          What the firm sells. Click a row to open the full dashboard — our offerings, every project tagged with this practice area, plus a daily news ticker fed by the Service News Watcher.
        </div>
      </div>

      <div style={{display:'flex',gap:10,marginBottom:14}}>
        <input
          className="input"
          placeholder="Search services…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{maxWidth:380}}
        />
        <div style={{flex:1}}/>
        {tax && <span style={{fontSize:12,color:'var(--fg-muted)',alignSelf:'center'}}>{filtered.length} of {servicesList.length}</span>}
        {isAdmin && <AddEntityButton kind="services" onCreated={reload}/>}
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}
      {!tax && !error && <Loading>Loading services…</Loading>}
      {tax && filtered.length === 0 && (
        <Empty>{needle ? 'No services match.' : isAdmin ? 'No services yet. Use the Add service button above.' : 'No services yet. Ask an admin to add one.'}</Empty>
      )}

      {filtered.length > 0 && (
        <div className="tbl-card">
          <table className="tbl" style={{width:'100%'}}>
            <thead>
              <tr>
                <th>Name</th>
                <th style={{width:200}}>Parent</th>
                <th style={{width:120,textAlign:'right'}}># sub-services</th>
                <th style={{width:120,textAlign:'right'}}># projects</th>
                <th style={{width:38}}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => {
                const parent = s.parentServiceId ? servicesById[s.parentServiceId] : null;
                return (
                  <tr key={s._id} onClick={() => open(s)} style={{cursor:'pointer'}}>
                    <td>
                      <div style={{fontWeight:500}}>{s.name}</div>
                      {s.description && (
                        <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:560}}>
                          {stripMarkdownForPreview(s.description)}
                        </div>
                      )}
                    </td>
                    <td style={{color:'var(--fg-muted)',fontSize:12.5}}>{parent ? parent.name : '—'}</td>
                    <td style={{textAlign:'right',color:'var(--fg-muted)',fontSize:12.5}}>{subCountByParentId[s._id] || 0}</td>
                    <td style={{textAlign:'right',color:'var(--fg-muted)',fontSize:12.5}}>{projectCountByServiceId[s._id] || 0}</td>
                    <td style={{textAlign:'right',color:'var(--fg-muted)',fontSize:14}}>›</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* Owner picker for the curated taxonomies (service / industry /
 * organization). Shown to editors; the dropdown is enabled only for
 * admins — a non-admin owner sees who owns it but can't reassign.
 * Self-fetches the firm directory for the option list. */
const OwnerField = ({ value, onSave, isAdmin }) => {
  const [people, setPeople] = React.useState([]);
  React.useEffect(() => { listPeople().then(p => setPeople(p || [])).catch(() => setPeople([])); }, []);
  const options = people.map(p => ({ value: String(p._id || p.id), label: p.name }));
  return (
    <div className="field" style={{margin:0,maxWidth:380}}>
      <EditableSelect
        value={value || ''}
        options={options}
        onSave={onSave}
        canEditField={isAdmin}
        allowEmpty
        emptyView="Unassigned"
        placeholder="— pick an owner —"
      />
      <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4}}>
        {isAdmin
          ? 'The owner can edit this page directly, alongside admins.'
          : 'Only admins can change the owner.'}
      </div>
    </div>
  );
};

/* Full-page Service detail dashboard. */
const ServiceDetailView = ({ slug, currentUser, onBack }) => {
  const isAdmin = currentUser?.role === 'admin';
  // Firm editors (admin + eOS-Manager) manage leads; owner reassignment
  // stays admin-only (OwnerField reads viewer.isAdmin).
  const canManage = isAdmin || currentUser?.role === 'eOS-Manager';
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);

  useSubCrumbs(data?.service?.name
    ? [{ label: data.service.name, href: null }]
    : []);

  React.useEffect(() => {
    let cancelled = false;
    setError(null); setData(null);
    getServiceProfile(slug)
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setError(err.message || String(err)); });
    return () => { cancelled = true; };
  }, [slug, reloadKey]);

  if (error) {
    return (
      <div className="page">
        <div className="page__head">
          <h1>Service · {slug}</h1>
          <div className="lede">Failed to load this service.</div>
        </div>
        <ErrorBox>{error}</ErrorBox>
        <div style={{fontSize:12,color:'var(--fg-muted)',margin:'6px 0 14px'}}>
          Tried: <code>/api/services/{slug}</code>
        </div>
        <div style={{display:'flex',gap:10}}>
          <button className="btn-orange" onClick={() => setReloadKey(k => k + 1)}>Retry</button>
          <button className="btn-ghost" onClick={onBack}>← Back to services</button>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="page">
        <Loading>Loading service <code style={{color:'var(--fg-muted)'}}>{slug}</code>…</Loading>
      </div>
    );
  }

  const { service, parent, subServices, projects, clients, offerings, offeringsPath } = data;
  const canEdit = !!data.viewer?.canEdit;
  const saveService = async patch => {
    const updated = await patchService(service.slug || service._id || service.id, patch);
    setData(d => (d ? { ...d, service: { ...d.service, ...updated } } : d));
  };
  // Plain const — useMemo here would sit AFTER the `if (!data)`
  // early-return, violating Rules of Hooks. Cheap to recompute.
  const clientLookup = Object.fromEntries((clients || []).map(c => [c.id, c.name]));

  const ttm = computeTtm(projects);

  const meta = (
    <>
      {projects.length} project{projects.length === 1 ? '' : 's'} · {clients.length} client{clients.length === 1 ? '' : 's'}
      {ttm > 0 && <><br/>TTM: {fmtAmount(ttm)} CHF</>}
    </>
  );

  return (
    <EditModeProvider canEdit={canEdit}>
    <EntityShell
      title={
        <EditableText
          value={service.name}
          onSave={v => saveService({ name: v })}
          placeholder="Service name"
          maxLength={80}
          inputStyle={{fontFamily:'var(--font-display)',fontSize:24,fontWeight:500}}
        />
      }
      subtitle={parent ? <>Sub-service of <EntityLink type="service" handle={entityHandle(parent)}>{parent.name}</EntityLink></> : null}
      badges={[{ label: 'Service', color: '#FF6428' }]}
      meta={meta}
      headerAction={<EditSwitch/>}
      entityType="service"
      entityId={service._id || service.id}
    >
      {(service.description || canEdit) && (
        <EntitySection title="About">
          <EditableText
            multiline
            rows={5}
            value={service.description}
            onSave={v => saveService({ description: v })}
            placeholder="Describe this service — what it covers and the value it delivers."
            viewRender={v => (v ? <MarkdownView source={v}/> : <span style={{color:'var(--fg-muted)'}}>No description yet.</span>)}
          />
        </EntitySection>
      )}

      {canEdit && (
        <EntitySection title="Owner">
          <OwnerField value={service.ownerUserId} onSave={v => saveService({ ownerUserId: v })} isAdmin={!!data.viewer?.isAdmin}/>
        </EntitySection>
      )}

      <ItemLeadsSection itemType="service" itemId={service._id || service.id} isAdmin={canManage}/>

      <EntitySection title="Our offerings">
        {offerings
          ? <MarkdownView source={offerings}/>
          : (
            <div style={{padding:'14px 12px',color:'var(--fg-muted)',fontSize:13,lineHeight:1.55}}>
              No offerings document at <code>{offeringsPath}</code> yet. Create that file in OrgRepo to surface what we sell under this service — packages, day-rates, sub-offerings, references.
            </div>
          )}
        {subServices.length > 0 && (
          <div style={{marginTop:14}}>
            <div style={{fontSize:11,letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:6,fontWeight:600}}>
              Sub-services · {subServices.length}
            </div>
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {subServices.map(s => (
                <span
                  key={s.id}
                  className="tag"
                  style={{cursor:'pointer'}}
                  onClick={() => { location.hash = `services/${s.slug || s.id}`; }}
                  title={s.description || ''}
                >
                  {s.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </EntitySection>

      <EntitySection title="Revenue — monthly, stacked by client">
        <MonthlyRevenueStack
          projects={projects}
          stackKey="clientId"
          tagLookup={clientLookup}
          months={18}
          footnote="Last 18 months. Each project's volume is split proportionally across its active months; the stack shows which clients drive revenue in this service."
        />
      </EntitySection>

      <EntitySection title={`Projects · ${projects.length}`}>
        {projects.length === 0 ? (
          <div style={{padding:'14px 12px',color:'var(--fg-muted)',fontSize:13}}>
            No projects tagged with this service yet. Tag a project with this service when you add or edit it on the Projects page.
          </div>
        ) : (
          <table className="tbl" style={{width:'100%'}}>
            <thead>
              <tr>
                <th>Project</th>
                <th style={{width:180}}>Client</th>
                <th style={{width:100}}>Status</th>
                <th style={{width:140,textAlign:'right'}}>Volume</th>
                <th style={{width:160,textAlign:'right'}}>Dates</th>
              </tr>
            </thead>
            <tbody>
              {projects.map(p => (
                <tr key={p._id} style={{borderLeft:`3px solid ${STATUS_COLOR[p.status] || 'var(--fg-muted)'}`}}>
                  <td>
                    <div style={{fontWeight:500}}>
                      <EntityLink type="project" handle={entityHandle(p)}>{p.name}</EntityLink>
                    </div>
                    {p.description && (
                      <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:480}}>
                        {stripMarkdownForPreview(p.description)}
                      </div>
                    )}
                  </td>
                  <td style={{color:'var(--fg-muted)',fontSize:12.5}}>
                    <EntityLink type="client" handle={p.clientId} muted>{p.clientName || '—'}</EntityLink>
                  </td>
                  <td>
                    <span style={{fontSize:10.5,padding:'2px 8px',borderRadius:4,fontWeight:600,letterSpacing:'0.05em',textTransform:'uppercase',background:STATUS_BG[p.status] || 'var(--bg-card)',color:STATUS_COLOR[p.status] || 'var(--fg-muted)'}}>
                      {p.status}
                    </span>
                  </td>
                  <td style={{textAlign:'right',fontFamily:'JetBrains Mono',fontSize:11.5,color:p.volumeChf ? 'var(--fg)' : 'var(--fg-muted)'}}>
                    {p.volumeChf ? `${fmtAmount(p.volumeChf)} CHF` : '—'}
                  </td>
                  <td style={{textAlign:'right',color:'var(--fg-muted)',fontSize:11.5}}>
                    {p.startsAt ? dateInputValue(p.startsAt) : '—'}
                    {p.endsAt ? <> → {dateInputValue(p.endsAt)}</> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </EntitySection>

      {isAdmin && (
        <DangerZoneSection
          noun="service"
          name={service.name}
          kind="service"
          entityId={service._id || service.id}
          onDeleted={onBack}
        />
      )}
    </EntityShell>
    </EditModeProvider>
  );
};

/* Phase 5: Industries list-with-detail. Dispatcher only — no hooks
 * at the export boundary. */
export const IndustriesView = ({ currentUser, subPath, onNavigate } = {}) => (
  subPath
    ? <IndustryDetailView slug={subPath} currentUser={currentUser} onBack={() => onNavigate?.('')}/>
    : <IndustriesList currentUser={currentUser} onNavigate={onNavigate}/>
);

const IndustriesList = ({ currentUser, onNavigate }) => {
  // industries from listTaxonomies + project counts from listProjects.
  const [tax, setTax] = React.useState(null);
  const [projects, setProjects] = React.useState([]);
  const [tagsByProject, setTagsByProject] = React.useState({});
  const [error, setError] = React.useState(null);
  const [q, setQ] = React.useState('');
  const isAdmin = currentUser?.role === 'admin';

  const reload = React.useCallback(() => {
    listTaxonomies()
      .then(t => setTax(t))
      .catch(err => setError(err.message));
    // The legacy listProjects() doesn't carry industries[] — so we
    // fall back to the singular industryId field on the project row
    // for counts. Good enough for a list view; the detail view does
    // the full join.
    listProjects().then(setProjects).catch(() => setProjects([]));
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  void tagsByProject; void setTagsByProject;   // reserved for richer counts

  const industriesList = tax?.industries || [];
  const projectCountByIndustryId = React.useMemo(() => {
    const m = {};
    for (const p of projects) {
      if (p.industryId) m[p.industryId] = (m[p.industryId] || 0) + 1;
      for (const id of (p.industries || [])) m[id] = (m[id] || 0) + 1;
    }
    return m;
  }, [projects]);

  const proposalCountByIndustryId = React.useMemo(() => {
    const m = {};
    for (const p of projects) {
      if (p.status !== 'lead' && p.status !== 'won-waiting') continue;
      if (p.industryId) m[p.industryId] = (m[p.industryId] || 0) + 1;
      for (const id of (p.industries || [])) m[id] = (m[id] || 0) + 1;
    }
    return m;
  }, [projects]);

  const needle = q.trim().toLowerCase();
  const filtered = !needle ? industriesList : industriesList.filter(i =>
    (i.name || '').toLowerCase().includes(needle) ||
    (i.description || '').toLowerCase().includes(needle),
  );

  const open = i => onNavigate?.(i.slug || i._id);

  return (
    <div className="page">
      <div className="page__head">
        <h1>Industries</h1>
        <div className="lede">
          Industry verticals the firm serves. Click a row to open the full dashboard — our viewpoints, latest proposals, every project in this vertical, plus a daily news ticker fed by the Industry News Watcher.
        </div>
      </div>

      <div style={{display:'flex',gap:10,marginBottom:14}}>
        <input
          className="input"
          placeholder="Search industries…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{maxWidth:380}}
        />
        <div style={{flex:1}}/>
        {tax && <span style={{fontSize:12,color:'var(--fg-muted)',alignSelf:'center'}}>{filtered.length} of {industriesList.length}</span>}
        {isAdmin && <AddEntityButton kind="industries" onCreated={reload}/>}
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}
      {!tax && !error && <Loading>Loading industries…</Loading>}
      {tax && filtered.length === 0 && (
        <Empty>{needle ? 'No industries match.' : isAdmin ? 'No industries yet. Use the Add industry button above.' : 'No industries yet. Ask an admin to add one.'}</Empty>
      )}

      {filtered.length > 0 && (
        <div className="tbl-card">
          <table className="tbl" style={{width:'100%'}}>
            <thead>
              <tr>
                <th>Name</th>
                <th style={{width:120,textAlign:'right'}}># projects</th>
                <th style={{width:120,textAlign:'right'}}># proposals</th>
                <th style={{width:38}}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(i => (
                <tr key={i._id} onClick={() => open(i)} style={{cursor:'pointer'}}>
                  <td>
                    <div style={{fontWeight:500}}>{i.name}</div>
                    {i.description && (
                      <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:560}}>
                        {stripMarkdownForPreview(i.description)}
                      </div>
                    )}
                  </td>
                  <td style={{textAlign:'right',color:'var(--fg-muted)',fontSize:12.5}}>{projectCountByIndustryId[i._id] || 0}</td>
                  <td style={{textAlign:'right',color:'var(--fg-muted)',fontSize:12.5}}>{proposalCountByIndustryId[i._id] || 0}</td>
                  <td style={{textAlign:'right',color:'var(--fg-muted)',fontSize:14}}>›</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* Full-page Industry detail dashboard. */
const IndustryDetailView = ({ slug, currentUser, onBack }) => {
  const isAdmin = currentUser?.role === 'admin';
  // Firm editors (admin + eOS-Manager) manage leads; owner reassignment
  // stays admin-only (OwnerField reads viewer.isAdmin).
  const canManage = isAdmin || currentUser?.role === 'eOS-Manager';
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);

  useSubCrumbs(data?.industry?.name
    ? [{ label: data.industry.name, href: null }]
    : []);

  React.useEffect(() => {
    let cancelled = false;
    setError(null); setData(null);
    getIndustryProfile(slug)
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setError(err.message || String(err)); });
    return () => { cancelled = true; };
  }, [slug, reloadKey]);

  if (error) {
    return (
      <div className="page">
        <div className="page__head">
          <h1>Industry · {slug}</h1>
          <div className="lede">Failed to load this industry.</div>
        </div>
        <ErrorBox>{error}</ErrorBox>
        <div style={{fontSize:12,color:'var(--fg-muted)',margin:'6px 0 14px'}}>
          Tried: <code>/api/industries/{slug}</code>
        </div>
        <div style={{display:'flex',gap:10}}>
          <button className="btn-orange" onClick={() => setReloadKey(k => k + 1)}>Retry</button>
          <button className="btn-ghost" onClick={onBack}>← Back to industries</button>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="page">
        <Loading>Loading industry <code style={{color:'var(--fg-muted)'}}>{slug}</code>…</Loading>
      </div>
    );
  }

  const { industry, projects, clients, proposals, viewpoints, viewpointsPath } = data;
  const canEdit = !!data.viewer?.canEdit;
  // Owner-aware inline save — merges the server's echoed entity back in.
  const saveIndustry = async patch => {
    const updated = await patchIndustry(industry.slug || industry._id || industry.id, patch);
    setData(d => (d ? { ...d, industry: { ...d.industry, ...updated } } : d));
  };
  // Plain const — useMemo here would be AFTER the `if (!data)`
  // early-return, which violates Rules of Hooks (hooks count must
  // be constant between renders). The computation is trivial so
  // recomputing on every render is fine.
  const clientLookup = Object.fromEntries((clients || []).map(c => [c.id, c.name]));

  const ttm = computeTtm(projects);

  const meta = (
    <>
      {projects.length} project{projects.length === 1 ? '' : 's'} · {clients.length} client{clients.length === 1 ? '' : 's'}
      {ttm > 0 && <><br/>TTM: {fmtAmount(ttm)} CHF</>}
    </>
  );

  return (
    <EditModeProvider canEdit={canEdit}>
    <EntityShell
      title={
        <EditableText
          value={industry.name}
          onSave={v => saveIndustry({ name: v })}
          placeholder="Industry name"
          maxLength={80}
          inputStyle={{fontFamily:'var(--font-display)',fontSize:24,fontWeight:500}}
        />
      }
      subtitle={null}
      badges={[{ label: 'Industry', color: '#715AAE' }]}
      meta={meta}
      headerAction={<EditSwitch/>}
      entityType="industry"
      entityId={industry._id || industry.id}
    >
      {(industry.description || canEdit) && (
        <EntitySection title="About">
          <EditableText
            multiline
            rows={5}
            value={industry.description}
            onSave={v => saveIndustry({ description: v })}
            placeholder="Describe this industry — what it covers, why it matters to the firm."
            emptyView="No description yet."
            viewRender={v => (v ? <MarkdownView source={v}/> : <span style={{color:'var(--fg-muted)'}}>No description yet.</span>)}
          />
        </EntitySection>
      )}

      {canEdit && (
        <EntitySection title="Owner">
          <OwnerField value={industry.ownerUserId} onSave={v => saveIndustry({ ownerUserId: v })} isAdmin={!!data.viewer?.isAdmin}/>
        </EntitySection>
      )}

      <ItemLeadsSection itemType="industry" itemId={industry._id || industry.id} isAdmin={canManage}/>

      <EntitySection title="Our viewpoints">
        {viewpoints
          ? <MarkdownView source={viewpoints}/>
          : (
            <div style={{padding:'14px 12px',color:'var(--fg-muted)',fontSize:13,lineHeight:1.55}}>
              No viewpoint document at <code>{viewpointsPath}</code> yet. Create that file in OrgRepo to surface the firm's position in this vertical here — what we think about it, what we've published, what we'd argue at a board.
            </div>
          )}
      </EntitySection>

      <EntitySection title="Revenue — monthly, stacked by client">
        <MonthlyRevenueStack
          projects={projects}
          stackKey="clientId"
          tagLookup={clientLookup}
          months={18}
          footnote="Last 18 months. Each project's volume is split proportionally across its active months; each project lands in exactly one client bucket."
        />
      </EntitySection>

      <EntitySection title={`Latest proposals · ${proposals.length}`}>
        {proposals.length === 0 ? (
          <div style={{padding:'14px 12px',color:'var(--fg-muted)',fontSize:13}}>
            Nothing in the pipeline for this industry right now. Lead and "won-waiting" projects appear here.
          </div>
        ) : (
          <table className="tbl" style={{width:'100%'}}>
            <thead>
              <tr>
                <th>Project</th>
                <th style={{width:180}}>Client</th>
                <th style={{width:120}}>Status</th>
                <th style={{width:140,textAlign:'right'}}>Volume</th>
              </tr>
            </thead>
            <tbody>
              {proposals.map(p => (
                <tr key={p._id} style={{borderLeft:`3px solid ${STATUS_COLOR[p.status] || 'var(--fg-muted)'}`}}>
                  <td><div style={{fontWeight:500}}><EntityLink type="project" handle={entityHandle(p)}>{p.name}</EntityLink></div></td>
                  <td style={{color:'var(--fg-muted)',fontSize:12.5}}><EntityLink type="client" handle={p.clientId} muted>{p.clientName || '—'}</EntityLink></td>
                  <td>
                    <span style={{fontSize:10.5,padding:'2px 8px',borderRadius:4,fontWeight:600,letterSpacing:'0.05em',textTransform:'uppercase',background:STATUS_BG[p.status] || 'var(--bg-card)',color:STATUS_COLOR[p.status] || 'var(--fg-muted)'}}>
                      {p.status}
                    </span>
                  </td>
                  <td style={{textAlign:'right',fontFamily:'JetBrains Mono',fontSize:11.5,color:p.volumeChf ? 'var(--fg)' : 'var(--fg-muted)'}}>
                    {p.volumeChf ? `${fmtAmount(p.volumeChf)} CHF` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </EntitySection>

      <EntitySection title={`Projects · ${projects.length}`}>
        {projects.length === 0 ? (
          <div style={{padding:'14px 12px',color:'var(--fg-muted)',fontSize:13}}>
            No projects tagged in this industry yet. Tag a project with this industry when you add or edit it on the Projects page.
          </div>
        ) : (
          <table className="tbl" style={{width:'100%'}}>
            <thead>
              <tr>
                <th>Project</th>
                <th style={{width:180}}>Client</th>
                <th style={{width:100}}>Status</th>
                <th style={{width:140,textAlign:'right'}}>Volume</th>
                <th style={{width:160,textAlign:'right'}}>Dates</th>
              </tr>
            </thead>
            <tbody>
              {projects.map(p => (
                <tr key={p._id} style={{borderLeft:`3px solid ${STATUS_COLOR[p.status] || 'var(--fg-muted)'}`}}>
                  <td>
                    <div style={{fontWeight:500}}>
                      <EntityLink type="project" handle={entityHandle(p)}>{p.name}</EntityLink>
                    </div>
                    {p.description && (
                      <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:480}}>
                        {stripMarkdownForPreview(p.description)}
                      </div>
                    )}
                  </td>
                  <td style={{color:'var(--fg-muted)',fontSize:12.5}}>
                    <EntityLink type="client" handle={p.clientId} muted>{p.clientName || '—'}</EntityLink>
                  </td>
                  <td>
                    <span style={{fontSize:10.5,padding:'2px 8px',borderRadius:4,fontWeight:600,letterSpacing:'0.05em',textTransform:'uppercase',background:STATUS_BG[p.status] || 'var(--bg-card)',color:STATUS_COLOR[p.status] || 'var(--fg-muted)'}}>
                      {p.status}
                    </span>
                  </td>
                  <td style={{textAlign:'right',fontFamily:'JetBrains Mono',fontSize:11.5,color:p.volumeChf ? 'var(--fg)' : 'var(--fg-muted)'}}>
                    {p.volumeChf ? `${fmtAmount(p.volumeChf)} CHF` : '—'}
                  </td>
                  <td style={{textAlign:'right',color:'var(--fg-muted)',fontSize:11.5}}>
                    {p.startsAt ? dateInputValue(p.startsAt) : '—'}
                    {p.endsAt ? <> → {dateInputValue(p.endsAt)}</> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </EntitySection>

      {isAdmin && (
        <DangerZoneSection
          noun="industry"
          name={industry.name}
          kind="industry"
          entityId={industry._id || industry.id}
          onDeleted={onBack}
        />
      )}
    </EntityShell>
    </EditModeProvider>
  );
};

/* ─── Organizations (companies) ────────────────────────────────────
 * "Organizations" map to the companies taxonomy — the legal entities
 * + business units people belong to (employer, sister offices, partner
 * firms). Same list-with-detail pattern as Services / Industries.
 * Dispatcher only — no hooks at the export boundary so list↔detail
 * transitions stay clean. */
export const OrganizationsView = ({ currentUser, subPath, onNavigate } = {}) => (
  subPath
    ? <OrganizationDetailView slug={subPath} currentUser={currentUser} onBack={() => onNavigate?.('')}/>
    : <OrganizationsList currentUser={currentUser} onNavigate={onNavigate}/>
);

const OrganizationsList = ({ currentUser, onNavigate }) => {
  const [tax, setTax] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [q, setQ] = React.useState('');
  const isAdmin = currentUser?.role === 'admin';

  const reload = React.useCallback(() => {
    listTaxonomies()
      .then(t => setTax(t))
      .catch(err => setError(err.message));
  }, []);

  React.useEffect(() => { reload(); }, [reload]);

  const companiesList = tax?.companies || [];
  const companiesById = React.useMemo(
    () => Object.fromEntries(companiesList.map(c => [c._id, c])),
    [companiesList],
  );
  const subCountByParentId = React.useMemo(() => {
    const m = {};
    for (const c of companiesList) {
      if (c.parentCompanyId) m[c.parentCompanyId] = (m[c.parentCompanyId] || 0) + 1;
    }
    return m;
  }, [companiesList]);

  const needle = q.trim().toLowerCase();
  const filtered = !needle ? companiesList : companiesList.filter(c =>
    (c.name || '').toLowerCase().includes(needle) ||
    (c.description || '').toLowerCase().includes(needle),
  );

  const open = c => onNavigate?.(c.slug || c._id);

  return (
    <div className="page">
      <div className="page__head">
        <h1>Organizations</h1>
        <div className="lede">
          The legal entities and business units of the firm — your employer, sister offices, partner firms. Click a row to open the full dashboard — its people (auto-filled), offices, company news, and guideline documents.
        </div>
      </div>

      <div style={{display:'flex',gap:10,marginBottom:14}}>
        <input
          className="input"
          placeholder="Search organizations…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{maxWidth:380}}
        />
        <div style={{flex:1}}/>
        {tax && <span style={{fontSize:12,color:'var(--fg-muted)',alignSelf:'center'}}>{filtered.length} of {companiesList.length}</span>}
        {isAdmin && <AddEntityButton kind="organizations" onCreated={reload}/>}
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}
      {!tax && !error && <Loading>Loading organizations…</Loading>}
      {tax && filtered.length === 0 && (
        <Empty>{needle ? 'No organizations match.' : isAdmin ? 'No organizations yet. Use the Add organization button above.' : 'No organizations yet. Ask an admin to add one.'}</Empty>
      )}

      {filtered.length > 0 && (
        <div className="tbl-card">
          <table className="tbl" style={{width:'100%'}}>
            <thead>
              <tr>
                <th>Name</th>
                <th style={{width:220}}>Parent</th>
                <th style={{width:140,textAlign:'right'}}># business units</th>
                <th style={{width:38}}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const parent = c.parentCompanyId ? companiesById[c.parentCompanyId] : null;
                return (
                  <tr key={c._id} onClick={() => open(c)} style={{cursor:'pointer'}}>
                    <td>
                      <div style={{fontWeight:500}}>{c.name}</div>
                      {c.description && (
                        <div style={{fontSize:11.5,color:'var(--fg-muted)',marginTop:2,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:560}}>
                          {stripMarkdownForPreview(c.description)}
                        </div>
                      )}
                    </td>
                    <td style={{color:'var(--fg-muted)',fontSize:12.5}}>{parent ? parent.name : '—'}</td>
                    <td style={{textAlign:'right',color:'var(--fg-muted)',fontSize:12.5}}>{subCountByParentId[c._id] || 0}</td>
                    <td style={{textAlign:'right',color:'var(--fg-muted)',fontSize:14}}>›</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* Full-page Organization detail dashboard. Mirrors the Industry /
 * Service detail shape: About + the org's people (auto-filled from
 * membership), offices, business units, and guideline documents, with
 * the right-rail news ticker fed by entity_news (entityType=company). */
const OrganizationDetailView = ({ slug, currentUser, onBack }) => {
  const isAdmin = currentUser?.role === 'admin';
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [showDocEditor, setShowDocEditor] = React.useState(false);
  const reload = React.useCallback(() => setReloadKey(k => k + 1), []);

  useSubCrumbs(data?.company?.name
    ? [{ label: data.company.name, href: null }]
    : []);

  React.useEffect(() => {
    let cancelled = false;
    setError(null); setData(null);
    getCompanyProfile(slug)
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setError(err.message || String(err)); });
    return () => { cancelled = true; };
  }, [slug, reloadKey]);

  if (error) {
    return (
      <div className="page">
        <div className="page__head">
          <h1>Organization · {slug}</h1>
          <div className="lede">Failed to load this organization.</div>
        </div>
        <ErrorBox>{error}</ErrorBox>
        <div style={{fontSize:12,color:'var(--fg-muted)',margin:'6px 0 14px'}}>
          Tried: <code>/api/companies/{slug}</code>
        </div>
        <div style={{display:'flex',gap:10}}>
          <button className="btn-orange" onClick={() => setReloadKey(k => k + 1)}>Retry</button>
          <button className="btn-ghost" onClick={onBack}>← Back to organizations</button>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="page">
        <Loading>Loading organization <code style={{color:'var(--fg-muted)'}}>{slug}</code>…</Loading>
      </div>
    );
  }

  const { company, parent, subCompanies = [], members = [], offices, officesPath, documents = [], news = [], allCreditors = [], revenueProjects = [] } = data;
  const canEdit = !!data.viewer?.canEdit;
  const saveCompany = async patch => {
    const updated = await patchCompany(company.slug || company._id || company.id, patch);
    setData(d => (d ? { ...d, company: { ...d.company, ...updated } } : d));
  };
  const companyCreditors = company.creditors || [];
  const saveCreditors = async next => {
    setData(d => (d ? { ...d, company: { ...d.company, creditors: next } } : d));  // optimistic
    try { await saveCompany({ creditors: next }); }
    catch (err) { window.alert(err.message || 'Failed to save creditors.'); reload(); }
  };
  const handleDeleteDoc = async doc => {
    if (!window.confirm(`Delete "${doc.title}"? This removes it from OrgRepo.`)) return;
    try { await deleteOrgRepoFile(doc.path); reload(); }
    catch (err) { window.alert(err.message || 'Failed to delete document.'); }
  };

  const meta = (
    <>
      {members.length} {members.length === 1 ? 'person' : 'people'}
      {subCompanies.length > 0 && <><br/>{subCompanies.length} business unit{subCompanies.length === 1 ? '' : 's'}</>}
    </>
  );

  return (
    <EditModeProvider canEdit={canEdit}>
    <EntityShell
      title={
        <EditableText
          value={company.name}
          onSave={v => saveCompany({ name: v })}
          placeholder="Organization name"
          maxLength={80}
          inputStyle={{fontFamily:'var(--font-display)',fontSize:24,fontWeight:500}}
        />
      }
      subtitle={parent ? <>Business unit of <EntityLink type="organization" handle={entityHandle(parent)}>{parent.name}</EntityLink></> : null}
      badges={[{ label: 'Organization', color: '#715AAE' }]}
      meta={meta}
      headerAction={<EditSwitch/>}
      rightRail={<CompanyNewsRail slug={company.slug} news={news} isAdmin={isAdmin} onChanged={reload}/>}
    >
      {(company.description || canEdit) && (
        <EntitySection title="About">
          <EditableText
            multiline
            rows={5}
            value={company.description}
            onSave={v => saveCompany({ description: v })}
            placeholder="Describe this organization — what it is and how it relates to the firm."
            viewRender={v => (v ? <MarkdownView source={v}/> : <span style={{color:'var(--fg-muted)'}}>No description yet.</span>)}
          />
        </EntitySection>
      )}

      {canEdit && (
        <EntitySection title="Owner">
          <OwnerField value={company.ownerUserId} onSave={v => saveCompany({ ownerUserId: v })} isAdmin={!!data.viewer?.isAdmin}/>
        </EntitySection>
      )}

      {(companyCreditors.length > 0 || canEdit) && (
        <EntitySection title={`Creditors · ${companyCreditors.length}`}>
          {canEdit ? (
            <>
              <TaxonomyMultiSelect
                hint="The billing legal entities that belong to this organization. A project's creditor is picked from the firm-wide list (Admin → Creditors); this org's revenue rolls up from every project sharing one of these creditors."
                items={allCreditors.map(c => ({ id: c, name: c }))}
                selectedIds={companyCreditors}
                onChange={saveCreditors}
                emptyMessage="No creditors defined yet — add them in Admin → Creditors."
              />
            </>
          ) : (
            <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
              {companyCreditors.map(c => <span key={c} className="tag tag--orange">{c}</span>)}
            </div>
          )}
        </EntitySection>
      )}

      <ItemLeadsSection itemType="organization" itemId={company._id || company.id} isAdmin={isAdmin}/>

      {revenueProjects.length > 0 && (
        <EntitySection title="Revenue">
          <MonthlyRevenueStack
            title={`Monthly revenue billed via ${company.name}`}
            projects={revenueProjects}
            stackKey="creditor"
            tagLookup={Object.fromEntries(revenueProjects.filter(p => p.creditor).map(p => [p.creditor, p.creditor]))}
            footnote="Revenue from projects whose creditor belongs to this organization, from each project's monthly commercial table (or a working-day spread of its volume where no table exists yet)."
          />
        </EntitySection>
      )}

      <EntitySection title={`People · ${members.length}`}>
        {members.length === 0 ? (
          <div style={{padding:'14px 12px',color:'var(--fg-muted)',fontSize:13}}>
            No one's mapped to this organization yet. People appear here automatically once they add it on Profile → Experience → Organizations.
          </div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:10}}>
            {members.map(m => (
              <PersonCard
                key={m.userId}
                userId={m.userId}
                name={m.name}
                photoUrl={m.photoUrl}
                title={m.title}
                lines={[m.office]}
              />
            ))}
          </div>
        )}
      </EntitySection>

      <EntitySection title="Offices">
        {offices
          ? <MarkdownView source={offices}/>
          : (
            <div style={{padding:'14px 12px',color:'var(--fg-muted)',fontSize:13,lineHeight:1.55}}>
              No offices document at <code>{officesPath}</code> yet. Create that file in OrgRepo to list this organization's locations — addresses, regions, who to contact.
            </div>
          )}
      </EntitySection>

      {subCompanies.length > 0 && (
        <EntitySection title={`Business units · ${subCompanies.length}`}>
          <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
            {subCompanies.map(s => (
              <span
                key={s.id}
                className="tag"
                style={{cursor:'pointer'}}
                onClick={() => { location.hash = `organizations/${s.slug || s.id}`; }}
              >
                {s.name}
              </span>
            ))}
          </div>
        </EntitySection>
      )}

      <EntitySection
        title={`Guidelines & documents · ${documents.length}`}
        action={isAdmin ? <SectionAddButton label="Add document" onClick={() => setShowDocEditor(true)}/> : null}
      >
        {documents.length === 0 ? (
          <div style={{padding:'14px 12px',color:'var(--fg-muted)',fontSize:13,lineHeight:1.55}}>
            No documents yet.{' '}
            {isAdmin
              ? 'Click "Add document" to write one, or drop markdown files under '
              : 'Markdown files under '}
            <code>/content/organizations/{company.slug}/</code>
            {isAdmin ? ' in OrgRepo.' : ' in OrgRepo — policies, onboarding guides, brand books — show here.'}
          </div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {documents.map(d => (
              <OrgDocItem key={d.path} doc={d} onDelete={isAdmin ? () => handleDeleteDoc(d) : null}/>
            ))}
          </div>
        )}
      </EntitySection>

      {showDocEditor && (
        <DocEditorModal
          slug={company.slug}
          existingPaths={documents.map(d => d.path)}
          onClose={() => setShowDocEditor(false)}
          onSaved={() => { setShowDocEditor(false); reload(); }}
        />
      )}

      {isAdmin && (
        <DangerZoneSection
          noun="organization"
          name={company.name}
          kind="company"
          entityId={company._id || company.id}
          onDeleted={onBack}
        />
      )}
    </EntityShell>
    </EditModeProvider>
  );
};

/* ─── Offices ──────────────────────────────────────────────────────
 * Knowledge-base element for the firm's office locations. Moved here
 * from Admin → Offices. List + detail, same dispatcher shape as the
 * other directory views. The detail page carries pictures, an event
 * calendar, office managers (item_leads), useful information, and a
 * Google-Maps pin derived from the entered location. Admins get the
 * View/Edit switch + the "Add office" button; everyone else views. */
export const OfficesView = ({ currentUser, subPath, onNavigate } = {}) => (
  subPath
    ? <OfficeDetailView slug={subPath} currentUser={currentUser} onBack={() => onNavigate?.('')}/>
    : <OfficesList currentUser={currentUser} onNavigate={onNavigate}/>
);

const OfficesList = ({ currentUser, onNavigate }) => {
  const isAdmin = currentUser?.role === 'admin';
  const [offices, setOffices] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [q, setQ] = React.useState('');
  const [creating, setCreating] = React.useState(false);

  const reload = React.useCallback(() => {
    listTaxonomies()
      .then(t => setOffices(t.offices || []))
      .catch(err => setError(err.message));
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  const needle = q.trim().toLowerCase();
  const list = offices || [];
  const filtered = !needle ? list : list.filter(o =>
    (o.name || '').toLowerCase().includes(needle) ||
    (o.location || '').toLowerCase().includes(needle),
  );

  const open = o => onNavigate?.(o.slug || o._id);

  return (
    <div className="page">
      <div className="page__head">
        <h1>Offices</h1>
        <div className="lede">
          The firm's locations. Click a row to open the office page — its photos, event calendar, office managers, useful information, and a map pin. People are tied to an office from their profile.
        </div>
      </div>

      <div style={{display:'flex',gap:10,marginBottom:14,alignItems:'center'}}>
        <input
          className="input"
          placeholder="Search offices…"
          value={q}
          onChange={e => setQ(e.target.value)}
          style={{maxWidth:380}}
        />
        <div style={{flex:1}}/>
        {offices && <span style={{fontSize:12,color:'var(--fg-muted)'}}>{filtered.length} of {list.length}</span>}
        {isAdmin && (
          <button className="btn-orange" onClick={() => setCreating(true)}>
            <Icon name="plus" size={14}/> Add office
          </button>
        )}
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}
      {!offices && !error && <Loading>Loading offices…</Loading>}
      {offices && filtered.length === 0 && (
        <Empty>{needle ? 'No offices match.' : (isAdmin ? 'No offices yet. Click "Add office" to create the first one.' : 'No offices yet.')}</Empty>
      )}

      {filtered.length > 0 && (
        <div className="tbl-card">
          <table className="tbl" style={{width:'100%'}}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Location</th>
                <th style={{width:110,textAlign:'right'}}># photos</th>
                <th style={{width:38}}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(o => (
                <tr key={o._id} onClick={() => open(o)} style={{cursor:'pointer'}}>
                  <td style={{fontWeight:500}}>{o.name}</td>
                  <td style={{color:'var(--fg-muted)',fontSize:12.5}}>{o.location || '—'}</td>
                  <td style={{textAlign:'right',color:'var(--fg-muted)',fontSize:12.5}}>{(o.photos || []).length}</td>
                  <td style={{textAlign:'right',color:'var(--fg-muted)',fontSize:14}}>›</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CreateOfficeModal
          onClose={() => setCreating(false)}
          onCreated={office => { setCreating(false); reload(); onNavigate?.(office.slug || office._id); }}
        />
      )}
    </div>
  );
};

/* Admin: name-only create modal. The richer fields (location, photos,
 * events, managers, useful info) are filled in on the office page after
 * it exists, in Edit mode. */
const CreateOfficeModal = ({ onClose, onCreated }) => {
  const [name, setName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const submit = async e => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true); setError(null);
    try {
      const office = await adminOffices.create({ name: name.trim() });
      onCreated(office);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:440}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--orange)',marginBottom:6,fontWeight:600}}>New office</div>
        <form onSubmit={submit}>
          <h3 style={{marginTop:0}}>Add an office.</h3>
          <p style={{color:'var(--fg-muted)',fontSize:13.5,marginTop:6,marginBottom:18}}>
            Name it now — you can add the location, photos, events, managers and useful info on the office page once it's created.
          </p>
          <div className="field">
            <label>Office name</label>
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Munich" autoFocus/>
          </div>
          {error && <div style={{marginBottom:14,padding:'10px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>{error}</div>}
          <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-orange" disabled={busy || !name.trim()}>{busy ? 'Creating…' : 'Create office'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* Full-page Office detail. */
const OfficeDetailView = ({ slug, currentUser, onBack }) => {
  const isAdmin = currentUser?.role === 'admin';
  // Firm editors (admin + eOS-Manager) manage office managers (leads).
  const canManage = isAdmin || currentUser?.role === 'eOS-Manager';
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);

  useSubCrumbs(data?.office?.name ? [{ label: data.office.name, href: null }] : []);

  React.useEffect(() => {
    let cancelled = false;
    setError(null); setData(null);
    getOfficeProfile(slug)
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setError(err.message || String(err)); });
    return () => { cancelled = true; };
  }, [slug, reloadKey]);

  if (error) {
    return (
      <div className="page">
        <div className="page__head">
          <h1>Office · {slug}</h1>
          <div className="lede">Failed to load this office.</div>
        </div>
        <ErrorBox>{error}</ErrorBox>
        <div style={{display:'flex',gap:10}}>
          <button className="btn-orange" onClick={() => setReloadKey(k => k + 1)}>Retry</button>
          <button className="btn-ghost" onClick={onBack}>← Back to offices</button>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="page"><Loading>Loading office <code style={{color:'var(--fg-muted)'}}>{slug}</code>…</Loading></div>;
  }

  const { office, people = [] } = data;
  const canEdit = !!data.viewer?.canEdit;
  const handle = office.slug || office._id || office.id;
  const photos = office.photos || [];
  const events = office.events || [];

  const saveOffice = async patch => {
    const updated = await patchOffice(handle, patch);
    setData(d => (d ? { ...d, office: { ...d.office, ...updated } } : d));
  };
  const setOffice = updated => setData(d => (d ? { ...d, office: { ...d.office, ...updated } } : d));

  const meta = (
    <>
      {people.length} {people.length === 1 ? 'person' : 'people'}
      {office.location && <><br/>{office.location}</>}
    </>
  );

  return (
    <EditModeProvider canEdit={canEdit}>
    <EntityShell
      title={
        <EditableText
          value={office.name}
          onSave={v => saveOffice({ name: v })}
          placeholder="Office name"
          maxLength={80}
          inputStyle={{fontFamily:'var(--font-display)',fontSize:24,fontWeight:500}}
        />
      }
      badges={[{ label: 'Office', color: '#1F8A70' }]}
      meta={meta}
      headerAction={<EditSwitch/>}
      rightRail={null}
    >
      <OfficePhotosSection slug={handle} photos={photos} canEdit={canEdit} onChange={setOffice}/>

      <OfficeEventsSection events={events} onSave={ev => saveOffice({ events: ev })}/>

      <ItemLeadsSection
        itemType="office"
        itemId={office._id || office.id}
        isAdmin={canManage}
        title="Office managers"
        noun="manager"
        emptyLabel={canManage ? 'No office managers yet. Use the picker to name who runs this office.' : 'No office managers named yet.'}
      />

      <EntitySection title="Useful information">
        <EditableText
          multiline
          rows={6}
          value={office.usefulInfo}
          onSave={v => saveOffice({ usefulInfo: v })}
          placeholder="Anything useful for visitors and staff — reception hours, how to get there, parking, wifi, building access, nearby food…"
          viewRender={v => (v ? <MarkdownView source={v}/> : <span style={{color:'var(--fg-muted)'}}>No useful information yet.</span>)}
        />
      </EntitySection>

      <EntitySection title="Location & map">
        <div className="field" style={{margin:0,marginBottom:12,maxWidth:520}}>
          <label style={{fontSize:10.5,letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600,marginBottom:4,display:'block'}}>Address / location</label>
          <EditableText
            value={office.location}
            onSave={v => saveOffice({ location: v })}
            placeholder="e.g. Theresienstraße 1, 80333 Munich, Germany"
            maxLength={240}
            emptyView="No location set"
          />
        </div>
        <OfficeMap location={office.location}/>
      </EntitySection>

      <EntitySection title={`People · ${people.length}`}>
        {people.length === 0 ? (
          <div style={{padding:'14px 12px',color:'var(--fg-muted)',fontSize:13,lineHeight:1.55}}>
            No one's based here yet. People appear automatically once they pick this office on Profile → Experience.
          </div>
        ) : (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:10}}>
            {people.map(m => (
              <PersonCard
                key={m.userId}
                userId={m.userId}
                name={m.name}
                photoUrl={m.photoUrl}
                title={m.title}
                lines={[m.company]}
              />
            ))}
          </div>
        )}
      </EntitySection>
    </EntityShell>
    </EditModeProvider>
  );
};

/* Embedded Google-Maps pin from a free-text address. Uses the keyless
 * `maps.google.com?q=…&output=embed` form (no API key, no billing). */
const OfficeMap = ({ location }) => {
  if (!location || !location.trim()) {
    return (
      <div style={{padding:'14px 12px',color:'var(--fg-muted)',fontSize:13,background:'var(--bg-alt)',borderRadius:8}}>
        Add a location above to drop a map pin here.
      </div>
    );
  }
  const src = `https://www.google.com/maps?q=${encodeURIComponent(location)}&output=embed`;
  return (
    <div style={{borderRadius:8,overflow:'hidden',border:'1px solid var(--border)'}}>
      <iframe
        title={`Map of ${location}`}
        src={src}
        width="100%"
        height={340}
        style={{border:0,display:'block'}}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
      />
    </div>
  );
};

/* Photo gallery + (in edit mode) an uploader and per-photo remove. */
const OfficePhotosSection = ({ slug, photos, canEdit, onChange }) => {
  const { editing } = useEditMode();
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const active = editing && canEdit;

  const onPick = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const updated = await uploadOfficePhoto(slug, file);
      onChange(updated);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };
  const remove = async key => {
    if (!window.confirm('Remove this photo?')) return;
    setBusy(true); setError(null);
    try {
      const updated = await removeOfficePhoto(slug, key);
      onChange(updated);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <EntitySection
      title="Pictures"
      action={active
        ? <>
            <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{display:'none'}} onChange={onPick}/>
            <button type="button" className="btn-ghost" style={{padding:'3px 10px',fontSize:11.5}} disabled={busy} onClick={() => inputRef.current?.click()}>
              {busy ? 'Uploading…' : '+ Add photo'}
            </button>
          </>
        : null}
    >
      {error && <div style={{padding:'8px 12px',color:'#A20235',fontSize:12.5}}>{error}</div>}
      {photos.length === 0 ? (
        <div style={{padding:'14px 12px',color:'var(--fg-muted)',fontSize:13}}>
          No photos yet.{active ? ' Use "+ Add photo" to upload one (PNG/JPG/WebP/GIF, max 2 MB).' : ''}
        </div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:10}}>
          {photos.map(p => (
            <div key={p.key} style={{position:'relative',borderRadius:8,overflow:'hidden',border:'1px solid var(--border)',aspectRatio:'4 / 3',background:'var(--bg-card)'}}>
              <a href={p.url} target="_blank" rel="noreferrer">
                <img src={p.url} alt="Office" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
              </a>
              {active && (
                <button
                  type="button"
                  title="Remove photo"
                  onClick={() => remove(p.key)}
                  disabled={busy}
                  style={{position:'absolute',top:6,right:6,width:24,height:24,borderRadius:'50%',border:0,background:'rgba(0,0,0,0.6)',color:'#fff',cursor:'pointer',fontSize:14,lineHeight:1,display:'flex',alignItems:'center',justifyContent:'center'}}
                >×</button>
              )}
            </div>
          ))}
        </div>
      )}
    </EntitySection>
  );
};

/* Event calendar. View mode lists upcoming-first; edit mode turns each
 * row into date / title / note inputs with add + remove. The whole
 * array is saved on blur / add / remove (admin-only edits). */
const OfficeEventsSection = ({ events, onSave }) => {
  const { canEdit, editing } = useEditMode();
  const [rows, setRows] = React.useState(events);
  const [error, setError] = React.useState(null);
  // Sync from props only when not editing — avoids clobbering an
  // in-progress row while a save round-trips.
  React.useEffect(() => { if (!editing) setRows(events); }, [events, editing]);

  const active = editing && canEdit;

  const commit = async next => {
    setRows(next);
    setError(null);
    try { await onSave(next); }
    catch (err) { setError(err.message || String(err)); }
  };
  const addRow = () => {
    const next = [...rows, { id: (crypto.randomUUID?.() || String(Date.now())), title: '', date: '', note: '' }];
    setRows(next);
  };
  const updateRow = (id, field, value) => setRows(rows.map(r => (r.id === id ? { ...r, [field]: value } : r)));
  const removeRow = id => commit(rows.filter(r => r.id !== id));

  const sorted = [...rows].sort((a, b) => (a.date || '9999').localeCompare(b.date || '9999'));
  const fmtDate = d => {
    if (!d) return 'No date';
    const dt = new Date(d + 'T00:00:00');
    return Number.isFinite(dt.getTime())
      ? dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
      : d;
  };

  return (
    <EntitySection
      title="Event calendar"
      action={active ? <button type="button" className="btn-ghost" style={{padding:'3px 10px',fontSize:11.5}} onClick={addRow}>+ Add event</button> : null}
    >
      {error && <div style={{padding:'8px 12px',color:'#A20235',fontSize:12.5}}>{error}</div>}
      {active ? (
        rows.length === 0 ? (
          <div style={{padding:'14px 12px',color:'var(--fg-muted)',fontSize:13}}>No events yet. Use "+ Add event" to schedule one.</div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {rows.map(r => (
              <div key={r.id} style={{display:'flex',gap:8,alignItems:'flex-start',flexWrap:'wrap',padding:'8px',background:'var(--bg-alt)',borderRadius:8}}>
                <input
                  type="date"
                  className="input"
                  value={r.date || ''}
                  onChange={e => updateRow(r.id, 'date', e.target.value)}
                  onBlur={() => commit(rows)}
                  style={{width:160}}
                />
                <input
                  className="input"
                  placeholder="Event title"
                  value={r.title || ''}
                  onChange={e => updateRow(r.id, 'title', e.target.value)}
                  onBlur={() => commit(rows)}
                  style={{flex:'1 1 200px'}}
                />
                <input
                  className="input"
                  placeholder="Note (optional)"
                  value={r.note || ''}
                  onChange={e => updateRow(r.id, 'note', e.target.value)}
                  onBlur={() => commit(rows)}
                  style={{flex:'1 1 200px'}}
                />
                <button type="button" className="btn-ghost" style={{padding:'6px 10px',fontSize:11.5,color:'var(--support-red)'}} onClick={() => removeRow(r.id)}>Remove</button>
              </div>
            ))}
          </div>
        )
      ) : (
        sorted.length === 0 ? (
          <div style={{padding:'14px 12px',color:'var(--fg-muted)',fontSize:13}}>No events scheduled.</div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {sorted.map(r => (
              <div key={r.id} style={{display:'flex',gap:12,alignItems:'baseline',padding:'10px 12px',background:'var(--bg-alt)',borderRadius:8}}>
                <div style={{fontSize:12,fontWeight:600,color:'var(--orange)',minWidth:120,fontFamily:'JetBrains Mono'}}>{fmtDate(r.date)}</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:500,fontSize:13.5}}>{r.title || '—'}</div>
                  {r.note && <div style={{fontSize:12,color:'var(--fg-muted)',marginTop:2}}>{r.note}</div>}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </EntitySection>
  );
};

/* Small "+ Add …" button for an EntitySection header. */
const SectionAddButton = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{display:'inline-flex',alignItems:'center',gap:5,padding:'4px 10px',fontSize:12,fontWeight:500,
            color:'var(--orange)',background:'transparent',border:'1px solid var(--orange)',borderRadius:6,cursor:'pointer'}}
  >
    <span style={{fontSize:14,lineHeight:1}}>＋</span>{label}
  </button>
);

function dirSlugify(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/* Admin: write a new guideline/document as a markdown file under the
 * org's OrgRepo folder. Title becomes the H1 + filename slug. */
const DocEditorModal = ({ slug, existingPaths, onClose, onSaved }) => {
  const [title, setTitle] = React.useState('');
  const [body, setBody] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const folder = `/content/organizations/${slug}/`;
  const base = dirSlugify(title);
  const taken = new Set((existingPaths || []).map(p => p.slice(folder.length, -3)));
  let finalSlug = base;
  for (let n = 1; base && taken.has(finalSlug); n++) finalSlug = `${base}-${n}`;
  const path = finalSlug ? `${folder}${finalSlug}.md` : '';

  const submit = async e => {
    e.preventDefault();
    if (!finalSlug || busy) return;
    setBusy(true); setError(null);
    try {
      const content = `# ${title.trim()}\n\n${body.trim() ? body.trim() + '\n' : ''}`;
      await putOrgRepoFile({ path, content, message: `add document ${path}` });
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to save.');
      setBusy(false);
    }
  };

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:720,width:'95%'}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <h3 style={{marginBottom:6}}>Add document</h3>
        <p style={{fontSize:12.5,color:'var(--fg-muted)',marginBottom:16,lineHeight:1.5,maxWidth:'64ch'}}>
          Saved as a markdown file in OrgRepo under <code>{folder}</code>. The title becomes the heading and filename.
        </p>
        <form onSubmit={submit}>
          <div className="field">
            <label>Title</label>
            <input className="input" placeholder="e.g. Code of Conduct" value={title} onChange={e => setTitle(e.target.value)} autoFocus/>
            {path && <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:4}}><code>{path}</code></div>}
          </div>
          <div className="field">
            <label>Body (markdown)</label>
            <textarea className="input" rows={9} placeholder="Write the document in markdown…" value={body} onChange={e => setBody(e.target.value)} style={{fontFamily:'JetBrains Mono, monospace',fontSize:12.5,lineHeight:1.5}}/>
          </div>
          {error && <ErrorBox>{error}</ErrorBox>}
          <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:8}}>
            <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn-orange" disabled={!finalSlug || busy}>{busy ? 'Saving…' : 'Save document'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* One collapsible guideline / document under an organization. Title +
 * meta row; click to expand the rendered markdown inline. Admins get a
 * delete control. */
const OrgDocItem = ({ doc, onDelete }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{border:'1px solid var(--border)',borderRadius:8,overflow:'hidden'}}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{padding:'10px 12px',background:'var(--bg-alt)',cursor:'pointer',display:'flex',alignItems:'center',gap:10}}
      >
        <span style={{color:'var(--fg-muted)',fontSize:12,transform:open ? 'rotate(90deg)' : 'none',transition:'transform 0.15s'}}>›</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontWeight:500,fontSize:13}}>{doc.title}</div>
          <div style={{fontSize:11,color:'var(--fg-muted)'}}>
            updated {new Date(doc.updatedAt).toLocaleDateString()}{doc.updatedBy ? ` · ${doc.updatedBy}` : ''}
          </div>
        </div>
        {onDelete && (
          <button
            type="button"
            title="Delete document"
            onClick={e => { e.stopPropagation(); onDelete(); }}
            style={{border:0,background:'transparent',cursor:'pointer',color:'var(--fg-muted)',fontSize:16,lineHeight:1,padding:'0 4px'}}
          >×</button>
        )}
      </div>
      {open && (
        <div style={{padding:'12px 14px'}}>
          <MarkdownView source={doc.content}/>
        </div>
      )}
    </div>
  );
};

/* Right-rail "Company news" — replaces the agent news ticker for
 * organizations. Lists manually-authored posts (image + headline +
 * markdown body), newest first. Admins can add + delete posts. */
const CompanyNewsRail = ({ slug, news = [], isAdmin, onChanged }) => {
  const [adding, setAdding] = React.useState(false);
  const [busyPath, setBusyPath] = React.useState(null);

  const del = async post => {
    if (!window.confirm(`Delete "${post.headline}"?`)) return;
    setBusyPath(post.path);
    try { await deleteCompanyNews(slug, post.path); onChanged?.(); }
    catch (err) { window.alert(err.message || 'Failed to delete.'); }
    finally { setBusyPath(null); }
  };

  return (
    <aside className="news-ticker">
      <div className="news-ticker__head">
        <Icon name="sparkle" size={14}/>
        <strong>Company news</strong>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            title="Add news post"
            style={{marginLeft:'auto',display:'inline-flex',alignItems:'center',gap:4,padding:'2px 8px',fontSize:11.5,
                    color:'var(--orange)',background:'transparent',border:'1px solid var(--orange)',borderRadius:6,cursor:'pointer'}}
          >＋ Add</button>
        )}
      </div>

      {news.length === 0 ? (
        <div style={{padding:'12px 14px',color:'var(--fg-muted)',fontSize:12.5,lineHeight:1.5}}>
          No posts yet.{isAdmin ? ' Click "Add" to publish the first company-news post.' : ''}
        </div>
      ) : (
        <ul className="news-ticker__list">
          {news.map(post => (
            <li key={post.path} className="news-ticker__item">
              {post.image && (
                <img
                  src={post.image}
                  alt=""
                  style={{width:'100%',maxHeight:140,objectFit:'cover',borderRadius:6,marginBottom:6,display:'block'}}
                  onError={e => { e.currentTarget.style.display = 'none'; }}
                />
              )}
              <div style={{display:'flex',alignItems:'flex-start',gap:6}}>
                <div style={{flex:1,minWidth:0}}>
                  <div className="news-ticker__title" style={{fontWeight:600}}>{post.headline}</div>
                  <div className="news-ticker__meta">
                    {post.date ? <span>{new Date(post.date).toLocaleDateString()}</span> : null}
                    {post.date && post.author ? <span> · </span> : null}
                    {post.author ? <span>{post.author}</span> : null}
                  </div>
                </div>
                {isAdmin && (
                  <button
                    type="button"
                    title="Delete post"
                    disabled={busyPath === post.path}
                    onClick={() => del(post)}
                    style={{border:0,background:'transparent',cursor:'pointer',color:'var(--fg-muted)',fontSize:15,lineHeight:1,padding:'0 2px'}}
                  >×</button>
                )}
              </div>
              {post.body && (
                <div style={{marginTop:6,fontSize:12.5,lineHeight:1.5}}>
                  <MarkdownView source={post.body}/>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding && (
        <NewsEditorModal
          slug={slug}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); onChanged?.(); }}
        />
      )}
    </aside>
  );
};

/* Admin: compose a company-news post — headline, markdown body, and an
 * optional image. POSTs to /api/admin/companies/:slug/news. */
const NewsEditorModal = ({ slug, onClose, onSaved }) => {
  const [headline, setHeadline] = React.useState('');
  const [body, setBody] = React.useState('');
  const [imageFile, setImageFile] = React.useState(null);
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const pickImage = e => {
    const f = e.target.files?.[0] || null;
    setImageFile(f);
    setPreview(p => { if (p) URL.revokeObjectURL(p); return f ? URL.createObjectURL(f) : null; });
  };
  React.useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const submit = async e => {
    e.preventDefault();
    if (!headline.trim() || busy) return;
    setBusy(true); setError(null);
    try {
      await createCompanyNews(slug, { headline: headline.trim(), body: body.trim(), imageFile });
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to publish.');
      setBusy(false);
    }
  };

  return (
    <div className="modal-mask" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{maxWidth:780,width:'95%'}}>
        <button className="modal__close" onClick={onClose}>×</button>
        <h3 style={{marginBottom:6}}>Add company news</h3>
        <p style={{fontSize:12.5,color:'var(--fg-muted)',marginBottom:16,lineHeight:1.5,maxWidth:'64ch'}}>
          Stored as a markdown file in OrgRepo with an optional image. Shows in this organization's news rail, newest first.
        </p>
        <form onSubmit={submit} className="intake-form">
          <div className="intake-cols">
            <div className="intake-col">
              <div className="field" style={{margin:0}}>
                <label>Headline</label>
                <input className="input" placeholder="e.g. We opened a Munich office" value={headline} onChange={e => setHeadline(e.target.value)} autoFocus/>
              </div>
              <div className="field" style={{margin:0}}>
                <label>Body (markdown)</label>
                <textarea className="input" rows={8} placeholder="Write the post in markdown…" value={body} onChange={e => setBody(e.target.value)} style={{fontFamily:'JetBrains Mono, monospace',fontSize:12.5,lineHeight:1.5}}/>
              </div>
            </div>
            <div className="intake-col">
              <div className="field" style={{margin:0}}>
                <label>Image (optional · ≤2 MB)</label>
                <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" onChange={pickImage}/>
                {preview
                  ? <img src={preview} alt="" style={{marginTop:8,maxWidth:'100%',maxHeight:220,borderRadius:6,display:'block'}}/>
                  : <div style={{marginTop:8,border:'1px dashed var(--border)',borderRadius:8,padding:'28px 12px',textAlign:'center',fontSize:12,color:'var(--fg-muted)'}}>No image — the post shows headline + body only.</div>}
              </div>
            </div>
          </div>
          {error && <ErrorBox>{error}</ErrorBox>}
          <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
            <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn-orange" disabled={!headline.trim() || busy}>{busy ? 'Publishing…' : 'Publish'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};
